// Typed client for the TPRM API.
//
// Every shape here mirrors a Go struct in packages/core. Where the API is
// inconsistent, the inconsistency is absorbed here and nowhere else: GET
// /v1/assessments/{id} returns repo.Score with camelCase keys, while POST
// /v1/assessments/{id}/score returns scoring.Result with snake_case keys, and
// no view should have to know which one it was handed.

import { ensureFreshToken } from './auth'
import type { DateWarning } from './dateRules'
import { daysUntil } from './days'
import { actionFailed, actionSucceeded, beginAction, type Action } from './feedback'
import type { MailState, Reach } from './mail'

export const TOKEN_KEY = 'tprm.devToken'
export const DEFAULT_TOKEN = 'dev:aaaaaaaa-0000-4000-8000-000000000001:tprm_owner'

// The development token, which is a token only in a build that has agreed to
// believe it. The API accepts the dev: form solely when AUTH_MODE=dev, and
// config.Load refuses that combination unless TPRM_ENV=development, so this
// value reaching a UAT deployment produces a 401 rather than a session.
export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || DEFAULT_TOKEN
  } catch {
    // Storage is unavailable in a hardened browser profile. Falling back to the
    // default token shows the demo tenant instead of a blank screen.
    return DEFAULT_TOKEN
  }
}

// Which credential this request should carry. A real session wins; the
// development token is what is left when there is no session, and it is sent
// rather than withheld so a development build still works with no sign-in.
export async function bearerToken(): Promise<string> {
  const session = await ensureFreshToken()
  return session || getToken()
}

export function setToken(value: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, value.trim() || DEFAULT_TOKEN)
  } catch {
    /* the caller re-reads and sees the old value, which is the honest outcome */
  }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly issues: Issue[]
  // The fields a refusal names. A refused vendor edit lists the fields it would
  // not write and a 400 lists the ones it did not recognise, and a form holding
  // them can mark the control the person typed in rather than leaving them to
  // find it again in a paragraph.
  readonly fields: string[]
  // What the API itself said to do next, where it said anything. A refusal that
  // knows its own remedy has a better one than a status code can guess: a 409
  // is usually a duplicate identifier and sometimes a tenant with no active
  // risk model, and only the API knows which.
  readonly sentRemedy: string
  // The whole refusal, as it arrived. A 409 ACTIVATION_BLOCKED carries the
  // evaluation that produced it, and a screen that showed only the message
  // would throw away the checklist the API went to the trouble of computing.
  readonly body: unknown
  // The reference support looks the request up by: the request id the API
  // puts in every error body (requestId) and on every answer (X-Request-ID).
  // Empty when the request never reached the server.
  readonly requestId: string

  constructor(
    status: number,
    code: string,
    message: string,
    issues: Issue[] = [],
    fields: string[] = [],
    remedy = '',
    body: unknown = null,
    requestId = '',
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.issues = issues
    this.fields = fields
    this.sentRemedy = remedy
    this.body = body
    this.requestId = requestId
  }

  // What the operator should do next, which is the half of an error message a
  // bare status code never carries.
  get remedy(): string {
    if (this.sentRemedy) return this.sentRemedy
    switch (this.status) {
      case 0:
        return 'The API could not be reached. If this is a local build, confirm tprm-api is up; otherwise the service is down or the load balancer has no healthy target.'
      case 200:
        // Only an empty or unparseable success body lands here.
        return 'The API answered but sent nothing. Locally this is what the dev proxy returns when tprm-api is not running behind it.'
      case 401:
        return 'The bearer token was rejected. Check the token in the header bar; it reads dev:<tenantId>:<roles>.'
      case 403:
        return 'The message above names the act this needs and the roles that hold it. Ask an administrator for one of those roles, or ask somebody who already has one to do this.'
      case 404:
        return 'No such record in this tenant. A record belonging to another tenant reads the same way, by design.'
      case 409:
        return 'Another record already holds this identifier. Open that record rather than creating a second one.'
      case 422:
        return 'The request was understood and rejected. The issues below name each field.'
      case 501:
        return 'This deployment does not offer that, which is a fact about the estate rather than about you or your roles. The message above names the setting that decides it.'
      default:
        return 'Retry once. If it persists, give support the error code and the reference shown with it.'
    }
  }
}

// The refusal an answer carries, as an ApiError with its code and reference.
// One reading for every caller, so a screen that fetches for itself (a file
// download) reports a failure exactly as request() does.
export function apiErrorFrom(res: Response, body: unknown, path: string): ApiError {
  const b = (body && typeof body === 'object' ? body : {}) as {
    error?: string
    code?: string
    message?: string
    issues?: Issue[]
    fields?: string[]
    remedy?: string
    requestId?: string
  }
  return new ApiError(
    res.status,
    b.code || b.error || 'HTTP_' + res.status,
    b.message || path + ' returned ' + res.status + '.',
    b.issues || [],
    b.fields || [],
    b.remedy || '',
    body,
    b.requestId || res.headers.get('X-Request-ID') || '',
  )
}

// A change the console sends is an action the person took, and the feedback
// net (lib/feedback.ts) makes sure it says whether it worked. Reads are not.
function actionFor(method: string, path: string): Action | null {
  const m = method.toUpperCase()
  if (m === 'GET' || m === 'HEAD') return null
  return beginAction(m, path)
}

// Reports a failure a screen caught from its own fetch (a download), so it is
// said once with its code and reference like every other.
export function reportActionFailure(method: string, path: string, err: ApiError): void {
  actionFailed(beginAction(method, path), err)
}

// Told after any change the API accepted, so a count of what waits for the
// reader (lib/myWork.ts) reads again at once rather than up to fifteen seconds
// later: approving, signing or deciding anything can take a row off My work.
const writeListeners = new Set<() => void>()

export function onWrite(fn: () => void): () => void {
  writeListeners.add(fn)
  return () => writeListeners.delete(fn)
}

// seen is for the one caller that has to read something off the answer that is
// not in its body (the evidence export's header). It is handed the response of
// a request that succeeded, before the body is returned.
export async function request<T>(path: string, init: RequestInit = {}, seen?: (res: Response) => void): Promise<T> {
  const action = actionFor(init.method || 'GET', path)
  try {
    const out = await send<T>(path, init, seen)
    if (action) {
      actionSucceeded(action)
      for (const fn of writeListeners) fn()
    }
    return out
  } catch (err) {
    if (action) actionFailed(action, err instanceof ApiError ? err : new ApiError(0, 'CLIENT_ERROR', String(err)))
    throw err
  }
}

async function send<T>(path: string, init: RequestInit, seen?: (res: Response) => void): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Authorization', 'Bearer ' + (await bearerToken()))
  headers.set('Accept', 'application/json')
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  let res: Response
  try {
    res = await fetch(path, { ...init, headers })
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'The server could not be reached, so nothing was sent. Check the connection and try again.')
  }

  const text = await res.text()

  // An empty body on a success status is not an empty result set. The dev
  // proxy answers exactly this way when tprm-api is not listening, and reading
  // it as data is how a screen ends up reporting a confident "0 of 0" for a
  // question it never actually asked.
  const reference = res.headers.get('X-Request-ID') || ''
  if (res.ok && res.status !== 204 && text.trim() === '') {
    throw new ApiError(res.status, 'EMPTY_RESPONSE', path + ' answered ' + res.status + ' with an empty body.', [], [], '', null, reference)
  }

  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      // A proxy's own error page, not the API's answer.
      throw new ApiError(
        res.status,
        res.ok ? 'BAD_RESPONSE' : 'HTTP_' + res.status,
        res.ok
          ? path + ' returned ' + res.status + ' with a body that is not JSON.'
          : 'The server answered ' + res.status + ' without saying why. If it repeats, check that tprm-api is running.',
        [],
        [],
        '',
        null,
        reference,
      )
    }
  }

  if (!res.ok) throw apiErrorFrom(res, body, path)
  seen?.(res)
  return body as T
}

// A download that has to carry the bearer token, so it cannot be a plain
// anchor: the browser would send the link without it and get a 401. The bytes
// come back as a blob the caller hands to a temporary anchor, which is the same
// shape the evidence pack download already uses.
//
// A refusal on this path still arrives as JSON, so it is read as one and thrown
// as an ApiError rather than saved to disk as a file called report.csv
// containing an error message.
export async function requestFile(path: string): Promise<{ blob: Blob; filename: string }> {
  // A download somebody asked for is an action: its failure is said with a
  // code and a reference. Its success is the file itself.
  try {
    return await fetchFile(path)
  } catch (err) {
    reportActionFailure('GET', path, err instanceof ApiError ? err : new ApiError(0, 'CLIENT_ERROR', String(err)))
    throw err
  }
}

async function fetchFile(path: string): Promise<{ blob: Blob; filename: string }> {
  const headers = new Headers()
  headers.set('Authorization', 'Bearer ' + (await bearerToken()))

  let res: Response
  try {
    res = await fetch(path, { headers })
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'The server could not be reached, so nothing was downloaded. Check the connection and try again.')
  }

  if (!res.ok) {
    let body: unknown = {}
    try {
      body = JSON.parse(await res.text())
    } catch {
      /* a refusal with no JSON body still has a status, which is what is reported */
    }
    throw apiErrorFrom(res, body, path)
  }

  const named = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')
  return { blob: await res.blob(), filename: named?.[1] ?? 'download' }
}

// Hands the reader a file the API produced. The object URL is revoked straight
// after the click, because the blob is held in memory until it is and a person
// exporting a dozen reports in a sitting would otherwise keep every one.
export function saveFile(file: { blob: Blob; filename: string }): void {
  const url = URL.createObjectURL(file.blob)
  const a = document.createElement('a')
  a.href = url
  a.download = file.filename
  a.click()
  URL.revokeObjectURL(url)
}


// ------------------------------------------------- campaigns and exceptions

// A campaign is the only way an assessment reaches a vendor, and it is created
// in DRAFT so that nothing leaves the system until a person approves it. The
// counts come back from the API rather than being derived here: issued and
// suppressed are the answer to "what actually happened when this was approved",
// and a vendor already holding an open assessment is suppressed rather than
// given a second one.
export type Campaign = {
  id: string
  name: string
  mode: string
  status: string
  dueOn?: string | null
  createdBy: string
  approvedBy?: string | null
  approvedAt?: string | null
  questionnaireVersionId?: string
  riskModelVersionId?: string
  // The bulk send this campaign is one baseline of (release 3.4, M7); the
  // detail read only.
  batchId?: string
  // The list and the detail answer with different shapes on purpose: the list
  // carries the counts a table needs, the detail carries the recipients
  // themselves and only their total. Both are optional here so one type serves
  // both rather than a caller guessing which endpoint it came from.
  recipients?: number
  issued?: number
  suppressed?: number
  recipientCount?: number
  // List only: invitations recorded and not emailed (mail muted or switched
  // off), and issued invitations the worker has not reached yet.
  notEmailed?: number
  queued?: number
}

export type CampaignRecipient = {
  id: string
  vendorId: string
  vendorCode?: string
  vendorName?: string
  email: string
  contactId?: string
  deliveryState: string
  failureReason?: string | null
  assessmentId?: string | null
  assessmentReference?: string | null
  // MUTED or OFF when the invitation was recorded and not emailed, and why,
  // in words. The stored deliveryState is FAILED then; this is what tells it
  // from a carrier that failed. See lib/mail.ts.
  mail?: MailState | string
  mailReason?: string
}

export type CampaignDetail = {
  campaign: Campaign
  recipients: CampaignRecipient[]
  // What the worker will do now with an invitation it has not reached yet.
  mail?: MailState | string
}

export type CampaignCreate = {
  name: string
  // Required when a suspended vendor is named (release 3.3): a suspended
  // vendor is sent due diligence only with a reason.
  reason?: string
  questionnaireCode?: string
  riskModelCode?: string
  mode?: string
  dueOn?: string
  vendorIds?: string[]
  tiers?: number[]
}

export type ApproveResult = {
  issued: number
  suppressed: number
  suppressedVendors?: { vendorName?: string; reason: string }[]
  queued?: number
  // Whether the invitations the approval queued will be emailed. See
  // lib/mail.ts.
  mail?: MailState | string
  mailNote?: string
}

// An exception is a documented acceptance of a gate that fired. It carries an
// owner and an expiry because an exception without one is a permanent
// acceptance nobody revisits.
export type RiskException = {
  id: string
  vendorId: string
  vendorName?: string
  gateKey?: string | null
  title: string
  justification: string
  compensatingControl: string
  ownerUserId: string
  status: string
  requestedBy: string
  decidedBy?: string | null
  decidedAt?: string | null
  expiresOn: string
  expired: boolean
  // The open item this acceptance is against. An exception raised against a
  // finding closes back on to it: approving one moves the finding to ACCEPTED
  // and its expiry is what reopens it. An older exception carries neither.
  findingId?: string | null
  // What the record is (release 3.2): an exception about one gap, the written
  // acceptance of the vendor's whole risk rating, or a contract waiver
  // standing in for a missing contract. kind is the stored code and is never
  // printed; kindName is the API's name for it. acceptsBand is the rating a
  // risk acceptance accepts. All absent on an API from before 3.2.
  kind?: string
  kindName?: string
  acceptsBand?: string
}

// What deciding one answers with. The note is the half of the outcome the
// console did not decide: approving moves the finding this exception names to
// accepted and hands it the expiry as its due date, and the API says so in its
// own words. It sends none where the exception names no finding, because then
// nothing on the register moved.
export type ExceptionDecision = {
  id: string
  status: string
  decidedBy: string
  expiresOn: string
  findingId?: string
  note?: string
}

// Section 14's chain, on one risk acceptance.
//
// The same shape the vendor gate uses, because it is the same mechanism: an
// ordered list of stages, each naming a capability and the roles that hold it,
// with the one in front flagged. What differs is only what is being signed.
export type ExceptionChain = {
  exceptionId: string
  vendorId: string
  status: string
  requestedBy: string
  band: string
  // True when the vendor has no score, so the strictest chain was applied
  // rather than one measured. The screen says so instead of presenting
  // CRITICAL as a finding.
  bandAssumed: boolean
  tier?: number | null
  tierLabel?: string
  round: number
  matrixVersionId?: string
  stages: GateStage[]
  complete: boolean
  outstanding: string[]
  signers: string[]
  // The matrix in force has no row for this band and tier, so nobody can sign.
  noRow: boolean
  message: string
  // What is being signed (release 3.2): the stored kind code, never printed,
  // and the rating a risk acceptance accepts.
  exceptionKind?: string
  acceptsBand?: string
}

export type ExceptionSignResult = {
  id: string
  status: string
  granted: boolean
  chain: ExceptionChain
  findingId?: string
  note?: string
}

export type ExceptionCreate = {
  vendorId: string
  // RISK_ACCEPTANCE or CONTRACT_WAIVER for the two kinds about the vendor as a
  // whole (release 3.2), which name no finding or gate. Absent for an
  // exception as before.
  kind?: 'RISK_ACCEPTANCE' | 'CONTRACT_WAIVER'
  gateKey?: string
  findingId?: string
  title: string
  justification: string
  compensatingControl: string
  ownerUserId: string
  expiresOn: string
}

// ----------------------------------------------------------------- findings

// A finding is the durable half of what a gate said. The score that raised it
// is recomputed every time somebody rescores; the finding is not. So a rescore
// changes the provenance below and never the status, the owner, the due date
// or the words, and nothing in this client offers a call that would close one
// without a person saying in writing what was done.

export type Finding = {
  id: string
  vendorId: string
  // The register reads across vendors and the API names the supplier on every
  // row, so nothing here has to open another screen to print a column.
  vendorName?: string
  vendorCode?: string

  // GATE or MANUAL. A gate finding names the rule that raised it and the score
  // it was last seen on; a manual one names the person.
  source: string
  gateKey?: string
  scoreId?: string
  assessmentId?: string
  riskType?: string
  riskTypeName?: string

  title: string
  detail?: string
  citation?: string

  // What the finding is treated as, and what the gate's effect and band said
  // on their own. They differ only where somebody overrode the first, and both
  // are carried so the model's reading stays visible beside the judgement.
  severity: string
  derivedSeverity: string
  severityReason?: string

  ownerUserId: string
  dueOn: string
  // Days past the due date, negative while there is still time. The API
  // computes it so the ageing column and the overdue filter cannot disagree
  // about what day it is.
  ageingDays: number

  status: string
  resolutionNote?: string
  resolutionDocumentId?: string
  resolvedBy?: string
  resolvedAt?: string

  lastSeenScoreId?: string
  // False once the gate stopped firing. Provenance, not a state: a band moving
  // is not evidence that anything was fixed, so the finding still waits for a
  // person.
  stillReproducing: boolean

  raisedBy: string
  raisedAt: string
  updatedAt: string

  // What this finding may become next, in the API's words. Present only on the
  // answer to a write, because that is the only shape the API puts it on: the
  // list routes serve the row alone. Where it is present it wins over anything
  // the console would otherwise offer.
  allowedNext?: string[]
}

// What a person raises by hand. There is no source and no vendor: the vendor is
// the path, and a caller cannot declare itself a gate.
export type FindingCreate = {
  title: string
  detail?: string
  citation?: string
  severity: string
  riskType?: string
  assessmentId?: string
  // Blank takes the default window for the severity, which the API applies.
  dueOn?: string
  // Blank takes the vendor's TPRM owner, which the API resolves.
  ownerUserId?: string
}

// Only the fields being changed. Every one is optional on the wire for the
// reason updateVendor gives: sending back the ones nobody touched is how an
// owner change becomes a refusal about a severity nobody meant to set.
export type FindingChange = {
  ownerUserId?: string
  dueOn?: string
  severity?: string
  severityReason?: string
  status?: string
  note?: string
  documentId?: string
  reason?: string
}

export type FindingQuery = {
  status?: string[]
  severity?: string[]
  owner?: string
  riskType?: string
  overdue?: boolean
  limit?: number
  offset?: number
}

export function findingQueryString(f: FindingQuery): string {
  const p = new URLSearchParams()
  if (f.status?.length) p.set('status', f.status.join(','))
  if (f.severity?.length) p.set('severity', f.severity.join(','))
  if (f.owner?.trim()) p.set('owner', f.owner.trim())
  if (f.riskType) p.set('riskType', f.riskType)
  if (f.overdue) p.set('overdue', 'true')
  if (f.limit) p.set('limit', String(f.limit))
  if (f.offset) p.set('offset', String(f.offset))
  const s = p.toString()
  return s ? '?' + s : ''
}

// matched is what came back and total is what the filter found, so a screen can
// say it is showing part of an answer rather than reporting a page as the
// estate.
export type FindingPage = {
  findings: Finding[]
  matched: number
  total: number
}

// A write answers with the row it wrote, what that row may become next, and one
// sentence saying what just happened. The sentence is the API's and is shown as
// it arrives: a console that composed its own would drift from the rule that
// produced it.
export type FindingReply = {
  finding: Finding
  note: string
}

// ----------------------------------------------------- clarifications

// One question a reviewer asked about one answer, section 11 steps seven to
// nine. The loop is deliberately per answer rather than per assessment: the
// only thing the product could do before this was hand the whole questionnaire
// back, which tells a vendor nothing about which of ninety answers was wrong.

export type Clarification = {
  id: string
  assessmentId: string
  questionKey: string
  // The prompt as the version the assessment was issued against words it, so
  // nothing here has to hold its own copy of the questionnaire.
  questionPrompt?: string
  // Which time of asking this is for this answer. Two rounds on one answer are
  // separate rows with separate replies, and a reply is addressed to a round.
  roundNo: number

  asked: string
  askedBy: string
  askedAt: string

  response?: string
  respondedBy?: string
  respondedAt?: string

  // OUTSTANDING, ANSWERED, ACCEPTED, REJECTED or WITHDRAWN.
  status: string
  decisionNote?: string
  decidedBy?: string
  decidedAt?: string
  updatedAt: string

  // Whether this row has stopped owing anybody anything, and the API's own
  // sentence saying which way round it is stuck. A rejected reply is a
  // decision that has been taken and a question that is still owed, so settled
  // is not the complement of "decided" and this client does not compute it.
  settled: boolean
  blocking?: string
}

// What the list route answers, and what every write route answers beside the
// row it wrote, so a screen never has to guess whether the assessment became
// scoreable.
export type ClarificationList = {
  clarifications: Clarification[]
  outstanding: Clarification[]
  scoreable: boolean
  message: string
}

function clarificationListFrom(raw: Record<string, unknown>): ClarificationList {
  const rows = Array.isArray(raw.clarifications) ? (raw.clarifications as Clarification[]) : []
  const open = Array.isArray(raw.outstanding) ? (raw.outstanding as Clarification[]) : []
  return {
    clarifications: rows,
    outstanding: open,
    scoreable: raw.scoreable === true,
    message: typeof raw.message === 'string' ? raw.message : '',
  }
}

// ------------------------------------------------- incidents and exposure

// Where a fact came from. Everything the traversal returns today is DECLARED:
// somebody in this organisation recorded it on the vendor record. OBSERVED is
// the same field filled in later by a platform product that watched it happen.
// It is in the vocabulary now so that no screen has to be rewritten on the day
// the second source arrives; only the chip beside the fact changes.
export type FactSource = 'DECLARED' | 'OBSERVED'

// One fact that turned a statutory clock on, in the words the catalogue uses.
export type EngagedFact = { fact: string; value: string; source: FactSource }

export type ExposedElement = {
  elementCode: string
  elementName: string
  classification: string
  personal: boolean
  sensitive: boolean
  note?: string
  source: FactSource
}

// What the vendor handles, as the register has it.
//
// subjectCountBand is a band and subjectCountBandWords is the sentence that
// goes with it. There is no figure on this type and there must never be one:
// a band rendered as a number would be this console inventing precision about
// how many people have to be told.
export type ExposedData = {
  elements: ExposedElement[]
  highestClassification: string
  anyPersonalData: boolean
  anySensitivePersonalData: boolean
  subjectCountBand: string
  subjectCountBandWords: string
  processingPurpose?: string
  retentionPeriodMonths?: number | null
  retentionBasis?: string
  deletionMechanism?: string
  recordedBy?: string
  recordedAt?: string
  source: FactSource
}

export type ExposedPlacement = {
  storageCountry?: string
  processingCountry?: string
  transferCountries: string[]
  storedOrProcessedOutsideIndia: boolean
  transferredOutsideIndia: boolean
  source: FactSource
}

export type ExposedSubprocessor = {
  id: string
  legalName: string
  country?: string
  serviceDescription?: string
  status: string
  declaredBy: string
  source: FactSource
  // How many other vendors of this tenant name the same party. The
  // concentration answer at a glance, before anybody opens the list.
  alsoBehindOtherVendors: number
}

export type ConcentrationEntry = {
  subprocessorLegalName: string
  vendorId: string
  vendorCode: string
  vendorLegalName: string
  vendorStatus: string
  tier?: number
  tierLabel?: string
  band?: string
  source: FactSource
}

export type DependencyEngagement = {
  serviceSummary?: string
  active: boolean
}

export type ExposureDependency = {
  businessFunction?: string
  businessProcess?: string
  serviceDescription?: string
  serviceCriticality?: string
  dependencyLevel?: string
  continuityDependency?: string
  criticalBusinessService: boolean
  customerFacing: boolean
  productionAccess: boolean
  engagements: DependencyEngagement[]
  source: FactSource
}

// One statutory duty as this incident engages it, or as it does not.
//
// A duty that does not apply arrives with the same weight as one that does,
// carrying notApplicable rather than being left out. Somebody signing off a
// response has to be able to tell "we considered the Reserve Bank and it does
// not reach this vendor" from "the product has never heard of the Reserve
// Bank", and an absent row cannot say which it was.
//
// window is a Go duration such as "6h0m0s", stored that way so whoever owns
// the regulatory content can check it against the circular. windowWords turns
// it into English; nothing prints it raw beside a statute.
//
// owedWithoutDelay is the duty the instrument owes now rather than inside a
// window. dueAt equals the discovery time on those, remainingSeconds is
// negative from the first second, and overdue is never set: a duty owed
// without delay cannot be late in the way a six hour one can, and reporting it
// as overdue would put a false fact about an instrument on the screen.
export type ExposureClock = {
  code: string
  name: string
  instrument: string
  trigger: string
  window?: string
  owedWithoutDelay?: boolean
  owedBy: string
  owedTo: string
  why: string
  applicable: boolean
  notApplicable?: string
  because?: EngagedFact[]
  dueAt: string
  remainingSeconds: number
  overdue: boolean
  // Set once somebody recorded that the body this duty is owed to was told:
  // a person here, FOCTTA Consent, or another breach tool (migration 0093). A
  // told duty is discharged and nothing chases it.
  told?: DutyNotice
}

export type DutyNotice = {
  at: string
  by: string
  reference?: string
  breachReference?: string
  via: 'PERSON' | 'FOCTTA_CONSENT' | 'REGISTRY_KEY'
  recordedBy: string
  recordedAt: string
}

export type DutyToldInput = { toldAt: string; toldBy?: string; reference?: string }

// The register's answer to "is anything overdue here", which is the only clock
// question a list can honestly answer in one column. It arrives only where the
// caller asked for clocks, because each row is a traversal of that vendor's
// declared facts.
//
// nextDueAt names the earliest duty that is neither overdue nor owed without
// delay, so a row can be past one deadline and still counting down to another.
// The three numbers are separate because they are separate facts: a duty owed
// without delay is never counted as overdue, since the instrument sets no
// window for it to pass.
export type IncidentClocks = {
  applicable: number
  owedWithoutDelay: number
  overdue: number
  nextDueAt?: string
  nextDue?: string
}

// One recorded vendor incident. The register names the supplier on every row,
// so nothing here has to open another screen to print a column.
export type Incident = {
  id: string
  vendorId: string
  vendorCode?: string
  vendorName?: string
  reference: string
  discoveredAt: string
  beganAt?: string
  source: string
  summary?: string
  status: string
  severity?: string
  handledBy?: string
  closedAt?: string
  closureNote?: string
  createdBy?: string
  createdAt: string
  updatedAt: string
  clocks?: IncidentClocks
}

// What somebody records when the call comes in. The vendor is in the path and
// not in the body: an incident belongs to a supplier, and the route says so.
export type IncidentDraft = {
  discoveredAt: string
  beganAt?: string
  source: string
  summary?: string
  severity?: string
  handledBy?: string
}

// Only the fields being changed. Closing needs words on the same request,
// because the schema refuses a closure with none and the API asks for them by
// name rather than handing back a constraint nobody can read.
export type IncidentChange = {
  status?: string
  severity?: string
  handledBy?: string
  closureNote?: string
}

// status defaults to the live register, which is the list somebody opens during
// an incident. Ask for ALL to read the whole thing.
export type IncidentQuery = { status?: string[]; overdueOnly?: boolean; withClocks?: boolean; limit?: number }

// matched is what came back and scanned is what the read returned before the
// status filter, so a screen can say it is showing part of an answer. note is
// the API's own sentence about what this page is, including the one it adds
// where it stopped working out clocks partway down.
export type IncidentPage = {
  incidents: Incident[]
  matched: number
  scanned: number
  rowsRead: number
  queries: number
  note: string
}

// What became of a vendor's incidents in FOCTTA Consent (contract 11.5).
// consent.state says whether Consent is linked at all; delivery says whether
// Consent has the incident (null when not linked) and updateDelivery whether a
// later change to it is failing; breach is the newest statement Consent made,
// or null when it has made none or the reader's role is not shown it
// (restricted). more is true when there were more incidents than limit.
export type ConsentLinkState = 'NOT_LINKED' | 'CONNECTED_NO_FEED' | 'LINKED'
export type IncidentBreach = {
  status: 'SUSPECTED' | 'CONFIRMED' | 'DISMISSED' | 'NOTIFIED' | 'CLOSED'
  changedAt: string
  sequence: number
  reportReference: string
  breachReference: string | null
  dismissReason: 'NO_BREACH' | 'DUPLICATE' | 'OUT_OF_SCOPE' | 'MALICIOUS' | 'SPAM' | null
  boardNotifiedAt: string | null
  principalsNotifiedAt: string | null
  via: 'FOCTTA_CONSENT' | 'REGISTRY_KEY'
}
export type IncidentBreachRow = {
  id: string
  reference: string
  status: string
  severity: string | null
  discoveredAt: string
  delivery: 'NOT_SENT' | 'PENDING' | 'FAILED' | 'SENT' | 'DEAD' | null
  updateDelivery: 'FAILED' | 'DEAD' | null
  breach: IncidentBreach | null
}
export type IncidentBreachPage = {
  consent: { state: ConsentLinkState }
  restricted: boolean
  incidents: IncidentBreachRow[]
  limit: number
  more: boolean
}

// A write answers with the row it wrote and one sentence saying what happened.
// The sentence is the API's and is shown as it arrives: a console composing its
// own would drift from the rule that produced it.
export type IncidentReply = { incident: Incident; note: string }

// The whole answer, computed on every read and stored nowhere.
//
// hypothetical is true where somebody asked the question against a discovery
// time they supplied rather than against a recorded incident, which is what
// the first ten minutes of an incident are actually spent on. The screen says
// so rather than letting the answer be mistaken for a record.
//
// data and placement are null where nobody has recorded what the vendor
// processes. That is an absence of a record and not a finding of no data, and
// caveats carries the sentence that says which.
//
// rowsRead and queries are what the traversal cost. They are printed, because
// the claim this capability makes is that the answer arrives in seconds, and a
// claim about speed that cannot be checked is marketing.
export type Exposure = {
  vendorId: string
  vendorCode: string
  vendorLegalName: string
  vendorStatus: string
  tier?: number
  tierLabel?: string
  band?: string

  incident?: Incident
  discoveredAt: string
  hypothetical: boolean
  computedAt: string

  data?: ExposedData | null
  placement?: ExposedPlacement | null

  subprocessors: ExposedSubprocessor[]
  concentration: ConcentrationEntry[]
  dependency: ExposureDependency
  openFindings: Finding[]
  clocks: ExposureClock[]

  basis: string
  caveats: string[]
  rowsRead: number
  queries: number
}

// ------------------------------------------------------------ custom fields

export type FieldOption = { value: string; label: string }

export type FieldDefinition = {
  id: string
  entity: string
  fieldKey: string
  label: string
  helpText: string
  dataType: string
  options: FieldOption[]
  isRequired: boolean
  requiredWhen?: Record<string, unknown> | null
  isPii: boolean
  piiPurpose?: string
  retentionDays?: number | null
  section: string
  ordinal: number
  isActive: boolean
  origin: string
  originRef?: string
}

export type FieldValue = {
  fieldKey?: string
  text?: string | null
  number?: number | null
  bool?: boolean | null
  date?: string | null
  array?: string[] | null
  erased?: boolean
}

export type VendorFields = {
  definitions: FieldDefinition[]
  values: Record<string, FieldValue>
}

export type PromoteResult = {
  created: { column: string; fieldKey: string; id: string }[]
  skipped: { column: string; reason: string }[]
  note: string
}



// -------------------------------------------------------- questionnaires

// A published version is frozen, so the studio edits a draft and publishes it.
// draftVersionId is present only while one is open, which is what lets the list
// say at a glance where the work in progress is.
export type Questionnaire = {
  id: string
  code: string
  name: string
  description: string
  purpose: string
  isArchived: boolean
  createdAt: string
  createdBy: string
  versionCount: number
  publishedVersionId?: string
  publishedVersionNo?: number
  publishedAt?: string
  publishedQuestionCount?: number
  draftVersionId?: string
  draftVersionNo?: number
}

// What archiving or restoring answers: the questionnaire as it now stands and
// how many assessments sent with it are still open, which carry on either way.
export type QuestionnaireArchiveResult = {
  questionnaire: Questionnaire
  openAssessments: number
  note: string
}

export type QuestionnaireVersion = {
  id: string
  questionnaireId: string
  questionnaireCode: string
  purpose: string
  versionNo: number
  status: string
  contentSha256?: string
  questionCount: number
  publishedAt?: string
  publishedBy?: string
  approvedBy?: string
  sourcePack?: string
  notes?: string
  createdAt: string
  createdBy: string
}

export type Section = {
  id: string
  sectionKey: string
  title: string
  description: string
  ordinal: number
  // Absent when the section feeds the vendor band only. A typed section is
  // also scored on its own under that type; see RiskTypeScore.
  riskType?: string
  questions: Question[]
}

export type BoundModel = {
  code: string
  name: string
  tiers: number[]
  riskModelVersionId: string
  versionNo: number
}

export type QuestionnaireDetail = {
  questionnaire: Questionnaire
  versions: QuestionnaireVersion[]
  boundRiskModels: BoundModel[]
}

export type VersionDetail = {
  version: QuestionnaireVersion
  sections: Section[]
  questionCount: number
  openAssessments: number
  editable: boolean
  editNote?: string
}

// The wire shape for a save is not the read shape. A draft is written as whole
// sections keyed by `key`, and ordinals fall out of array order rather than
// being carried, because two questions claiming ordinal 3 is not a state worth
// being able to express.
export type SectionInput = {
  key: string
  title: string
  description: string
  // Omitted, not sent empty: the server checks the value against its catalogue
  // and an empty string is not in it.
  riskType?: string
  questions: QuestionInput[]
}

// The one conditional form the API accepts. Anything else is refused at publish
// with INVALID, so there is no reason for the console to be able to express a
// shape the server will not take.
export type VisibleWhen = { question_key: string; in: string[] }

export type QuestionInput = {
  key: string
  prompt: string
  helpText: string
  answerType: string
  options: QuestionOption[]
  isRequired: boolean
  allowsNa: boolean
  evidenceRequired: boolean
  visibleWhen?: VisibleWhen | null
  citations: string[]
}

export type SaveVersionResult = {
  id: string
  versionNo: number
  status: string
  sections: number
  questionCount: number
  questionKeys: string[]
  supersededVersionId?: string
  note: string
}

export type PublishResult = {
  id: string
  versionNo: number
  status: string
  contentSha256: string
  questionCount: number
  createdBy: string
  publishedBy: string
  riskModelsChecked: string[]
  note: string
}

export type DiffEntry = {
  questionKey: string
  sectionKey?: string
  from?: string
  to?: string
  prompt?: string
  inFlightAnswersAffected: number
}

export type VersionDiff = {
  from: QuestionnaireVersion
  to: QuestionnaireVersion
  sameQuestionnaire: boolean
  counts: Record<string, number>
  added: DiffEntry[]
  removed: DiffEntry[]
  reworded: DiffEntry[]
  retyped: DiffEntry[]
  reweighted: DiffEntry[]
  moved: DiffEntry[]
  // When a question is shown, and what answering it needs (required, N/A,
  // evidence). Absent from a server older than release 3.3.
  conditioned?: DiffEntry[]
  ruled?: DiffEntry[]
  inFlight: {
    openAssessmentsOnFromVersion: number
    questionsWithExistingAnswers: number
    note: string
  }
}

// ------------------------------------------------------------ risk models

export type RiskModel = {
  id: string
  code: string
  name: string
  description: string
  isDefault: boolean
  createdBy: string
  createdAt: string
  versionCount: number
  draftCount: number
  activeVersionId?: string
  activeVersionNo?: number
}

export type TierWhen = {
  data_categories_any?: string[]
  volume_band_in?: string[]
  system_access_in?: string[]
  regulated_process?: boolean
  material_outsourcing?: boolean
  cross_border?: boolean
  subcontractors_declared?: boolean
  always?: boolean
}

export type TierRule = { when: TierWhen; tier: number; why: string }

export type Cut = { max: number; band: string }

export type Gate = {
  key: string
  question_key: string
  answer_in?: string[]
  answer_not_in?: string[]
  when_unanswered?: boolean
  greater_than?: number
  effect: string
  band?: string
  reason: string
  citation: string
}

export type ScoringModel = {
  tier_rules: TierRule[]
  weights: Record<string, number>
  band_matrix: Record<string, Cut[]>
  gates: Gate[]
  coverage_floor: number
  provisional_ceiling: string
}

export type ModelVersion = {
  id: string
  riskModelId: string
  riskModelCode?: string
  versionNo: number
  status: string
  contentSha256?: string
  coverageFloor: number
  provisionalCeiling: string
  notes?: string
  createdBy: string
  createdAt: string
  activatedBy?: string
  activatedAt?: string
  approvedBy?: string
  model?: ScoringModel
}

export type KeySource = {
  questionnaireCode: string
  questionnaireVersionId: string
  versionNo: number
  questionCount: number
  because: string
  tiers?: number[]
}

export type RiskModelDetail = {
  riskModel: RiskModel
  versions: ModelVersion[]
  usedAgainst: KeySource[]
}

export type ModelValidation = {
  riskModelVersionId: string
  versionNo: number
  status: string
  valid: boolean
  problems: string[]
  validatedAgainst: KeySource[]
  questionsCarryingNoWeight: string[]
  contentSha256: string
  note: string
}

export type ModelExplain = {
  riskModelVersionId: string
  riskModelCode: string
  versionNo: number
  status: string
  vendor: { id: string; vendorCode: string; name: string }
  against: {
    assessmentId: string
    reference: string
    questionnaireVersionId: string
    questions: number
    answers: number
  }
  result: unknown
  comparison: { standingBand: string; underThisVersion: string; changes: boolean }
  note: string
}

export type BandChange = {
  vendorId: string
  vendorCode: string
  vendorName: string
  assessmentReference: string
  from: string
  to: string
}

export type LapsedOverride = {
  vendorCode: string
  vendorName: string
  assessmentReference: string
  wasOverriddenTo: string
  wasToRunUntil?: string
  computedBandNow: string
}

export type ModelPromoteResult = {
  id: string
  riskModelCode: string
  versionNo: number
  status: string
  contentSha256: string
  createdBy: string
  activatedBy: string
  validatedAgainst: KeySource[]
  retiredVersionId?: string
  recompute: {
    requested: boolean
    assessmentsOnPriorVersion?: number
    assessmentsRescored?: number
    vendorsChangedBand?: number
    changes?: BandChange[]
    overridesLapsed?: LapsedOverride[]
    failed?: { vendorId: string; vendorCode: string; error: string }[]
  }
  note: string
}

// ------------------------------------------ the approval matrix and the gate

// What has to be true before a vendor becomes ACTIVE, and who has to sign.
//
// Every sentence a person reads about the gate is the API's: the condition
// names, the words shown when one is unmet, the reason each is asked, what was
// found on this vendor today, and which roles hold the capability a stage wants.
// None of it is composed here. The refusal on the button and the checklist
// beside it are then the same words, because they are one source.

// The paths, in one block. This console is the only client of them, so a rename
// on the API side is a change here and at no call site.
const APPROVAL_PATHS = {
  activation: (vendorId: string) => '/v1/vendors/' + encodeURIComponent(vendorId) + '/activation',
  approvals: (vendorId: string) => '/v1/vendors/' + encodeURIComponent(vendorId) + '/approvals',
  versions: '/v1/approval-matrix-versions',
  version: (id: string) => '/v1/approval-matrix-versions/' + encodeURIComponent(id),
}

// One signature the matrix asks for. Two stages of one row may not ask for the
// same capability: one person holding only that could sign both, and a matrix a
// single person can satisfy is not a matrix.
export type ApprovalStage = {
  capability: Capability
  why: string
  // What the stage is called on a screen. Optional: a stage without one is
  // called by the act its capability names.
  name?: string
  // The roles whose holders may sign this stage, where holding the capability
  // is not enough (release 3.2): the Security and risk approval is the CISO's
  // or the risk officer's, not every holder of the capability. Optional and
  // absent on documents written before it. Typed here because the editor
  // saves the whole document it was given, and a type without it invited
  // code that rebuilt a stage from the fields it knew and dropped this one,
  // which would silently open the stage to every holder of the capability.
  roles?: string[]
}

export type MatrixRow = {
  band: string
  tier: number
  stages: ApprovalStage[]
  // Whether reaching this band needs somebody to accept the risk in writing.
  // Absent on documents written before section 14's opening line was enforced,
  // which is why it is optional rather than defaulted here: the API omits it
  // when false so that an old document re-marshals to the same content hash.
  requiresAcceptance?: boolean
}

// Which of the named conditions are in force at one tier. Per tier because a
// stationery supplier and a core banking processor do not need the same gate.
export type TierConditions = { tier: number; conditions: string[]; why: string }

// The versioned document: who signs, and what must be true. Document rather
// than Matrix because the screen that edits it is called ApprovalMatrix and one
// name for both would be a rename waiting to happen.
export type MatrixDocument = {
  rows: MatrixRow[]
  conditions: TierConditions[]
  // Whether an approved contract waiver stands in for a missing contract
  // (release 3.2). Optional and absent on documents written before it; kept
  // on save for the reason ApprovalStage.roles is.
  contractWaiver?: boolean
}

export type ApprovalMatrixVersion = {
  id: string
  versionNo: number
  status: string
  contentSha256?: string
  notes?: string
  createdBy: string
  createdAt: string
  activatedBy?: string
  activatedAt?: string
  approvedBy?: string
  // The first of the two people who put a draft the release upgrade added
  // into force: the TPRM Admin who proposed it, and when (release 3.2).
  proposedBy?: string
  proposedAt?: string
  // Whether the release upgrade added this version, rather than a person.
  fromRelease?: boolean
  matrix?: MatrixDocument
}

// The condition vocabulary, served beside the versions so an editor never holds
// its own copy of the six codes and their sentences.
export type GateConditionEntry = { code: string; name: string; unmet: string; why: string }

// Three states and not a boolean. "Not required here" and "not met" are
// opposite news, and a screen deriving one from two flags would derive it
// differently each time it was written.
export type GateConditionStateName = 'met' | 'outstanding' | 'not required'

export type GateConditionState = {
  code: string
  name: string
  state: GateConditionStateName | string
  unmet: string
  why: string
  // What was found on this vendor today, whether the condition is met, unmet
  // or not asked for at this tier.
  detail: string
}

export type GateStage = {
  index: number
  capability: Capability
  // What the stage is called on a screen: the matrix's own name for it ("TPRM
  // approval", "Business owner sign-off"). A version stored before release 3.2
  // names none, and the API then sends the act its capability names.
  name?: string
  why: string
  signed: boolean
  signedBy?: string
  signedAt?: string
  note?: string
  // Set when a signature stands against this stage and does not count: the
  // matrix moved under the round and this stage now asks for something else.
  superseded?: string
  // Role codes, from the API's own table. Never printed: signersText turns
  // them into the names a reader knows.
  heldBy: string[]
  // The roles the stage is limited to, where it names any (the chain on a
  // risk acceptance carries them; release 3.2). Role codes, never printed.
  roles?: string[]
  // The first stage nobody has signed. The API says which one rather than
  // leaving a client to re-derive the order and get it wrong once.
  next: boolean
}

// An approved, unexpired contract waiver standing in for a missing contract
// (release 3.2). Shown on the vendor page until the contract is filed.
export type StandingWaiver = {
  exceptionId: string
  expiresOn: string
  reason: string
}

export type ActivationGate = {
  vendorId: string
  tier?: number | null
  tierLabel?: string
  band: string
  matrixVersionId: string
  matrixVersionNo: number
  round: number
  met: boolean
  conditions: GateConditionState[]
  stages: GateStage[]
  unmet: GateConditionState[]
  // Release 3.2, all optional so an older API still reads. Whether this
  // vendor's rating needs a written risk acceptance under the rules in force;
  // whether those rules let a contract waiver stand in for a missing contract;
  // the waiver doing so now; and whether the round in flight runs under an
  // earlier rule than the one now in force.
  acceptanceRequired?: boolean
  contractWaiverAllowed?: boolean
  contractWaiver?: StandingWaiver | null
  pinnedToEarlierRule?: boolean
}

// ------------------------------------------------------ the ten steps

const ONBOARDING_PATHS = {
  progress: (vendorId: string) => '/v1/vendors/' + encodeURIComponent(vendorId) + '/onboarding',
  step: (vendorId: string, key: string) =>
    '/v1/vendors/' + encodeURIComponent(vendorId) + '/onboarding/' + encodeURIComponent(key),
  versions: '/v1/onboarding-profile-versions',
  version: (id: string) => '/v1/onboarding-profile-versions/' + encodeURIComponent(id),
}

export type StepSatisfaction = 'OBSERVED' | 'ASSERTED' | 'EITHER'

// One step, carrying two facts that are never merged: what the product saw, and
// what a person said. Where they disagree the console shows the disagreement
// rather than picking one, which is why there is no single "done" field here.
export type OnboardingStepState = {
  key: string
  name: string
  mandatory: boolean
  satisfiedBy: StepSatisfaction
  evidence?: string
  why: string

  observed: boolean
  observedDetail: string

  asserted: boolean
  assertedBy?: string
  assertedAt?: string
  assertedNote?: string

  satisfied: boolean
  diverged: boolean
}

export type OnboardingProgress = {
  vendorId: string
  versionId?: string
  versionNo: number
  isDefault: boolean
  // "seed" means the process was installed when the tenant was set up and
  // nobody here has changed it. Different from a process this organisation
  // decided on, and the screen says which.
  versionAuthor?: string
  steps: OnboardingStepState[]
  mandatoryMet: boolean
  outstanding: string[]
}

export type OnboardingStepReply = { onboarding: OnboardingProgress; note?: string }

// The step as it is authored, without the two facts a vendor puts against it.
export type OnboardingStepDef = {
  key: string
  name: string
  mandatory: boolean
  satisfiedBy: StepSatisfaction
  evidence?: string
  why: string
}

export type OnboardingProfileDoc = { steps: OnboardingStepDef[] }

export type OnboardingProfileVersion = {
  id: string
  versionNo: number
  status: string
  contentSha256?: string
  notes?: string
  createdBy: string
  createdAt: string
  activatedBy?: string
  activatedAt?: string
  approvedBy?: string
  profile?: OnboardingProfileDoc
  isDefault: boolean
}

export type OnboardingVersionList = {
  versions: OnboardingProfileVersion[]
  note?: string
  default?: OnboardingProfileDoc
}

export type OnboardingVersionRead = {
  version: OnboardingProfileVersion
  problems: string[]
  valid: boolean
}

export type OnboardingDraftResult = {
  id: string
  versionNo: number
  status: string
  startedFrom: string
  clonedFrom?: string
  problems: string[]
  valid: boolean
  note: string
}

export type OnboardingSaveResult = {
  id: string
  status: string
  contentSha256: string
  problems: string[]
  valid: boolean
  note: string
}

export type OnboardingPromoteResult = {
  id: string
  versionNo: number
  status: string
  contentSha256: string
  mandatory: string[]
  retiredVersionId?: string
  note: string
}

// gateApplies is the whole of design point four. The gate is enforced on the
// move from pending approval into the register and nowhere else, so a vendor
// already ACTIVE is evaluated and never demoted, and the note says so in the
// API's words.
export type ActivationView = {
  vendorId: string
  status: string
  activation: ActivationGate
  gateApplies: boolean
  note?: string
  // Why THIS reader may not give the business stage of this vendor's approval,
  // in the API's own words, or empty when they may. The business sign-off is
  // the vendor's own business owner's, which is a fact about the record and not
  // about a role, so the API decides it and the button only shows the answer.
  businessSignoffRefusal?: string
  // Why THIS reader may not sign the stage in front of the vendor, whichever
  // stage it is, decided by the signing route's own rule (release 3.2): empty
  // when they may, absent on an API from before it.
  nextStageRefusal?: string
}

// What the lifecycle read carries beside the moves. A summary, because that
// read happens on every vendor screen; the detail is one request away.
export type ActivationSummary = {
  met: boolean
  band: string
  tierLabel?: string
  matrixVersionNo: number
  round: number
  outstanding: number
  readAt?: string
}

export type ApprovalSignResult = {
  id: string
  vendorId: string
  round: number
  stageIndex: number
  capability: string
  signedBy: string
  signedAt: string
  eventId?: string
  // The evaluation after the signature landed, so the panel shows what is left
  // rather than what was left.
  activation: ActivationGate
  note: string
}

export type ApprovalMatrixList = {
  versions: ApprovalMatrixVersion[]
  conditions: GateConditionEntry[]
  bands: string[]
}

// The read validates what is stored and says so. There is no separate check
// route: a document is checked when it is read, when it is saved and again
// before it is promoted.
export type ApprovalMatrixVersionView = {
  version: ApprovalMatrixVersion
  problems: string[]
  valid: boolean
  // Who changed the draft after it was written. None of them may put it in
  // force (release 3.2).
  editedBy?: string[]
}

export type MatrixWriteResult = {
  id: string
  versionNo?: number
  status: string
  contentSha256?: string
  problems: string[]
  valid: boolean
  startedFrom?: string
  clonedFrom?: string
  // A save that changed a proposed draft withdrew the proposal (release 3.2).
  proposalWithdrawn?: boolean
  note?: string
}

export type MatrixPromoteResult = {
  id: string
  versionNo: number
  // DRAFT when this call was the TPRM Admin's proposal of a draft the
  // release added, which puts nothing in force; ACTIVE otherwise.
  status: string
  contentSha256?: string
  createdBy?: string
  activatedBy?: string
  proposedBy?: string
  proposedAt?: string
  confirmedBy?: string
  retiredVersionId?: string
  note?: string
}

// The evaluation carried by a refused activation. A 409 ACTIVATION_BLOCKED
// holds the whole checklist, so the console shows the same list it would have
// shown before the button was pressed rather than an error code.
export function activationFromError(err: unknown): ActivationGate | null {
  if (!(err instanceof ApiError)) return null
  const body = err.body as { activation?: ActivationGate } | null
  return body?.activation ?? null
}

// ------------------------------------------------------------- the way out

// Section 17's checklist. Eleven acts performed on the way out of a
// relationship, each with the sentence saying what is still wanted and the
// reason it is asked for at all.
//
// Every word shown on the offboarding screen arrives here. The item names, what
// is outstanding on each, why it matters and which ones actually hold the exit
// are all the API's, for the same reason the activation gate's are: the refusal
// on the button and the checklist beside it must not be able to disagree.
export type OffboardingItemState = {
  key: string
  name: string
  // What is still wanted, in the API's words, on an item nobody has recorded.
  outstanding: string
  why: string
  // Whether it holds the exit. The optional ones are the ones that quietly
  // never get done once nothing is asking for them, so they are shown too.
  mandatory: boolean
  done: boolean
  note?: string
  recordedBy?: string
  recordedAt?: string
}

export type OffboardingGate = {
  vendorId: string
  status: string
  met: boolean
  done: number
  total: number
  mandatory: number
  items: OffboardingItemState[]
  outstanding: OffboardingItemState[]
  blocking: OffboardingItemState[]
}

export type OffboardingView = {
  offboarding: OffboardingGate
  message: string
  // Readable at any status, recordable only during the exit itself. An item
  // ticked against a supplier we are still buying from is a tick for something
  // nobody has done.
  recordable: boolean
}

export type OffboardingRecordResult = {
  item: OffboardingItemState
  changed: boolean
  offboarding: OffboardingGate
  message: string
}

// The evaluation carried by a refused offboarding, read the same way the
// activation refusal is. A 409 OFFBOARDING_INCOMPLETE holds the whole
// checklist, and without this the console showed the sentence and none of the
// items it names.
export function offboardingFromError(err: unknown): OffboardingGate | null {
  if (!(err instanceof ApiError)) return null
  const body = err.body as { offboarding?: OffboardingGate } | null
  return body?.offboarding ?? null
}

const OFFBOARDING_PATHS = {
  checklist: (vendorId: string) => '/v1/vendors/' + encodeURIComponent(vendorId) + '/offboarding',
  item: (vendorId: string, key: string) =>
    '/v1/vendors/' + encodeURIComponent(vendorId) + '/offboarding/' + encodeURIComponent(key),
}

// ------------------------------------------------------- sustain and export

export type TierProfile = {
  tier: number
  configured: boolean
  questionnaireId?: string
  questionnaireCode?: string
  questionnaireName?: string
  riskModelId?: string
  riskModelCode?: string
  cadenceMonths: number | null
  reassessOn: string
  // Stored, and read by no issuing path. The card shows TierIssuing instead.
  requiresApproval: boolean
}

// Whether an invitation waits for a second person, as the API enforces it:
// a single vendor's invitation follows the deployment setting, and a campaign
// always does. Absent from an API older than this field.
export type TierIssuing = {
  singleInviteNeedsApprover: boolean
  campaignNeedsApprover: boolean
  // Tiers whose own setting (Settings, Workflows) holds a single invitation,
  // and Reassess now, for a second person.
  tiersNeedingSecondPerson?: number[]
}

export type PolicyVendor = {
  vendorId: string
  vendorCode: string
  vendorName: string
  inherentTier: number | null
  vendorRecordNextDueOn?: string
  lastAssessmentOn?: string
  lastAssessmentReference?: string
  openAssessmentReference?: string
}

// A value with no provenance cannot answer the only question this screen is
// asked, which is why a given vendor is on the cadence it is on.
export type ResolvedField = {
  field: string
  value: unknown
  source: string
  detail: string
}

export type ResolvedPolicy = {
  cadenceMonths: number | null
  reassessOn: string
  defaultMode: string
  suppressionDays: number
  eventTriggers: Record<string, unknown>
  isPaused: boolean
  pausedReason: string | null
  nextDueOn: string | null
  nextDueBasis: string
  nextDueSource?: 'CHOSEN' | 'FREQUENCY' | 'NONE' | string
  nextDueChosen?: ChosenNextDue | null
}

export type PolicyView = {
  // D15 on a save (release 3.3, M4): said, never refused.
  warnings?: DateWarning[]
  vendor: PolicyVendor
  resolved: ResolvedPolicy
  provenance: ResolvedField[]
  tierDefault: TierProfile | null
  override: unknown
  note: string
}

export type TriggerEvent = {
  id: string
  vendorId: string
  vendorCode?: string
  vendorName?: string
  triggerType: string
  detail: unknown
  dueOn: string
  affectedSectionKeys: string[]
  status: string
  reason?: string
  mergedIntoId?: string
  raisedAt: string
  raisedBy: string
}

export type TriggerList = {
  triggers: TriggerEvent[]
  count: number
  overdue: number
  note: string
}

export type EvidencePack = Record<string, unknown>

// Whether the API recorded the export it has just answered.
//
// It appends "evidence pack exported" to the chain when it builds a pack. If
// that append fails it still answers 200 with the pack, because refusing a
// regulator the document over a record that could not be written would be the
// worse failure, and says so in a header (apps/tprm-api/internal/api/
// sustain.go, evidenceExport). The console did not read the header, so the
// Evidence screen could say an audit act had happened when it had not (the
// last re-review, 11 October 2026).
export function exportRecorded(headers: { get: (name: string) => string | null }): boolean {
  return headers.get('X-Tprm-Export-Unrecorded') !== 'true'
}

// ------------------------------------------------------------- contacts

// A vendor is not one person. The signatory who can commit the company is
// rarely the engineer who knows the answer, and neither is the grievance
// officer a data principal has to be given.
export type ContactRole =
  | 'PRIMARY'
  | 'AUTHORISED_SIGNATORY'
  | 'SECURITY'
  | 'PRIVACY'
  | 'GRIEVANCE'
  | 'ESCALATION'
  | 'BILLING'

export type Contact = {
  id: string
  vendorId: string
  respondentRef: string
  role: string
  fullName?: string
  designation?: string
  email?: string
  mobileE164?: string
  purpose: string
  retainUntil?: string
  erasedAt?: string
  erasureReason?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  // The one a campaign would actually address today.
  wouldBeInvited: boolean
}

export type ContactInput = {
  role: string
  fullName: string
  designation?: string
  email?: string
  mobileE164?: string
  purpose?: string
  retainUntil?: string
}

// ------------------------------------------------------------- documents

// A vendor's contract dates from both sources (release 3.3, C13; the server's
// repo/contract_dates.go). start, end and source are what the product reads.
export type VendorContract = {
  recordStart?: string
  recordEnd?: string
  documentId?: string
  kind?: string
  filedFrom?: string
  filedEnd?: string
  autoRenews?: boolean
  start?: string
  end?: string
  source?: 'MSA' | 'SOW' | 'RECORD'
}

// One row of "Dates to check" (release 3.3, M4): a stored date that breaks a
// rule (kind error) or carries a warning, in the rule's words, and where to fix it.
export type DateToCheck = {
  rule: string
  kind: 'error' | 'warning'
  vendorId: string
  vendorName: string
  documentId?: string
  subject: string
  from?: string
  to?: string
  message: string
  fix: string
}

export type VendorDocument = {
  id: string
  vendorId: string
  vendorName?: string
  docType: string
  title: string
  objectKey: string
  contentSha256: string
  uploadedBy: string
  createdAt: string
  // Only the vendor's list carries this. The API reads it through the same
  // fold the provisional build uses, so the sum across the list is what the
  // build has to take from.
  acceptedCount?: number

  // Where it came from, and what became of it. A document a supplier sent
  // counts for nothing until somebody here accepts it: the contract condition
  // reads this table, so an unaccepted upload satisfying it would be a vendor
  // filing the evidence for their own approval. See migration 0039.
  source?: 'INTERNAL' | 'VENDOR'
  acceptedAt?: string
  acceptedBy?: string
  declinedAt?: string
  declinedBy?: string
  declineReason?: string
  // Whether an MSA or SOW renews itself (release 3.3, C14).
  autoRenews?: boolean

  // How the worker read it (release 3.3). QUEUED and READING are a document
  // being read; NOT_READ carries the reason in readNote, and a document that
  // was read can still have pages nobody read, named in readNote too.
  // holdsPersonalDataValues says values were found and masked before any text
  // was kept; absent until the document has been read.
  readState?: 'QUEUED' | 'READING' | 'READ' | 'NOT_READ'
  readNote?: string
  readAt?: string
  readBy?: string
  holdsPersonalDataValues?: boolean
  legacyTextHoldsValues?: boolean
  fileName?: string
}

// readByWords says how a document was read, when it was not all from the
// file's own text (release 3.3, D6): a value OCR read is one a person checks
// against the file. Read from the file says nothing extra.
export function readByWords(readBy?: string): string {
  switch (readBy) {
    case 'ocr':
      return 'Read by OCR'
    case 'native+ocr':
      // Not "Partly read": that is a reading its budget stopped. Every page
      // was read, the scanned ones by OCR.
      return "Read by OCR: the scanned pages; the rest from the file's text"
  }
  return ''
}

// termPlaceWords is where a contract term was read, and how when the OCR
// add-on read that page: a value OCR read is one a person checks against the
// file before agreeing (review D6, should-fix 4).
export function termPlaceWords(t: { locator: string; place?: { readBy?: string } }): string {
  return t.place?.readBy === 'ocr' && t.locator !== '' ? t.locator + ', read by OCR' : t.locator
}

// awaitingDecision is the whole rule, in one place, so no screen has to
// remember which combination of three fields means "somebody has to look at
// this".
export function awaitingDecision(d: VendorDocument): boolean {
  return d.source === 'VENDOR' && !d.acceptedAt && !d.declinedAt
}

// ------------------------------------------- the evidence register

// One row of the cross-vendor document list. Status and daysToExpiry are
// derived by the API against a date it takes once for the whole page, and are
// never stored, so a register drawn across midnight cannot report two answers
// about the same document. Nothing here recomputes them.
export type RegisterDocument = VendorDocument & {
  vendorCode: string
  effectiveFrom?: string
  expiresOn?: string
  status: 'VALID' | 'EXPIRING' | 'EXPIRED' | 'NO_EXPIRY' | 'AWAITING' | 'DECLINED'
  daysToExpiry?: number
  // The standing legal review's outcome, for an agreement that has one
  // (release 3.1, package T3). Absent otherwise.
  legalReview?: string
}

// total is what the filter matched and omitted is what this page did not
// carry. Both are printed, because a register that stops at its limit without
// saying so reports a smaller and better covered estate than the one that
// exists, and that is the direction of error that gets somebody hurt.
export type DocumentRegister = {
  documents: RegisterDocument[]
  matched: number
  total: number
  omitted: number
  limit: number
  offset: number
  // The breakdown with the status filter taken out, so it is what somebody
  // picks the next status from rather than a restatement of the one they have.
  byStatus: Record<string, number>
  asOf: string
}

export type DocumentVocabulary = {
  kinds: { code: string; name: string }[]
  statuses: string[]
  note: string
}

export type ExtractionRun = {
  runId: string
  extractedAt: string
  extractedBy: string
  questionnaireVersionId: string
  ruleTableSha256: string
  counts: { proposed: number; accepted: number; rejected: number }
}

// Release 3.3, documents lane. Output A: personal data values found inside a
// vendor's documents, masked, with where and in what context. Output B: what
// the contract says the vendor will handle. The two are never mixed.
export type DocumentFinding = {
  id: string
  vendorId: string
  documentId: string
  documentTitle?: string
  kind: string
  context: 'SIGNATORY' | 'DATA_SAMPLE' | 'IN_TEXT' | 'PARTY_ID'
  partyIdentifier: boolean
  count: number
  maskedSample: string
  place: string
  readBy: 'native' | 'ocr'
  confidence: number
  engine: string
  status: 'OPEN' | 'EXPECTED' | 'REDACTION_REQUESTED' | 'RESTRICTED'
  decidedBy?: string
  decidedAt?: string
  note?: string
  // The document is restricted to evidence readers and this reader is not
  // one: the sample and the place are blank, the row is still counted.
  withheld?: boolean
}

export type DocumentFindingSet = {
  vendorId: string
  findings: DocumentFinding[]
  byContext: Record<string, number>
  byKind: Record<string, number>
  kinds: string[]
  open: number
  documents: { documentId: string; title: string; readState: string; readNote?: string; scan: string; restricted: boolean }[]
  decisions: Record<string, string>
  note: string
  // Said once when some rows are withheld.
  restrictedNote?: string
}

export type ContractTerm = {
  key: string
  documentId: string
  kind: string
  code: string
  value?: string
  detail?: Record<string, string>
  locator: string
  // The part read: readBy is 'ocr' on a page the OCR add-on read.
  place?: { readBy?: string }
  section: string
  snippet: string
  matched: string
  confidence: number
  why: string
  status: 'PROPOSED' | 'ACCEPT' | 'REJECT'
  // The document is restricted to evidence readers and this reader is not
  // one: the snippet and the matched words are blank.
  withheld?: boolean
  decision?: {
    decision: string
    value?: string
    note?: string
    decidedBy?: string
    decidedAt?: string
    // Made on an earlier reading that read the same thing in the same place.
    carried?: boolean
    runId?: string
  }
}

export type ContractGap = {
  code: string
  title: string
  detail: string
  basis?: string[]
  // Rests on an absence while some document was not read in full.
  partial?: boolean
  // The latest raise of it as a finding. A dismissed gap is not listed until
  // a document is read again.
  decision?: { state: 'RAISED' | 'DISMISSED'; at: string; findingId?: string | null; reason?: string | null }
}

export type ContractTermSet = {
  vendorId: string
  proposals: ContractTerm[]
  gaps: ContractGap[]
  counts: { needsDecision: number; accepted: number; rejected: number; gaps: number }
  documents: {
    documentId: string
    title: string
    readState: string
    readNote?: string
    termsRead: boolean
    // False for a document the supplier sent that nobody accepted.
    counts: boolean
    termsNote?: string
    runId?: string
    readAt?: string
  }[]
  // Answers filled in for the vendor (from the contract or the data record)
  // that the vendor changed in the portal before confirming them, newest first.
  vendorCorrections?: VendorCorrection[]
  lexiconSha256: string
  note: string
  // Said once when some rows are withheld.
  restrictedNote?: string
}

export type VendorCorrection = {
  reference: string
  at: string
  questionKey: string
  // The question and the options in words, where the questionnaire is found.
  prompt?: string
  wasWords?: string
  nowWords?: string
  was: string
  now: string
  notApplicable: boolean
  provenance: string
  readFrom: string
}

export type DocumentReading = {
  state: 'QUEUED' | 'READING' | 'READ' | 'NOT_READ'
  note?: string
  readAt?: string
  // native, ocr (every page read by the OCR add-on) or native+ocr.
  readBy?: string
  // reading (the masked text the worker kept) or legacy (an earlier release's
  // upload text, masked as it is read); empty when no text is kept.
  textSource?: string
  holdsPersonalDataValues?: boolean
  legacyTextHoldsValues?: boolean
  // The original file opens only for people who may read evidence.
  restrictedToEvidenceReaders?: boolean
}

export type DocumentDetail = {
  document: VendorDocument
  extractable: boolean
  extraction?: ExtractionRun
  textRetainedBytes?: number
  reading?: DocumentReading
}

// A proposal is a reading of a clause, not an answer. Nothing scores until a
// person has accepted it, which is why every one carries the snippet it came
// from and a note on what to check before agreeing.
export type Proposal = {
  questionKey: string
  control: string
  proposedValue: string
  locator: string
  snippet: string
  confidence: number
  matchedPhrase: string
  why: string
  status: string
}

export type ProposalSet = {
  documentId: string
  vendorId: string
  title: string
  runId: string
  questionnaireVersionId: string
  ruleTableSha256: string
  extractedAt: string
  extractedBy: string
  counts: { proposed: number; accepted: number; rejected: number }
  proposals: Proposal[]
}

// ------------------------------------------------------------------ shapes

export type Issue = { field: string; code: string; detail: string }

export type Tenant = {
  id: string
  slug: string
  legalName: string
  displayName: string
  status: string
  deploymentMode: string
  databaseIsolationMode: string
  dataRegion: string
  processorRole: string
  tprmEntitlement: string
}

export type Me = {
  subject: string
  roles: string[]
  // Every persona this account holds. One for an account that does one job;
  // more for an account still to be split before production.
  personas?: string[]
  // One account, one persona: the job this account works as, the console
  // screens that are that job, and whether the account still does more than
  // one. All optional, because an API from before personas named screens
  // answers without them, and absent screens means nothing is filtered.
  persona?: { code: string; name: string; job?: string } | null
  screens?: string[] | null
  splitBeforeProduction?: boolean
  personaNotice?: string
  tenant: Tenant
  authMode: string
  processorRole: string
}

// Personas as GET /v1/personas serves them (packages/core/domain/personas.go).
export type PersonaHomeItem = { key: string; label: string; href: string }
export type Persona = { code: string; name: string; job: string; role?: string; home: PersonaHomeItem[]; screens?: string[] }
export type PersonaCatalogue = { personas: Persona[]; derivedFrom: Record<string, string> }
// The Home control panel as GET /v1/home/panel serves it
// (apps/tprm-api/internal/api/home_panel.go). Every section is optional: the
// API leaves out what the reader's capabilities or persona do not reach,
// rather than sending a zero.
export type HomePeriodKey = '7d' | '30d' | '90d'
export type HomeTone = 'neutral' | 'info' | 'ok' | 'warn' | 'crit' | 'brand'
export type HomeKpi = {
  key: string
  label: string
  value: number
  change: number | null
  direction: 'up_good' | 'up_bad' | 'neutral'
  series: (number | null)[]
  seriesSince?: string
  basis: 'timestamps' | 'snapshots'
  href: string
}
export type HomeHealthComponent = { key: string; label: string; lost: number; max: number; count: number; href: string }
export type HomeHealth = {
  score: number
  word: string
  components: HomeHealthComponent[]
  inputs: Record<string, number>
  change: number | null
  series: (number | null)[]
  seriesSince?: string
}
export type HomeBand = { key: string; label: string; count: number; href: string }
export type HomeTierBands = { bands: string[]; short: string[]; rows: { tier: number; label: string; counts: number[]; hrefs: string[] }[] }
export type HomeWeek = { start: string; label: string; raised: number; closed: number; open: number }
// The findings trend over the period: 7 or 30 daily buckets, or 13 weekly. A
// day is labelled by the date it ends, a week by the date it began.
export type HomeFindingsTrend = { unit: 'day' | 'week'; buckets: HomeWeek[]; stillOpen: number; href: string }
// What happened in the period, from since (a UTC date), in a fixed order. A
// count whose list is another job's comes with an empty href.
export type HomePeriodSummary = { since: string; items: { key: string; label: string; count: number; href: string }[] }
// Vendors on the register at the start of the period and now whose band changed.
export type HomeBandMove = { band: string; in: number; out: number }
export type HomePipeline = {
  steps: { key: string; label: string; count: number; href: string }[]
  // "period" when the bars count only questionnaires sent since `since`.
  // Overdue is always the live count.
  scope?: 'period'
  since?: string
  overdue: number
  overdueVendor?: string
  overdueDays?: number
  overdueHref: string
}
export type HomeDeadline = { date: string; kind: string; title: string; subject: string; tone: HomeTone; word: string; href: string }
export type HomeClock = {
  kind: string
  title: string
  subject: string
  dueAt?: string
  remainingSeconds: number
  overdue: boolean
  withoutDelay?: boolean
  elapsed: number
  words: string
  href: string
}
// A journey in flight: where an exit, a questionnaire or a bulk send is. The
// state is a stored domain and code, worded by lib/status.ts like every other
// state; late lays "Overdue" over a questionnaire still with the vendor.
export type HomeJourney = {
  key: string
  kind: string
  title: string
  context?: string
  status: { domain: string; code: string }
  late?: boolean
  track?: ('done' | 'current' | 'blocked' | 'rejected' | 'upcoming')[]
  trackLabel?: string
  href: string
}
export type HomePanel = {
  period: { key: HomePeriodKey; days: number }
  computedAt: string
  persona: { code: string; name: string } | null
  vendorCount?: number
  health?: HomeHealth
  kpis: HomeKpi[]
  bands?: HomeBand[]
  tierBands?: HomeTierBands
  periodSummary?: HomePeriodSummary
  bandMoves?: HomeBandMove[]
  findingsWeekly?: { weeks: HomeWeek[]; stillOpen: number; href: string }
  findingsTrend?: HomeFindingsTrend
  pipeline?: HomePipeline
  clearance?: { system: string; cleared: number; pending: number; blocked: number; href: string }
  deadlines?: HomeDeadline[]
  clocks?: HomeClock[]
  journeys?: { items: HomeJourney[]; total: number }
  // Needs you: the first rows of the same list My work shows, with the size of
  // the whole list. They are My work's rows, so each carries My work's words and
  // its one button.
  needsYou?: { items: MyWorkItem[]; total: number }
  // How much is waiting for the reader in My work, by group, counted from the
  // same list GET /v1/my-work returns. Home shows the numbers and links there;
  // the work itself is done in My work or on the record it belongs to. The
  // shell's count takes it from here on Home rather than asking again.
  myWork?: MyWorkSummary
  notCounted?: { key: string; why: string }[]
}

// My work (release 3.2): one list of everything waiting for the person signed
// in. The API decides what is in it (each row is a task the reader's roles may
// do, on a screen that is part of their job); the console decides the words,
// from lib/status.ts, so a row, a chip and a button never use two words for
// one thing.
export type MyWorkGroup = 'decide' | 'review' | 'follow-up' | 'notice'
// The list's filters: every task, one group, or the tasks on vendors the reader
// owns. Notices are a filter of their own and not among "all".
export type MyWorkShow = 'all' | MyWorkGroup | 'mine'
export type MyWorkKind =
  | 'review'
  | 'clarification'
  | 'document'
  | 'chase'
  | 'send'
  | 'finding'
  | 'vendor-approval'
  // A vendor request waiting for a TPRM lead (release 3.3).
  | 'vendor-request'
  | 'tier-change'
  | 'exception'
  | 'exception-review'
  | 'exception-closure'
  | 'campaign-approval'
  | 'exit-proposal'
  | 'exit-sign'
  | 'notice'
export type MyWorkItem = {
  // Unique across the list: the kind and the id of the thing it is about.
  key: string
  kind: MyWorkKind
  group: MyWorkGroup
  // The thing the task is about, usually the vendor.
  title: string
  // One short line on what is waiting.
  what: string
  // The state for the status chip, read through lib/status.ts.
  status: { domain: string; code: string }
  dueOn?: string
  late?: boolean
  // When it started waiting.
  since?: string
  // On a vendor the reader owns.
  mine?: boolean
  // Where the task is done, and whether the row can do it in place: chase
  // resends the invitation from the row, mark-read files a notice.
  action: { href: string; kind: 'open' | 'chase' | 'mark-read' }
  vendorId?: string
  assessmentId?: string
  noticeId?: string
}
// total is the tasks, every group but notices; counts has each group, the
// unread notices (counted in full, though only the newest are listed) and
// mine, the tasks on vendors the reader owns.
export type MyWorkSummary = { total: number; counts: Record<MyWorkGroup | 'mine', number> }
export type MyWorkCount = MyWorkSummary & { persona: { code: string; name: string } | null; computedAt: string }
export type MyWork = MyWorkCount & {
  show: MyWorkShow
  // One page of the filter asked for, how many rows the filter holds, and the
  // value to send as after for the next page (empty on the last).
  items: MyWorkItem[]
  matched: number
  next: string
}
export type PersonaMapping = {
  id: string
  claimValue: string
  persona: string
  note?: string
  createdBy: string
  createdAt: string
  // When the mapping stops naming its persona (release 3.1); absent for never.
  expiresAt?: string
}
export type PersonaMappingPreview = { roles: string[]; personas: string[]; message?: string; refused?: boolean }

// The role table as GET /v1/roles serves it. Which capability a role holds is
// the API's answer and never this file's: the API enforces one table, and a
// console carrying a second copy of it drifts, at which point the person
// pressing the button is the one who finds out. The lifecycle is read the same
// way and for the same reason.
//
// The union below is the vocabulary, not the mapping. It is here so a mistyped
// capability is a build error rather than a control that sits greyed out
// forever with nobody the wiser.
export type Capability =
  | 'session.read'
  | 'vendor.read'
  | 'assessment.read'
  | 'config.read'
  | 'reports.read'
  | 'audit.read'
  | 'evidence.read'
  | 'vendor.create'
  | 'vendor.edit'
  | 'vendor.import'
  // Discarding another person's import in progress (release 3.3).
  | 'vendor.import_admin'
  | 'evidence.upload'
  | 'evidence.review'
  | 'questionnaire.author'
  | 'riskmodel.author'
  | 'assessment.plan'
  | 'assessment.score'
  | 'finding.manage'
  | 'risk.accept'
  | 'config.write'
  | 'programme.write'
  | 'user.administer'
  | 'vendor.approve'
  // The TPRM lead's decision on a vendor request (release 3.3).
  | 'vendor.request_approve'
  | 'vendor.business_signoff'
  | 'offboarding.run'
  | 'questionnaire.publish'
  | 'questionnaire.issue'
  | 'riskmodel.promote'
  | 'risk.approve'
  | 'privacy.erase'
  // Legal counsel's (release 3.1, package T3).
  | 'legal.hold'
  | 'exception.review_legal'
  | 'dpa.review'

// act is the words a refusal uses, held once on the API side so a 403 and a
// disabled button in here say the same thing about the same wall.
export type CapabilityEntry = { code: Capability; act: string; why: string }

// grantable and beyondYourReach are answered by the API for the sign-in that
// asked and for nobody else, so a row is not shared between people. The console
// holds no copy of the rule behind them, and it held one once: a local
// comparison of role codes greyed out every grant the API had just been changed
// to allow, and nothing caught it because there is no console test harness in
// this repository. Whether somebody may hand out a role is a question POST
// /v1/users and PUT /v1/users/{subject}/roles answer before they write
// anything, and this screen asks rather than works it out.
//
// Both are optional because an API old enough not to answer must leave the
// People screen as it was rather than lock every checkbox on it. Absent is not
// refused; it is unasked, which is the same reading capabilities are given
// below.
export type RoleEntry = {
  code: string
  name: string
  why: string
  capabilities: Capability[]
  // Whether this caller may hand the role out. Declared reach on the role
  // rather than a comparison of capability sets: staffing a tenant and doing
  // its work are different jobs.
  grantable?: boolean
  // What the person being created will be able to do that the caller cannot.
  // Not a refusal: an administrator staffs a tenant with people who do work the
  // administrator does not do, and the screen says so rather than hiding it.
  beyondYourReach?: Capability[]
}

export type RoleCatalogue = { roles: RoleEntry[]; capabilities: CapabilityEntry[]; note: string }

// What the tier rules decided about one vendor. tier is the stored ordinal,
// which is never printed: 4 is the most exposed and the label is the name a
// person has learned. See tierLabel.
//
// recorded says whether a decision row stands behind this. A vendor scored
// before classifications were recorded carries a tier and no argument, and a
// screen has to be able to tell that apart from a tier nobody has decided.
export type VendorTier = {
  tier: number
  label: string
  displayRank: number
  recorded: boolean
  ruleKey?: string
  why?: string
  decidedAt?: string
  decidedBy?: string
  riskModelVersionId?: string
  // RULES for the classifier, MANUAL for a tier a person set and a second
  // person approved. Absent from an API older than the field.
  source?: 'RULES' | 'MANUAL' | string
  overrideId?: string
}

// What a reclassification did, in the API's own arithmetic rather than in a
// comparison this file would otherwise have to make for itself. setByHand says
// a manual tier stood, so the rules ran and moved nothing; rules is what they
// said.
export type ClassifyResult = {
  changed: boolean
  previousTier?: number
  previousTierLabel?: string
  decision?: VendorTier
  setByHand?: boolean
  rules?: VendorTier
}

// A request to set a vendor's tier by hand, and what became of it. tier is the
// stored ordinal and is never printed; tierLabel is.
export type TierOverride = {
  id: string
  vendorId: string
  tier: number
  tierLabel: string
  rulesTier?: number
  reason: string
  expiresOn?: string
  status: 'PENDING' | 'ACTIVE' | 'REJECTED' | 'WITHDRAWN' | 'ENDED' | 'EXPIRED' | 'SUPERSEDED' | string
  requestedBy: string
  requestedAt: string
  decidedBy?: string
  decidedAt?: string
  decisionNote?: string
  endedBy?: string
  endedAt?: string
  endNote?: string
}

// override is the manual tier in force, pending the request waiting for a
// second person, rules what the rules say now. All three are absent from an
// API older than wave 3.
export type VendorTierView = {
  current?: VendorTier
  history: VendorTier[]
  note: string
  override?: TierOverride
  pending?: TierOverride
  rules?: VendorTier
  overrides: TierOverride[]
}

// One tier as GET /v1/tiers serves it, in the order a screen shows them, with
// the rules in force for it. A tenant may relabel a tier, so this is read
// rather than assumed.
export type TierRow = {
  tier: number
  label: string
  displayRank: number
  why?: string
  rules?: { order: number; key: string; tier: number; why: string }[]
}

// The statutory identifiers a vendor created from the basics still owes.
// Recomputed by the API on every read; never cached here.
export type Statutory = {
  complete: boolean
  gaps: Issue[]
  note?: string
}

// What the tier rules read on Add a vendor, in the create's own names.
export type TierPreviewInput = {
  dataCategories: string[]
  volumeBand: string
  systemAccess: string
  regulatedProcess: boolean
  crossBorder: boolean
  criticalBusinessService: boolean
  productionAccess: boolean
  continuityDependency?: string
  regulatoryImpact?: string
}

// The answer: the tier and the rule's own reason, or no tier and a note when
// the tenant has no active risk model.
export type TierPreview = {
  tier: number | null
  tierLabel?: string
  displayRank?: number
  ruleKey?: string
  why?: string
  note?: string
}

export type VendorCreateResult = {
  id: string
  vendorCode?: string
  statutory?: Statutory
}

export type InviteRequest = {
  // Required by Reassess now, and to send due diligence to a suspended vendor
  // (release 3.3); not asked otherwise.
  reason?: string
  name?: string
  questionnaireCode?: string
  questionnaireVersionId?: string
  riskModelCode?: string
  riskModelVersionId?: string
  mode?: string
  dueOn?: string
}

export type InviteResult = {
  campaignId: string
  status: 'ISSUED' | 'DRAFT' | string
  vendor?: { id: string; vendorCode: string; name: string }
  invitedEmail?: string
  assessment?: { id: string; reference: string; dueOn: string; link: string; carriedForward?: number }
  // QUEUED only when the worker will hand the invitation to a carrier; MUTED,
  // OFF and NO_CARRIER mean nothing was emailed. See lib/mail.ts.
  mail?: MailState | string
  mailNote?: string
  // When nothing was emailed: whether the vendor can still sign in by phone.
  reach?: Reach
  policy?: string
  note?: string
}

// Reassess now, from the Reassessment screen: the single-vendor invitation,
// with a reason the API refuses to go without.
export type ReassessRequest = InviteRequest & { reason: string }

// One vendor on the Reassessment screen. The frequency and the next date are
// resolved exactly as the vendor's own policy panel resolves them.
export type ReassessmentState = 'IN_FLIGHT' | 'PAUSED' | 'ON_CHANGE' | 'OVERDUE' | 'DUE_SOON' | 'SCHEDULED'

export type ReassessmentRow = {
  vendorId: string
  vendorCode: string
  vendorName: string
  status: string
  // Whether the request counts as approved (release 3.3). Absent from an older
  // API, which reads as approved.
  requestApproved?: boolean
  inherentTier: number | null
  tierLabel?: string
  tierQuestionnaireCode?: string
  tierCadenceMonths: number | null
  lastAssessedOn: string | null
  lastAssessmentReference?: string
  openAssessmentReference?: string
  openAssessmentDueOn?: string
  cadenceMonths: number | null
  cadenceSource: 'VENDOR_OVERRIDE' | 'TIER_DEFAULT' | 'SYSTEM_DEFAULT' | string
  nextDueOn: string | null
  nextDueBasis: string
  // CHOSEN when a person set the date and it is still ahead, FREQUENCY
  // otherwise, NONE on change only. Absent from an API older than wave 3.
  nextDueSource?: 'CHOSEN' | 'FREQUENCY' | 'NONE' | string
  nextDueChosen?: ChosenNextDue
  isPaused: boolean
  pausedReason?: string
  state: ReassessmentState | string
}

// A date a person chose for the next reassessment, while it is the one in force.
export type ChosenNextDue = { on: string; setBy?: string; setAt?: string }

export type ReassessmentRoster = {
  vendors: ReassessmentRow[]
  total: number
  counts: Record<string, number>
  note: string
}

// Why a band is what it is, in one line, and which step of the explanation
// decided it.
export type BandReason = {
  band: string
  line: string
  step: string
  stepIndex: number
  gateKey?: string
  citation?: string
  // The deciding step's words without the band word or the override clause
  // (review of 12 Oct, N4). Older APIs leave it out.
  reason?: string
  overrideBand?: string
}

// How a vendor's request came to count as approved (release 3.3, migration
// 0108): a person, the importer, the upgrade or the demonstration seed.
export type RequestApproval = {
  at: string
  by?: string
  basis: 'PERSON' | 'IMPORT' | 'UPGRADE' | 'SEED' | string
  note?: string
}

// The request block of the lifecycle read: where the request stands and, for a
// reader who may decide it, whether they may approve or reject it now and why
// not. The Approve request drawer is drawn from this and holds no copy of the
// rule (domain.CheckRequestDecision).
export type RequestDecisionState = { allowed: boolean; code?: string; blockedBecause?: string }
export type VendorRequestStanding = {
  approved: boolean
  approval?: RequestApproval | null
  raisedBy: string
  secondPersonApproves: boolean
  legalFormKnown: boolean
  // Present only for a reader holding vendor.request_approve.
  approve?: RequestDecisionState
  reject?: RequestDecisionState
}

// The tenant's setting: whether a second person approves a vendor request.
export type VendorRequestPolicy = {
  secondPersonApproves: boolean
  updatedBy?: string
  updatedAt?: string
  what?: string
}

export type Vendor = {
  statutory?: Statutory
  id: string
  vendorCode: string
  legalName: string
  displayName: string
  constitution: string
  residency: string
  pan?: string
  country: string
  stateCode?: string
  status: string
  inherentTier?: number
  currentBand?: string
  nextDueOn?: string
  ownerUserId?: string
  dataCategories: string[]
  volumeBand?: string
  systemAccess?: string
  regulatedProcess: boolean
  materialOutsourcing: boolean
  crossBorder: boolean
  subcontractorsDeclared: boolean
  msmeCategory?: string
  origin: string
  coveragePct?: number
  attestationPct?: number
  // The band in one line (release 3.3, N2): the step of the current score's
  // explanation that decided the band, in its own words, from the API
  // (packages/core/scoring/bandline.go). Absent for a vendor with no score.
  bandReason?: BandReason

  // Whether the vendor's request counts as approved, so due diligence may
  // begin (release 3.3). On the list and the record both; an API older than
  // the field sends nothing, which is read as approved, because every vendor
  // that API knew was there before the request step existed.
  requestApproved?: boolean
  // Who approved it and how. The record read only.
  requestApproval?: RequestApproval

  // Section 7's master profile, and section 8's remaining tier parameters. The
  // record read carries these and the portfolio list does not, so a Vendor that
  // came out of api.vendors() has every one of them undefined. The one
  // exception is businessOwnerUserId, which the list now also carries, because
  // the portfolio's owner column falls back to it.
  vendorType?: string
  vendorCategory?: string
  businessFunction?: string
  businessOwnerUserId?: string
  procurementOwnerUserId?: string
  tprmOwnerUserId?: string
  relationshipStartDate?: string
  contractStartDate?: string
  contractEndDate?: string
  // The contract dates every screen shows (release 3.3, C13): the filed
  // contract's where one is filed, the record's otherwise, with both sides.
  contract?: VendorContract
  processingLocation?: string
  dataCentreLocation?: string
  serviceDescription?: string
  serviceCriticality?: string
  businessProcess?: string
  dependencyLevel?: string
  criticalBusinessService?: boolean
  productionAccess?: boolean
  customerFacing?: boolean
  continuityDependency?: string
  regulatoryImpact?: string
  // Folded from tierLabel and tierDecision on the record read, so a screen
  // reads one thing rather than three.
  tier?: VendorTier

  // What kinds of data this vendor processes, and how many parties sit behind
  // it. The record read carries these and the portfolio list does not. An
  // absent record means nobody has recorded it, which is a different fact from
  // a vendor that processes nothing.
  concentrationFlag?: boolean
  singlePointFailure?: boolean
  dataProcessing?: DataProcessing
  subprocessorCount?: number

  // How much is still owed on this vendor. The record read carries the standing
  // findings beside the record; only the count is kept here, because the tab
  // that lists them reads the register route with its own filters.
  standingFindings?: number

  // The standing findings themselves, as the record read returns them: open and
  // in progress, capped at fifty, most overdue first. The vendor profile reads
  // the worst severity from them, and claims one only when it is Critical or the
  // page holds every standing finding (items.length equals standing).
  findings?: { standing: number; items: Finding[] }

  // The vendor's Oracle Fusion supplier record, on the record read only, and
  // only for a vendor that has one.
  oracle?: VendorOracle
}

// What the vendor's page shows of its Oracle Fusion supplier record. The site
// counts mean something only once sitesObservedAt is set: before the first
// write or check has read the sites they are zero because nobody has looked.
// The last write's sentence is what Oracle was asked for and what it now
// holds. Spend is Payables' invoiced total over twelve calendar months to
// today; no currency means no invoices, not a missing figure.
export type VendorOracle = {
  supplierNumber: string
  sitesHeld: number
  sitesHeldByOthers: number
  sitesObservedAt?: string
  lastWrite?: { status: string; at: string; sentence: string }
  spend12m?: {
    amount: number
    currency?: string
    invoices: number
    periodStart: string
    periodEnd: string
    observedAt: string
  }
}

export type VendorPage ={ vendors: Vendor[]; matched: number; totalInScope: number }

// Who a stored subject is, as the people read gives it: always the address,
// and the name only where the directory holds one.
// A work phone is kept in People and roles, in E.164, where one is on file.
export type PersonContact = { email: string; name?: string; phone?: string }

// ------------------------------------------ what a vendor processes

// The catalogue GET /v1/data-elements serves: the fifteen data elements, the
// five classifications and the five subject count bands, each with the sentence
// that says why it matters.
//
// The why is the API's own words and is never restated here. The same sentences
// are in packages/core/domain, migration 0028 mirrors the codes as a CHECK, and
// a console carrying its own version of them is a second catalogue that drifts
// from the one the database enforces. Only the label falls back, and only where
// the API sends none.
export type DataElementDef = {
  code: string
  name: string
  why: string
  personal: boolean
  sensitive: boolean
}

// A classification or a subject count band. The order the API sends them in is
// the ranking, least exposed first, so nothing here re-sorts them: a vendor's
// classification is the chosen one furthest down the list.
export type CatalogueValue = { code: string; name?: string; why: string }

export type DataElementCatalogue = {
  dataElements: DataElementDef[]
  classifications: CatalogueValue[]
  subjectCountBands: CatalogueValue[]
  note: string
}

// Title case off the code where the API sent no name, with the band suffixes
// left upper so UNDER_1K reads as Under 1K rather than Under 1k.
export function valueLabel(v: CatalogueValue): string {
  if (v.name) return v.name
  return v.code
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/(\d+)([km])/g, (_all, n: string, unit: string) => n + unit.toUpperCase())
}

// One element on a record. The classification is per element because a vendor
// holding a name and an Aadhaar number is not one level overall.
export type DataElementRecord = {
  elementCode: string
  elementName?: string
  classification: string
  note?: string
}

export type DataProcessing = {
  id: string
  vendorId: string
  // The highest of the elements below, derived by the API and never typed.
  classification: string
  subjectCountBand: string
  processingPurpose?: string
  retentionPeriodMonths?: number | null
  retentionBasis?: string
  storageCountry?: string
  processingCountry?: string
  transferCountries: string[]
  deletionMechanism?: string
  deletionEvidenceDocumentId?: string | null
  recordedBy?: string
  recordedAt: string
  elements: DataElementRecord[]
}

// What a PUT declares. classification is absent on purpose: the API derives it
// from the elements, and a field the console could send would be a field that
// disagrees with them.
export type DataProcessingWrite = {
  // The API refuses a sensitive element classified below Restricted unless this
  // says the caller meant it, and records the acknowledgement on the chain
  // against the person who gave it. It is a judgement rather than a mistake,
  // which is why it is a field and not a validation error.
  acknowledge: boolean
  subjectCountBand: string
  processingPurpose: string
  retentionPeriodMonths: number | null
  retentionBasis: string
  storageCountry: string
  processingCountry: string
  transferCountries: string[]
  deletionMechanism: string
  deletionEvidenceDocumentId: string
  elements: { elementCode: string; classification: string; note: string }[]
  // The record the form was opened on, '' when there was none. A record that
  // changed since (a save elsewhere, or a contract reading accepted) is
  // refused with 409 RECORD_CHANGED rather than saved over.
  basedOnRecordId?: string
}

// The record and whatever sentence the API wants shown beside it. A vendor
// nobody has recorded this for answers with no record rather than a 404, so
// null is the ordinary case and a screen has to tell it apart from a read that
// failed.
export type DataProcessingReply = {
  record: DataProcessing | null
  note: string
  // Only a write carries these. The route reclassifies in the same transaction
  // it writes the record, so what the tier did is part of the answer rather
  // than something a screen has to go and ask for afterwards.
  tierChanged?: boolean
  previousTierLabel?: string
  decision?: VendorTier
}

// One named party behind a vendor. Withdrawn ones come back too: which parties
// stood behind a vendor last March is the first question an incident asks.
export type Subprocessor = {
  id: string
  vendorId: string
  legalName: string
  country?: string
  serviceDescription?: string
  declaredBy: string
  firstSeenOn?: string
  status: string
  sourceDocumentId?: string | null
  recordedBy?: string
  recordedAt: string
}

// legalName is sent on create and ignored on update, because the register is
// keyed on it. See api.updateSubprocessor.
export type SubprocessorWrite = {
  legalName: string
  country: string
  serviceDescription: string
  declaredBy: string
  firstSeenOn: string
  status: string
  sourceDocumentId?: string
}

// GET /v1/vendors/{id}/residency, section 13 as one view. It reads the vendor,
// the data processing record and the sub-processor register together and
// answers the India question once, so nothing here re-derives it: the flag, its
// reasons and the eight questions the requirement lists are the API's answer,
// and the console renders them.
//
// Every field is optional because a build can meet an API that predates the
// route. The panel falls back to the countries on the record when it does.
export type ResidencyReason = { code: string; detail: string }

export type ResidencyFlag = {
  code: string
  label: string
  raised: boolean
  reasons: ResidencyReason[]
}

// One of the eight things section 13 asks for. heldIn says where the answer
// lives, which for four of them is the agreement rather than the product, and
// saying so is the difference between a blank field and a question nobody owns.
export type ResidencyQuestion = {
  key: string
  question: string
  answered: boolean
  answer?: string
  heldIn: string
}

export type ResidencyParty = {
  legalName: string
  country?: string
  outsideIndia: boolean
  status: string
  serviceDescription?: string
}

export type ResidencyView = {
  vendorId?: string
  vendorCode?: string
  vendorCountry?: string
  residency?: string
  locatedInIndia?: boolean
  dataProcessed?: {
    recorded: boolean
    classification: string
    subjectCountBand: string
    elements: DataElementRecord[]
  }
  storageCountry?: string
  processingCountry?: string
  transferCountries?: string[]
  subprocessors?: ResidencyParty[]
  crossBorderFlagOnVendorRecord?: boolean
  flag?: ResidencyFlag
  additionalQuestions?: ResidencyQuestion[]
  note?: string
}

// The record may arrive as itself or wrapped beside a note, and this is the one
// place that has to know which. See the file header: an inconsistency in the
// API is absorbed here rather than in a view.
function dataProcessingReply(raw: unknown): DataProcessingReply {
  if (raw === null || typeof raw !== 'object') return { record: null, note: '' }
  const body = raw as Record<string, unknown>
  const note = str(body.note)
  const tierChanged = body.tierChanged === true
  const previousTierLabel = str(body.previousTierLabel) || undefined
  const decision = vendorTierFrom({}, body.decision)
  // The envelope names the record even when there is none: a vendor nobody has
  // recorded one for answers {"dataProcessing": null} beside the sentence that
  // says so. So the key deciding the shape is whether it is present, not what
  // it holds. Reading a null value as "no envelope" turned the envelope itself
  // into a record with no elements, and the screen then reported an empty
  // record where it should have reported none.
  const enveloped = 'dataProcessing' in body || 'record' in body
  const inner = enveloped
    ? ((body.dataProcessing ?? body.record) as Record<string, unknown> | null | undefined)
    : 'elements' in body || 'id' in body
      ? body
      : null
  if (inner === null || inner === undefined) return { record: null, note, tierChanged, previousTierLabel, decision }
  const record = inner as unknown as DataProcessing
  return {
    record: {
      ...record,
      transferCountries: Array.isArray(record.transferCountries) ? record.transferCountries : [],
      elements: Array.isArray(record.elements) ? record.elements : [],
    },
    note,
    tierChanged,
    previousTierLabel,
    decision,
  }
}

export type Assessment = {
  id: string
  reference: string
  vendorId: string
  vendorName?: string
  questionnaireVersionId: string
  riskModelVersionId?: string
  status: string
  triggerReason: string
  isDelta: boolean
  deltaSectionKeys: string[]
  entitlementStamp: string
  dueOn?: string
  issuedAt?: string
  submittedAt?: string
  respondentName?: string
  respondentEmail?: string
}

export type QuestionOption = { value: string; label?: string; score?: number }

export type RiskMatrixInput = {
  matrixSize: number
  likelihoodLabels: string[]
  impactLabels: string[]
  mediumAt: number
  highAt: number
  criticalAt: number
  appetiteAt: number
  inherentResidual: boolean
}

export type RiskMatrixConfig = RiskMatrixInput & {
  configured: boolean
  locked: boolean
  lockedBy?: string
  lockedRole?: string
  lockedAt?: string
  lockNote?: string
  lockSignature?: string
  signatureValid: boolean
  updatedBy?: string
  updatedAt?: string
}

export type RiskRegRow = {
  id: string
  title: string
  severity: string
  status: string
  source: string
  riskType: string
  owner: string
  dueOn: string
  overdue: boolean
  vendorId: string
  vendorName: string
  vendorCode: string
  tier: number
  likelihood: number
  impact: number
  score: number
  cell: string
}

// One thing this product talks to, as the API found it. State is LIVE, OFF
// (this deployment has not set it up) or ABSENT (nothing is on the other end
// yet). The distinction matters: a person acts on the two differently.
export type Connection = {
  key: string
  name: string
  what: string
  state: 'LIVE' | 'OFF' | 'ABSENT'
  detail: string
  fix?: string
  href?: string
  // What else can be put on the other end. Words rather than a logo, because
  // a mark implies a built integration and a sentence does not.
  accepts?: string
  // Already worded and already in reading order, so the console prints them
  // rather than deciding how to say them.
  counts?: { label: string; value: number; alert?: boolean }[]
}

// ------------------------------------------------- where configuration lives
//
// A customer running FOCTTA's consent product configured their identity
// provider, their mail and their organisation's shape once, and expects this
// product to use it. A customer who bought this product alone configured none
// of it and has to configure all of it here. Both are ordinary, so a domain
// has a source rather than a location.
export type ConfigSource = 'INHERIT' | 'LOCAL' | 'OFF'

// What FOCTTA Core currently supplies for an inherited setting: never a
// credential. live is false when Core could not be asked and this is the last
// value read.
export type InheritedValue = {
  value: Record<string, unknown>
  live: boolean
  seenAt?: string
  changedAt?: string
  why?: string
}

export type ConfigDomain = {
  domain: 'IDENTITY' | 'MAIL' | 'MESSAGING' | 'PEOPLE' | 'LIFECYCLE' | 'CONNECTORS'
  inherited?: InheritedValue
  name: string
  what: string
  source: ConfigSource
  // What is in force as a choice: somebody's decision, or the default. Always
  // present, so a control showing the source never contradicts the reason
  // beside it.
  asked?: ConfigSource
  // True when a person chose, rather than the default applying.
  decided?: boolean
  // False when this domain cannot be inherited here: with TPRM's own
  // accounts, people and their personas are never taken from FOCTTA Core.
  inheritable?: boolean
  why: string
  // True when the source in effect is not the one that was asked for, which
  // is a state nobody chose and is shown differently.
  degraded: boolean
  href?: string
  decidedBy?: string
  decidedAt?: string
  note?: string
  // Who is actually in charge of this right now, which is the effective
  // source and not the choice, and when that was last shown to be true by
  // something that could only happen if it were. confirmedAt is absent when
  // nothing has confirmed it yet, and confirmedHow then says what would.
  inCharge: 'CONSENT' | 'TPRM' | 'NOBODY'
  inChargeName: string
  confirmedAt?: string
  confirmedHow: string
}

export type PlatformLink = {
  baseUrl: string
  remoteTenantId: string
  displayName: string
  status: 'PENDING' | 'LIVE' | 'FAILING' | 'PAUSED'
  tokenRef: string
  lastHandshakeAt?: string
  lastSuccessAt?: string
  lastFailureAt?: string
  lastError?: string
  consecutiveFailures: number
  // Set when Consent made this pairing, on licensing third-party risk. Such a
  // pairing is changed in Consent, not here.
  provisioned: boolean
  launchState?: 'NONE' | 'ENABLED' | 'DISABLED'
  launchChangedAt?: string
  launchReason?: string
  provisionedAt?: string
}

export type ConfigState = {
  origin: 'PLATFORM' | 'STANDALONE' | 'ATTACHED'
  originNote: string
  domains: ConfigDomain[]
  link?: PlatformLink
}

// The licence this deployment runs under, as GET /v1/settings/licence reports
// it for the line on Settings > Platform. EXPIRING is a valid licence in its
// last thirty days. Never the operator's problem text: the state says what
// kind of problem it is, and verify.sh and the log say the rest.
export type LicenceState =
  | 'OFF'
  | 'VALID'
  | 'EXPIRING'
  | 'EXPIRED'
  | 'NOT_YET_VALID'
  | 'MISSING'
  | 'INVALID'
  | 'WRONG_HOST'
  | 'NOT_FOR_TPRM'

export type LicenceStatus = {
  state: LicenceState
  enforced: boolean
  licenceId?: string
  host?: string
  notBefore?: string
  expires?: string
  daysLeft?: number
  // Zero is no seat limit.
  seatsLicensed: number
  // Present only where a valid licence names a seat limit.
  seatsUsed?: number
  coversThisWorkspace: boolean
  coversEveryWorkspace: boolean
  checkedAt: string
}

// A check has three answers. `unproven` means neither passed nor failed:
// nothing was established, and the detail says what would establish it. It is
// its own answer because folding it into either of the other two is how a
// screen ends up claiming mail works when nobody checked.
export type Probe = { check: string; ok: boolean; detail: string; unproven?: boolean }
export type ProbeResult = { checks: Probe[]; allOk: boolean; note: string; status?: string }

// ------------------------------------------------------------- connectors
export type ConnectorField = {
  key: string
  type: string
  label: string
  help?: string
  required?: boolean
  // A secret is collected here and stored as a reference. The value never
  // reaches this product's tables.
  secret?: boolean
  default?: unknown
  enum?: string[]
}

export type ConnectorTemplate = {
  key: string
  displayName: string
  category: string
  what: string
  direction: 'OUTBOUND' | 'INBOUND' | 'BOTH'
  authKind: string
  fields: ConnectorField[]
}

export type ConnectorInstance = {
  id: string
  templateKey: string
  displayName: string
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'FAILING'
  config: Record<string, unknown>
  inboundEnabled: boolean
  hasInboundKey: boolean
  lastSuccessAt?: string
  lastFailureAt?: string
  lastError?: string
  consecutiveFailures: number
  // Set on a card that is a view of a risk register: where it is managed, and
  // how many registers of that kind this tenant has.
  managedAt?: string
  registers?: number
}

// Whether a messaging card can actually send. Not the same as its test
// passing: it is true only once a test message through it was delivered.
export type ConnectorSending = {
  channel?: string
  canSend: boolean
  probeOnly?: boolean
  why: string
  webhookUrl?: string
  lastTest?: { status: string; kind: string; to: string; detail?: string; at: string; stale: boolean }
}

// The wording one vendor message is registered in, with Meta and on DLT.
export type MessageKind = {
  key: string
  label: string
  params: string[]
  whatsAppCategory: string
  whatsAppBody: string
  smsText: string
  whatsAppField: string
  dltField: string
}

export type ConnectorGroup = {
  category: string
  name: string
  what: string
  items: { template: ConnectorTemplate; instance?: ConnectorInstance; sending?: ConnectorSending }[]
}

export type ConnectorsView = {
  groups: ConnectorGroup[]
  source?: ConfigSource
  why?: string
  degraded?: boolean
}

export type ConnectionsView = {
  connections: Connection[]
  live: number
  total: number
  note: string
}

// One month of the risk mix: how many vendors stood in each band at the end of
// it. Rebuilt from score history each time it is read, so the line agrees with
// the register it is drawn from even after somebody rescores an old
// assessment.
export type TrendPoint = {
  asOf: string
  label: string
  bands: Record<string, number>
  scored: number
}

// One outside-in check as it stands now. The verdict and the words are the
// API's, taken against one moment for the whole sweep, so nothing here
// recomputes a judgement the server already made.
export type StoredObservation = {
  checkKey: string
  label: string
  verdict: 'PASS' | 'WARN' | 'FAIL' | 'UNKNOWN'
  value?: string
  detail: string
  domain: string
  observedAt: string
  since: string
}

export type ObservationsView = {
  vendorId: string
  domain: string
  domainSource: string
  // Read from a contact's address, and never observed until somebody confirms
  // it. Empty where the address says nothing about the supplier.
  suggestion: string
  observations: StoredObservation[]
  summary: { failing: number; warning: number; unknown: number; passing: number }
  note: string
  boundary: string
}

// One message to a vendor contact and what the carrier said became of it.
export type VendorMessage = {
  channel: 'EMAIL' | 'SMS' | 'WHATSAPP'
  templateKey: string
  to: string
  provider: string
  status: string
  detail?: string
  createdAt: string
  deliveredAt?: string
  readAt?: string
}

// One message a vendor contact sent back, and where it was filed.
export type InboundMessage = {
  channel: 'WHATSAPP' | 'SMS'
  kind: 'TEXT' | 'DOCUMENT' | 'IMAGE' | 'OTHER'
  text?: string
  fileName?: string
  filedAs: 'CLARIFICATION_REPLY' | 'RESPONSE_EVIDENCE' | 'VENDOR_DOCUMENT' | 'ASSESSMENT_MESSAGE' | 'UNFILED'
  detail?: string
  assessmentId?: string
  receivedAt: string
}

export type ChannelConsent = {
  contactId: string
  channel: 'EMAIL' | 'SMS' | 'WHATSAPP'
  preferred: boolean
  lawfulBasis: string
  optedInAt?: string
  optInSource?: string
  objectedAt?: string
  objectionSource?: string
  objectionNote?: string
}

export type MessageLadder = {
  id: string
  assessmentId?: string
  messageKind: string
  state: 'RUNNING' | 'DELIVERED' | 'RESPONDED' | 'EXHAUSTED' | 'STOPPED'
  outcome?: string
  startedAt: string
  finishedAt?: string
  rungs: { rungNo: number; channel: string; outcome: string; detail?: string; at?: string }[]
}

export type MessagingPolicy = {
  reminderLadder: string[]
  rungWaitMinutes: number
  reminderDaysBeforeDue: number
  unreachableAfterHours: number
  updatedBy?: string
  updatedAt?: string
}

export type RiskCell = {
  likelihood: number
  impact: number
  score: number
  severity: string
  aboveAppetite: boolean
  count: number
  vendors: number
  top: RiskRegRow[]
  // What a register elsewhere put in this cell. Never added into count: a
  // board looking at the top right corner has to know whether the thing
  // sitting there is a vendor this product assesses or an enterprise risk
  // somebody else owns.
  external: number
  topExternal: ExternalRisk[]
}

// Whether procurement may buy from a supplier. The three words are the API's;
// the reasons carry the sentence a requester reads.
export type ClearanceStatus = 'CLEARED' | 'PENDING_ASSESSMENT' | 'BLOCKED'

export type Clearance = {
  vendorId: string
  vendorCode: string
  legalName: string
  vendorStatus?: string
  status: ClearanceStatus
  reasons: { code: string; words: string }[]
  conditions: string[]
  tier?: number | null
  tierLabel?: string | null
  nextReviewOn?: string | null
  version: number
  cause?: string
  decidedAt: string
}

export type ProcurementRequest = {
  id: string
  sourceSystem: string
  externalRequestId: string
  externalSupplierId?: string
  vendorId: string
  matchedBy: 'CREATED' | 'SUPPLIER_LINK' | 'PAN' | 'GSTIN'
  legalName: string
  pan?: string
  gstin?: string
  country: string
  category?: string
  spendBand?: string
  dataAccess?: string
  systemAccess?: string
  requesterEmail?: string
  requesterName?: string
  receivedVia: string
  receiveCount: number
  firstReceivedAt: string
  lastReceivedAt: string
  clearance: Clearance
}

export type SupplierLink = {
  id: string
  vendorId: string
  sourceSystem: string
  externalSupplierId: string
  connectorInstanceId?: string
}

export type Writeback = {
  id: string
  connectorInstanceId: string
  connectorKey: string
  externalSupplierId: string
  clearanceVersion: number
  clearanceStatus: ClearanceStatus
  status: 'PENDING' | 'SENDING' | 'SENT' | 'FAILED' | 'GAVE_UP' | 'SUPERSEDED'
  attempts: number
  nextAttemptAt: string
  lastError?: string
  detail?: string
  sentAt?: string
  createdAt: string
}

export type ProcurementCredential = {
  id: string
  name: string
  kind: 'API_KEY' | 'WEBHOOK_HMAC'
  scopes: string[]
  sourceSystem?: string
  enabled: boolean
  lastUsedAt?: string
  revokedAt?: string
  createdBy: string
  createdAt: string
}

// What issuing a credential hands back, once. Exactly one of apiKey and
// signingSecret is present.
export type IssuedProcurementCredential = {
  id: string
  name: string
  kind: 'API_KEY' | 'WEBHOOK_HMAC'
  scopes: string[]
  apiKey?: string
  keyId?: string
  signingSecret?: string
  how: string
  note: string
}

export type ApprovalTask = {
  id: string
  taskId: string
  kind?: string
  title?: string
  externalSupplierId?: string
  vendorId?: string
  state: 'WAITING' | 'APPROVED' | 'DENIED' | 'UNMATCHED' | 'FAILED'
  detail?: string
  attempts: number
  lastSeenAt: string
  answeredAt?: string
}

export type StatutoryCheck = {
  vendorId: string
  legalName: string
  kind: 'GSTIN' | 'COMPANY'
  identifier: string
  provider: string
  answer: 'PROVEN' | 'REFUSED' | 'UNPROVEN'
  status?: string
  effectiveOn?: string
  detail?: string
  findingId?: string
  checkedAt: string
}

// A register somewhere else that sends us its risks. The secret is never
// returned by any read: it is stored as a hash and shown once, when created.
export type RiskSource = {
  id: string
  kind: 'FOCTTA_CORE' | 'GENERIC_WEBHOOK'
  name: string
  originLabel: string
  enabled: boolean
  createdBy: string
  createdAt: string
  lastSeenAt?: string
  lastError?: string
  // Their words for a level, to the number on this tenant's matrix.
  likelihoodMap: Record<string, number>
  impactMap: Record<string, number>
  live: number
  // Sent, kept, and not on the matrix because the level it used is not in the
  // mapping. Nothing is ever dropped for this reason, so the number is a
  // prompt to correct the mapping rather than a loss.
  unplaced: number
  withdrawn: number
}

// One risk as it arrived. Likelihood and impact are absent where the source's
// level had no mapping; the raw values are always kept, so correcting the
// mapping places it without asking the sender for the register again.
export type ExternalRisk = {
  id: string
  sourceId: string
  sourceName: string
  originLabel: string
  externalId: string
  title: string
  description?: string
  category?: string
  owner?: string
  status?: string
  treatment?: string
  rawLikelihood?: string
  rawImpact?: string
  likelihood?: number
  impact?: number
  subject?: string
  raisedOn?: string
  dueOn?: string
  firstSeenAt: string
  lastSeenAt: string
  withdrawn: boolean
}

export type RiskMatrixView = {
  riskMatrix: RiskMatrixConfig
  cells: RiskCell[]
  register: {
    open: number
    bySeverity: Record<string, number>
    byType: Record<string, number>
    overdue: number
    accepted: number
    aboveAppetite: number
    rows: RiskRegRow[]
  }
  external: {
    total: number
    unplaced: number
    byOrigin: Record<string, number>
    rows: ExternalRisk[]
    note: string
  }
  vendors: { total: number; scored: number; notScored: number; byBand: Record<string, number>; byTier: Record<string, number> }
  how: string
}

export type RiskSink = {
  id: string
  kind: string
  name: string
  url: string
  enabled: boolean
  secretStored: 'SEALED' | 'REFERENCE' | 'PLAIN_TEXT'
  secretRef?: string
  createdBy: string
  createdAt: string
  lastSentAt?: string
  lastError?: string
  pending: number
  failed: number
  sent: number
}

// A risk register, as one record (migration 0074). The same row a catalogue
// card under Integrations opens, and the row the worker sends from. The two
// directions are reported apart because they fail apart: a register can take
// every risk and never send a decision back.
export type RegisterKind = 'FOCTTA_CORE' | 'GENERIC_WEBHOOK' | 'SERVICENOW_IRM'
export type RiskRegister = RiskSink & {
  kind: RegisterKind
  templateKey: string
  config: Record<string, unknown>
  reflectTreatment: boolean
  reflectTreatmentBy?: string
  reflectTreatmentAt?: string
  returnSecretStored: '' | 'SEALED' | 'REFERENCE'
  updatedAt: string
  probedAt?: string
  probeAnswer?: 'ok' | 'refused' | 'unproven'
  probeDetail?: string
  lastReceivedAt?: string
  lastReceiveError?: string
  lastPulledAt?: string
  lastPullError?: string
  returnPath?: string
  wayBack: 'WEBHOOK' | 'POLL' | 'NONE'
}
export type RegisterOwnership = { tprm: string[]; register: string[] }
export type RegisterReceipt = {
  deliveryId: string
  eventType: string
  externalId?: string
  findingId?: string
  outcome: 'APPLIED' | 'RECORDED' | 'UNMATCHED' | 'IGNORED'
  detail: string
  receivedAt: string
}
export type RegisterProbe = {
  id: string
  kind: string
  answer: 'ok' | 'refused' | 'unproven'
  checks: Probe[]
  httpStatus: number
  ok: boolean
  note: string
}
export type RegisterInput = {
  kind?: string
  name?: string
  url?: string
  secret?: string
  returnSecret?: string
  config?: Record<string, unknown>
}

export type ImportQuestionnaireResult = {
  questionnaireId: string
  versionId: string
  versionNo: number
  status: string
  sections: number
  questionCount: number
  questionnaireCreated: boolean
  note: string
}

export type Question = {
  id: string
  questionKey: string
  sectionKey: string
  sectionTitle: string
  ordinal: number
  prompt: string
  helpText: string
  answerType: string
  options: QuestionOption[] | null
  isRequired: boolean
  allowsNa: boolean
  evidenceRequired: boolean
  visibleWhen?: VisibleWhen | null
  citations: string[]
}

export type Provenance =
  | 'VENDOR_ATTESTED'
  | 'INTERNAL_ASSERTED'
  | 'CARRIED_FORWARD'
  | 'DOCUMENT_EXTRACTED'
  | 'UNANSWERED'

export type AnswerRow = {
  questionKey: string
  value?: string
  isNa: boolean
  provenance: Provenance | string
  sourceRef?: string
  originalAnsweredAt?: string
  assertedBy?: string
}

export type ScoreStep = { step: string; detail: string; gateKey?: string; citation?: string }

// The reading for one risk type: the questions in the sections carrying that
// type, scored with the same tier, matrix, floor and ceiling as the assessment.
// The vendor band is the worst of the assessment band and every one of these,
// which the risk_type_rollup step in the assessment's explanation spells out.
export type RiskTypeScore = {
  riskType: string
  controlScore: number
  coveragePct: number
  attestationPct: number
  matrixBand: string
  finalBand: string
  gatesFired: string[]
  explanation: ScoreStep[]
}

// One score shape for the views, whichever endpoint produced it.
export type Score = {
  inherentTier: number
  controlScore: number
  coveragePct: number
  attestationPct: number
  matrixBand: string
  finalBand: string
  gatesFired: string[]
  blockApproval: boolean
  needsException: string[]
  explanation: ScoreStep[]
  inputHash: string
  computedAt?: string
  overrideBand?: string
  overrideReason?: string
  // Empty when no applicable question sat in a typed section.
  riskTypes?: RiskTypeScore[]
}

// ------------------------------------------------------------- risk types

// The catalogue is the API's, not the console's. Eight codes today; the
// console renders whatever count comes back rather than assuming it.
export type RiskTypeCatalogueEntry = { code: string; name: string; why: string }

// Why a type has no band for this vendor, in the order the gaps are closed:
// no section of the type exists in the questionnaire, no assessment has been
// issued on a questionnaire that has one, the assessment is not yet scored, or
// it was scored and too little of the typed section came back to claim a band.
export type RiskTypeGap = 'NO_SECTION' | 'NO_ASSESSMENT' | 'NOT_SCORED' | 'BELOW_FLOOR'

export type RiskTypeCoveredBy = {
  questionnaireCode: string
  questionnaireVersionId: string
  sectionKeys: string[]
  questionCount: number
}

export type VendorRiskTypeRow = {
  riskType: string
  name: string
  covered: boolean
  coveredBy: RiskTypeCoveredBy | null
  score: RiskTypeScore | null
  gap: RiskTypeGap | string | null
}

export type VendorRiskTypes = {
  vendor: { id: string; vendorCode: string; name: string; currentBand?: string; attestationPct?: number }
  riskTypes: VendorRiskTypeRow[]
  covered: number
  total: number
  note: string
}

export type AssessmentDetail = {
  assessment: Assessment
  questions: Question[]
  responses: AnswerRow[]
  score?: Score
  // The vendor's answers the last time it finished a questionnaire, before
  // this one was sent; absent for a first assessment (release 3.3).
  lastTime?: LastTime
  // What this round raised on each question: a rule the current score fired
  // on the answer, or an open finding the answer raised.
  questionFlags: QuestionFlag[]
  // What this round's latest score raised or refreshed.
  findings: Finding[]
}

// The earlier round a review compares with (repo/answers_last_time.go).
// keys: every question both rounds asked, answered or not, so a question new
// in this version is never read as a changed answer.
export type QuestionFlag = {
  questionKey: string
  kind: 'GATE' | 'ANSWER' | string
  // The gate's key, or the finding's id.
  key: string
  title?: string
  severity?: string
}

export type LastTime = {
  assessmentId: string
  reference: string
  finishedOn?: string
  keys: string[]
  answers: { questionKey: string; value?: string; isNa: boolean }[]
}

// The compiled filter is repo.VendorFilter, which carries no json tags, so Go
// serialises it under the field names exactly as they are declared.
export type CompiledFilter = {
  Tiers: number[] | null
  Bands: string[] | null
  Statuses: string[] | null
  DataCategories: string[] | null
  DueBefore: string | null
  OverdueOnly: boolean
  // Unset, true or false, as the API's filter holds it.
  CrossBorder?: boolean | null
  Search: string
  Owner: string
  Limit: number
  Offset: number
}

export type IntentCompiled = {
  compiled: true
  expression: string
  filter: CompiledFilter
  vendors: Vendor[]
  matched: number
  totalInScope: number
}

export type IntentRefused = { compiled: false; message: string; examples: string[] }

export type IntentResult = IntentCompiled | IntentRefused

// One bar on a chart: the stored value, the words, and the count. The key is
// what the register is filtered by when somebody clicks it, so it travels with
// the label rather than being reconstructed from it.
export type Tally = { key: string; label: string; count: number }

// ------------------------------------------------------- management reports

// Section 18's eight, as repo.Report serialises them. A report is a declared
// column list and rows of text, one shape for all eight, so this file holds one
// type rather than eight and the Reports screen renders whatever it is handed.
// The kind is what a column is set by rather than what it is: everything is
// text on the wire, because the CSV and the JSON have to be the same table.
export type ReportColumn = { key: string; label: string; kind: 'TEXT' | 'NUMBER' | 'DATE' }

export type ReportFilterDoc = { name: string; label: string; meaning: string; values?: string[] }

export type ReportDoc = {
  key: string
  title: string
  question: string
  basis: string
  columns: ReportColumn[]
  filters: ReportFilterDoc[]
}

export type ManagementReport = ReportDoc & {
  rows: string[][]
  // What the filter matched, and what this answer did not carry. Both are
  // printed: a report that stopped at its limit without saying so describes a
  // smaller estate than the one that exists.
  total: number
  omitted: number
  limit: number
  asOf: string
  computedAt: string
}

export type ReportCatalogue = { reports: ReportDoc[]; formats: string[]; note: string }

// What a report can be narrowed by. Which of these a given report reads is on
// its own catalogue entry, and the API refuses one it does not read rather than
// ignoring it, so the screen offers only what the report declares.
export type ReportQuery = {
  vendorId?: string
  status?: string[]
  band?: string[]
  tier?: string[]
  severity?: string[]
  kind?: string[]
  limit?: number
}

function reportQueryString(f: ReportQuery, format?: string): string {
  const p = new URLSearchParams()
  if (f.vendorId) p.set('vendorId', f.vendorId)
  for (const [name, values] of [
    ['status', f.status],
    ['band', f.band],
    ['tier', f.tier],
    ['severity', f.severity],
    ['kind', f.kind],
  ] as [string, string[] | undefined][]) {
    if (values && values.length) p.set(name, values.join(','))
  }
  if (f.limit) p.set('limit', String(f.limit))
  if (format) p.set('format', format)
  const q = p.toString()
  return q ? '?' + q : ''
}

export type Dashboard = {
  totalVendors: number
  active: number
  draft: number
  pendingApproval: number
  suspended: number
  offboarding: number
  underAssessment: number
  highRisk: number
  criticalRisk: number
  notScored: number
  personalData: number
  sensitiveData: number
  crossBorder: number
  reassessmentOverdue: number
  assessmentsOverdue: number
  certificationsExpired: number
  certificationsExpiring: number
  contractsExpiring: number
  findingsOpen: number
  findingsCritical: number
  findingsOverdue: number
  incidentsOpen: number
  bands: Tally[]
  tiers: Tally[]
  statuses: Tally[]
  assessmentStates: Tally[]
  businessFunctions: Tally[]
  countries: Tally[]
  // Section 6's last three charts.
  dataClassifications: Tally[]
  certificationStates: Tally[]
  riskTrend: TrendPoint[]
  computedAt: string
  queries: number
  basis: string
}

export type DashboardAnswer = { dashboard: Dashboard; links: Record<string, string> }

// One act, as it was recorded. The payload is whatever was written at the time
// and is deliberately unshaped: an audit trail whose payload this year's code
// reformats is a trail of this year's opinion.
// An in-app notice.
//
// Addressed by the capability a reader needs rather than to a person: this
// product has no user table, and the two identifiers that look like user ids,
// the token's subject and the free text in ownerUserId, do not agree with each
// other. See migration 0040. The consequence here is that the list is already
// filtered to what this reader can act on, so the console does no filtering of
// its own and cannot get it wrong.
export type Notice = {
  id: string
  kind: string
  headline: string
  detail?: string
  entityType?: string
  entityId?: string
  needsCapability?: string
  createdAt: string
  read: boolean
}

export type NoticeInbox = { notices: Notice[]; unread: number }

// What a value was and what it became. Present in the payload under "changes"
// for any act that moved a stored value, absent for one that created or read
// something. It is inside the payload, and therefore inside the hash, so a
// change list cannot be edited without breaking the chain.
export type AuditChange = { field: string; from: string; to: string }

export type AuditEvent = {
  sequence: number
  eventId: string
  actorId: string
  actorType: string
  action: string
  entityType: string
  entityId: string
  correlationId: string
  occurredAt: string
  payload: unknown
  eventHash: string
  previousHash: string
}

// The change list, read out of the payload rather than off a field of its own,
// because the payload is what the chain hashes. Anything malformed is treated
// as absent: a trail that renders half a change is worse than one that says
// nothing and leaves the payload there to be read.
export function changesOf(e: AuditEvent): AuditChange[] {
  const raw = (e.payload as { changes?: unknown } | null)?.changes
  if (!Array.isArray(raw)) return []
  const out: AuditChange[] = []
  for (const c of raw) {
    if (c && typeof c === 'object' && typeof (c as AuditChange).field === 'string') {
      const { field, from, to } = c as AuditChange
      out.push({ field, from: typeof from === 'string' ? from : '', to: typeof to === 'string' ? to : '' })
    }
  }
  return out
}

export type AuditPage = {
  events: AuditEvent[]
  matched: number
  total: number
  // The sequence to send as `before` for the next page. Absent at the end of
  // the trail, so the reader knows it has reached the beginning rather than
  // inferring it from a short page.
  nextBefore?: number
  limit: number
  basis: string
  note: string
}

export type AuditQuery = {
  entityType?: string
  entityId?: string
  action?: string[]
  actor?: string
  since?: string
  until?: string
  before?: number
  limit?: number
}

function auditQueryString(q: AuditQuery): string {
  const p = new URLSearchParams()
  if (q.entityType) p.set('entityType', q.entityType)
  if (q.entityId) p.set('entityId', q.entityId)
  if (q.action?.length) p.set('action', q.action.join(','))
  if (q.actor) p.set('actor', q.actor)
  if (q.since) p.set('since', q.since)
  if (q.until) p.set('until', q.until)
  if (q.before) p.set('before', String(q.before))
  if (q.limit) p.set('limit', String(q.limit))
  const s = p.toString()
  return s ? '?' + s : ''
}

export type CoveragePack = { pack: string; clausesCited: number; clauses: string[] }
export type CoverageReport = { packs: CoveragePack[]; note: string }

export type ChainVerdict = {
  tenantId?: string
  events: number
  ok: boolean
  breakAt?: number
  detail?: string
  headHash?: string
}

export type ImportProfile = {
  id: string
  sourceSystem: string
  version: number
  entity: string
  requiredFields?: string[]
  notes?: string
  isActive: boolean
}

export type ImportBatch = {
  id: string
  sourceSystem: string
  entity: string
  fileName: string
  status: string
  rowsTotal: number
  rowsReady: number
  rowsBlocked: number
  rowsDuplicate: number
  rowsCommitted: number
  unmappedColumns?: string[]
  errorDetail?: string
  startedBy?: string
  createdAt?: string
  committedAt?: string
  // Release 3.2: the undo window and what the import did besides adding.
  undoUntil?: string
  undoneAt?: string
  canUndo?: boolean
  rowsUpdated?: number
  rowsHeld?: number
  documents?: number
  // true for a batch made by the universal import. Undo, restore and filing
  // contracts in bulk work only for these (409 NOT_UNIVERSAL otherwise).
  universal?: boolean
  // Where the batch came from, in neutral words. Shown in place of the source
  // system, which is a key and can name the tool the customer is leaving.
  sourceLabel?: string
  // A commit that stopped part way and can be carried on (or undone).
  canResume?: boolean
}

export type ImportRow = {
  id: string
  sourceRow: number
  status: string
  raw?: Record<string, string>
  // Mapped values are not all strings: booleans and string arrays land here
  // once the profile has been applied.
  mapped?: Record<string, unknown>
  issues?: Issue[]
  matchedVendorId?: string
  createdEntityId?: string
}

// Staging answers with its own shape, not with an ImportBatch: the batch does
// not exist as a list row until the next GET, and the id it reports is
// importId. Typing this as ImportBatch is what made the staged file fail to
// select after upload.
export type StageResult = {
  importId: string
  sourceSystem: string
  entity: string
  fileName: string
  status: string
  profileId?: string
  profileVersion?: number
  profileShipped?: boolean
  profileNotes?: string
  rowsTotal: number
  rowsReady: number
  rowsBlocked: number
  rowsDuplicate: number
  unmappedColumns?: string[]
  priorImportId?: string
  note?: string
}

// What a commit did. The universal import (release 3.2) adds the counts the
// result page states: vendors updated, held, still needing attention, and what
// came in with them (starting tiers asked for, contacts, certificates noted,
// extra detail), and the date the import can be undone until. The earlier
// staging list reads only committed, leftAlone and note, which stay.
export type CommitResult = {
  importId: string
  entity?: string
  status: string
  committed: number
  failed: number
  leftAlone: number
  updated?: number
  held?: number
  attention?: number
  startingTiers?: number
  contacts?: number
  claims?: number
  extraFields?: number
  ownersUnmatched?: number
  keptForApproval?: number
  undoUntil?: string
  created?: { sourceRow: number; vendorId: string; legalName: string }[]
  failures?: { sourceRow: number; detail: string }[]
  note?: string
}

// A commit too large to write inside the request answers 202 with this, and
// the rows are written in the background; the console follows them on the
// count report (GET /report) until it carries the result. The same answer comes
// back when a commit that stopped part way is carried on.
export type CommitAccepted = {
  importId: string
  status: string
  remaining: number
  done: number
  resumed?: boolean
  note?: string
}

// Whether a commit answer is the 202 (still being written) rather than the
// result. A finished commit always carries its counts; one still running says
// how many rows remain.
export function commitAccepted(r: CommitResult | CommitAccepted): r is CommitAccepted {
  return r.status === 'COMMITTING' || ('remaining' in r && !('committed' in r))
}

// ------------------------------------------------ universal vendor import
//
// The five-step wizard (Choose the file, Match the columns, Match the values,
// Check and fix, Bring them in). Every string meant for a screen arrives in
// plain words: a field's label, an option's label, a layout's label. The keys
// and codes beside them are what the console sends back, and are never shown.

export type ImportReading = {
  format: string
  encoding?: string
  delimiter?: string
  sheets?: string[]
  sheet?: string
  headerRow: number
  headerFound: boolean
  rows: number
  columns: number
  notes?: string[]
}

// A layout the API recognised. key is never shown; label may be.
export type ImportLayout = {
  key: string
  label: string
  applied: boolean
  checked: boolean
  score: number
}

// MATCHED (sure), CHECK (to review), NONE (not matched).
export type ImportColumnStatus = 'MATCHED' | 'CHECK' | 'NONE'

// How sure the importer is of its suggestion (release 3.3, I1): MATCHED (an
// exact header, a saved mapping or a recognised layout), REVIEW (a synonym or
// what the values look like) or UNMATCHED (nothing credible, so nothing is
// suggested).
export type ImportColumnTier = 'MATCHED' | 'REVIEW' | 'UNMATCHED'

export type ImportColumn = {
  index: number
  column: string
  samples?: string[]
  filled: number
  // The suggested field key, or "" when nothing fits.
  field: string
  confidence: number
  status: ImportColumnStatus | string
  // Release 3.3 (I1). An API from before it sends neither; status says the same.
  tier?: ImportColumnTier | string
  // Where the suggestion came from: saved, layout, header, synonym, content.
  source?: string
  why?: string
  personal?: boolean
  // For a column with no field: EXTRA keeps it as extra detail on each vendor,
  // LEAVE leaves it out.
  keep?: 'EXTRA' | 'LEAVE' | ''
}

// The saved mapping a file arrived matched by (its headers were seen before).
export type ImportMappingUse = {
  id: string
  name: string
  timesUsed: number
  lastUsedAt: string
  duplicates?: string
  fill?: Record<string, string>
}

export type ImportOption = { label: string; code: string }

export type ImportQuestion = {
  key: string
  text: string
  why?: string
  answer: string
  // false: the file already settles it. Not shown, but its answer is sent back.
  asked: boolean
  options: ImportOption[]
}

export type ImportField = {
  key: string
  label: string
  group: string
  kind: string
  required: boolean
  help?: string
  // false: one column at most may be matched to it.
  many: boolean
  options?: ImportOption[]
}

export type ImportAnalysis = {
  importId: string
  fileName: string
  reading: ImportReading
  layout?: ImportLayout | null
  columns: ImportColumn[]
  missing?: string[]
  questions?: ImportQuestion[]
  fields: ImportField[]
  banner?: string
  savedMapping?: ImportMappingUse | null
}

// Per file column: a field key, "" (keep as extra detail) or "-" (leave out).
export type ImportColumnChoices = Record<string, string>

// The import's draft on the server (release 3.3, I1; plan 4.8): what the
// wizard keeps between steps, so a refresh, another tab or another day lands
// on the same step with the same answers. Fixes and holds are not in it: the
// review step writes them on the rows as they are made.
export type ImportDraftStep = 'upload' | 'columns' | 'values' | 'review' | 'confirm'

export type ImportDraftState = {
  columns: ImportColumnChoices
  // Columns whose suggestion the person confirmed, and columns the person
  // chose by hand (a later suggestion never overwrites those).
  confirmed: string[]
  byPerson: string[]
  answers: Record<string, string>
  values: Record<string, Record<string, string>>
  duplicates: ImportDuplicates | ''
  fill: Record<string, string>
  remember: { on: boolean; name: string } | null
  review: { filter: string; page: number } | null
}

// A write names the version it read; an older one is refused (409
// DRAFT_STALE) rather than undoing what another tab saved.
export type ImportDraftWrite = { version: number; step: ImportDraftStep; state: ImportDraftState }

export type ImportServerDraft = {
  importId: string
  fileName: string
  owner: string
  ownerName: string
  yours: boolean
  step: ImportDraftStep | string
  stepNumber: number
  steps: number
  rows: number
  version: number
  updatedAt: string
  keptUntil: string
  status: string
  updatedBy: string
  canEdit: boolean
  canDiscard: boolean
  state: ImportDraftState
  savedMapping: ImportMappingUse | null
  banner?: string
}

// A saved mapping, as save-mapping answers it.
export type ImportSavedMapping = {
  id: string
  name: string
  columns: number
  matched: number
  values: number
  timesUsed: number
  version: number
}

export type ImportValue = {
  value: string
  count: number
  code: string
  label: string
  suggested: boolean
  confidence: number
  why?: string
}

export type ImportValueSet = {
  column: string
  field: string
  label: string
  multi: boolean
  options: ImportOption[]
  values: ImportValue[]
  distinct: number
  other: number
}

export type ImportCounts = {
  total: number
  add: number
  update: number
  leftAlone: number
  attention: number
  held: number
}

// FILL_EMPTY: fill in what is empty (the default). SKIP: leave them alone.
// UPDATE: update from the file.
export type ImportDuplicates = 'FILL_EMPTY' | 'SKIP' | 'UPDATE'

export type ImportMappingBody = {
  columns: ImportColumnChoices
  // Per field key: the file's value to the option code chosen for it.
  values: Record<string, Record<string, string>>
  answers: Record<string, string>
  duplicates: ImportDuplicates
  fill?: Record<string, string>
}

export type ImportMappingResult = { counts: ImportCounts; note?: string }

export type ImportPreviewFilter = 'all' | 'attention' | 'existing' | 'held' | 'add'

export type ImportPreviewCell = {
  field: string
  label: string
  value: string
  note?: string
  changed?: boolean
}

export type ImportProblem = {
  field: string
  label: string
  // The file column to edit. "" means the fix is a choice for the field
  // itself, sent under the key "@" + field.
  column: string
  message: string
  input: 'text' | 'choice' | string
  options?: ImportOption[]
}

// ADD, UPDATE, LEAVE, FIX, HELD or SAME (a repeat of another row in the file).
export type ImportPreviewRow = {
  id: string
  sourceRow: number
  outcome: string
  outcomeLabel: string
  matched?: { vendorId: string; name: string; code?: string; how: string } | null
  cells: ImportPreviewCell[]
  problems: ImportProblem[]
  raw: Record<string, string>
  fixes: Record<string, string>
}

export type ImportPreview = {
  counts: ImportCounts
  total: number
  offset: number
  limit: number
  rows: ImportPreviewRow[]
}

export type ImportRowChange = { fixes: Record<string, string> } | { hold: boolean }

// Find and replace within one column of the file (release 3.3, I1; plan 4.5).
// The text is matched literally, never as a pattern. A preview counts and
// shows what would change and writes nothing; without it every change is a fix
// beside the file, as if typed on each row.
export type ImportFindReplace = {
  column: string
  find: string
  replace: string
  wholeCell: boolean
  matchCase: boolean
  preview: boolean
}

export type ImportFindReplaceResult = {
  changed: number
  preview: boolean
  examples: { rowId: string; sourceRow: number; before: string; after: string }[]
  counts: ImportCounts | null
  note: string
}

// An import in progress, as Import history lists it (release 3.3, I1 and
// I5): a file, a person and a step, nothing from the rows.
export type ImportDraftSummary = {
  importId: string
  fileName: string
  owner: string
  ownerName: string
  yours: boolean
  step: ImportDraftStep | string
  stepNumber: number
  steps: number
  rows: number
  version: number
  updatedAt: string
  keptUntil: string
}

export type ImportDraftDiscarded = { importId: string; discardedAt: string; discardedBy: string; note: string }

// A saved mapping as the Saved mappings list shows it. timesUsed counts the
// imports it was applied to; one used by an import is never deleted (the API
// answers 409 MAPPING_IN_USE with the count).
export type ImportMappingListed = {
  id: string
  name: string
  source: string
  headers: string[]
  columns: number
  matched: number
  values: number
  duplicates: ImportDuplicates | string
  version: number
  timesUsed: number
  lastUsedAt: string
  createdBy: string
  createdAt: string
  updatedBy: string
  updatedAt: string
}

export type ImportReport = {
  importId: string
  fileName: string
  rowsRead: number
  created: number
  updated: number
  skipped: number
  held: number
  rejected: number
  rejectedReasons?: { reason: string; rows: number }[]
  documentsMatched?: number
  documentsUnmatched?: number
  undoUntil?: string
  undoneAt?: string
  canUndo?: boolean
  // While a commit runs in the background, and after one stopped part way:
  // the batch's status (a code, never shown), how far it got, whether nobody is
  // writing it any more (interrupted), and whether it can be carried on. result
  // is the commit's own answer, the same as a 200 from commit, once it is done.
  status?: string
  progress?: { done: number; total: number }
  interrupted?: boolean
  canResume?: boolean
  result?: CommitResult
}

export type ImportDocVendor = { id: string; name: string; code?: string }

export type ImportDocMatchFile = {
  name: string
  size: number
  ok: boolean
  problem?: string
  vendor?: ImportDocVendor | null
  how?: string
  docType?: string
  candidates?: ImportDocVendor[]
}

export type ImportDocMatch = {
  files: ImportDocMatchFile[]
  docTypes: ImportOption[]
  limits?: { maxFiles: number; maxFileMB: number; maxTotalMB: number; types: string }
}

export type ImportDocAssignment = { name: string; vendorId: string; docType: string; expiresOn?: string }

export type ImportDocAttachResult = {
  attached: number
  skipped: number
  files: { name: string; vendorId?: string; documentId?: string; problem?: string }[]
  note?: string
}

export type ImportUndoResult = {
  retired: number
  kept?: { name: string; reason: string }[]
  fieldsRestored?: number
  fieldsKept?: number
  documentsDetached?: number
  undoneAt?: string
}

export type ImportRestoreResult = {
  restored: number
  notRestored?: { name: string; reason: string }[]
}

// ----------------------------------------------------------- normalisation

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s)
  } catch {
    return null
  }
}

function stepsFrom(raw: unknown): ScoreStep[] {
  if (!Array.isArray(raw)) return []
  return raw.map((s) => {
    const o = (s ?? {}) as Record<string, unknown>
    return {
      step: String(o.step ?? ''),
      detail: String(o.detail ?? ''),
      gateKey: (o.gate_key ?? o.gateKey) as string | undefined,
      citation: o.citation as string | undefined,
    }
  })
}

function num(...candidates: unknown[]): number {
  for (const c of candidates) {
    if (typeof c === 'number' && Number.isFinite(c)) return c
  }
  return 0
}

function str(...candidates: unknown[]): string {
  for (const c of candidates) {
    if (typeof c === 'string' && c !== '') return c
  }
  return ''
}

// A per-type reading arrives three ways: snake_case from the engine inside a
// fresh scoring result, camelCase from the stored child row on an assessment
// read, and camelCase without its own riskType key on the vendor's risk-type
// view, where the code sits on the enclosing row. All three land here.
function typeScoreFrom(raw: unknown, riskType = ''): RiskTypeScore | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  return {
    riskType: str(o.riskType, o.risk_type, riskType),
    controlScore: num(o.controlScore, o.control_score),
    coveragePct: num(o.coveragePct, o.coverage_pct),
    attestationPct: num(o.attestationPct, o.attestation_pct),
    matrixBand: str(o.matrixBand, o.matrix_band),
    finalBand: str(o.finalBand, o.final_band),
    gatesFired: (o.gatesFired ?? o.gates_fired ?? []) as string[],
    explanation: stepsFrom(typeof o.explanation === 'string' ? safeParse(o.explanation) : o.explanation),
  }
}

function typeScoresFrom(raw: unknown): RiskTypeScore[] {
  if (!Array.isArray(raw)) return []
  return raw.map((t) => typeScoreFrom(t)).filter((t): t is RiskTypeScore => t !== null)
}

export function normalizeScore(raw: unknown): Score | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const o = raw as Record<string, unknown>
  return {
    inherentTier: num(o.inherentTier, o.inherent_tier),
    controlScore: num(o.controlScore, o.control_score),
    coveragePct: num(o.coveragePct, o.coverage_pct),
    attestationPct: num(o.attestationPct, o.attestation_pct),
    matrixBand: str(o.matrixBand, o.matrix_band),
    finalBand: str(o.finalBand, o.final_band),
    gatesFired: (o.gatesFired ?? o.gates_fired ?? []) as string[],
    blockApproval: Boolean(o.blockApproval ?? o.block_approval),
    needsException: (o.needsException ?? o.needs_exception ?? []) as string[],
    // repo.Score carries explanation as raw jsonb, so it can arrive as a string.
    explanation: stepsFrom(typeof o.explanation === 'string' ? safeParse(o.explanation) : o.explanation),
    inputHash: str(o.inputHash, o.input_hash),
    computedAt: o.computedAt as string | undefined,
    overrideBand: o.overrideBand as string | undefined,
    overrideReason: o.overrideReason as string | undefined,
    riskTypes: typeScoresFrom(o.riskTypes ?? o.risk_types),
  }
}

function vendorRiskTypesFrom(raw: Record<string, unknown>): VendorRiskTypes {
  const rows = Array.isArray(raw.riskTypes) ? (raw.riskTypes as Record<string, unknown>[]) : []
  const riskTypes = rows.map((r) => {
    const code = str(r.riskType, r.risk_type)
    return {
      riskType: code,
      name: str(r.name, code),
      covered: Boolean(r.covered),
      coveredBy: (r.coveredBy ?? null) as RiskTypeCoveredBy | null,
      score: typeScoreFrom(r.score, code),
      gap: (r.gap ?? null) as RiskTypeGap | string | null,
    }
  })
  return {
    vendor: (raw.vendor ?? {}) as VendorRiskTypes['vendor'],
    riskTypes,
    covered: num(raw.covered, riskTypes.filter((r) => r.covered).length),
    total: num(raw.total, riskTypes.length),
    note: str(raw.note),
  }
}

export function normalizeFinding(raw: unknown, allowedNext?: unknown): Finding {
  const o = (raw ?? {}) as Record<string, unknown>
  const dueOn = str(o.dueOn)
  return {
    id: str(o.id),
    vendorId: str(o.vendorId),
    vendorName: str(o.vendorName) || undefined,
    vendorCode: str(o.vendorCode) || undefined,
    source: str(o.source) || 'MANUAL',
    gateKey: str(o.gateKey) || undefined,
    scoreId: str(o.scoreId) || undefined,
    assessmentId: str(o.assessmentId) || undefined,
    riskType: str(o.riskType) || undefined,
    riskTypeName: str(o.riskTypeName) || undefined,
    title: str(o.title),
    detail: str(o.detail) || undefined,
    citation: str(o.citation) || undefined,
    severity: str(o.severity, o.derivedSeverity),
    derivedSeverity: str(o.derivedSeverity, o.severity),
    severityReason: str(o.severityReason) || undefined,
    ownerUserId: str(o.ownerUserId),
    dueOn,
    // The API computes the ageing so the column and the overdue filter share
    // one arithmetic. The browser clock is the fallback and keeps the same
    // sign: positive once the date has gone.
    ageingDays: typeof o.ageingDays === 'number' ? o.ageingDays : -(daysUntil(dueOn) ?? 0),
    status: str(o.status) || 'OPEN',
    resolutionNote: str(o.resolutionNote) || undefined,
    resolutionDocumentId: str(o.resolutionDocumentId) || undefined,
    resolvedBy: str(o.resolvedBy) || undefined,
    resolvedAt: str(o.resolvedAt) || undefined,
    lastSeenScoreId: str(o.lastSeenScoreId) || undefined,
    // Absent reads as still reproducing. The other way round would put "no
    // longer reproducing" on every row an older build served, and that is the
    // one sentence on this screen that must not be said without evidence.
    stillReproducing: o.stillReproducing !== false,
    raisedBy: str(o.raisedBy),
    raisedAt: str(o.raisedAt),
    updatedAt: str(o.updatedAt),
    allowedNext: Array.isArray(allowedNext) ? allowedNext.map(String) : undefined,
  }
}

function findingPageFrom(raw: Record<string, unknown>): FindingPage {
  const rows = Array.isArray(raw.findings) ? raw.findings : []
  return {
    findings: rows.map((r) => normalizeFinding(r)),
    matched: num(raw.matched, rows.length),
    total: num(raw.total, rows.length),
  }
}

// The three write routes answer with the same envelope, and this is the one
// place that knows its shape.
function findingReplyFrom(raw: unknown): FindingReply {
  const o = (raw ?? {}) as Record<string, unknown>
  return { finding: normalizeFinding(o.finding ?? o, o.allowedNext), note: str(o.note) }
}

// The tier reaches this file across three keys: inherentTier carries the stored
// ordinal, tierLabel carries the name to print, and tierDecision carries the
// rule that reached it, or null where nothing recorded one. Per the note at the
// top of this file the three are folded here and nowhere else, so a screen
// reads one object and never has to know which route answered it.
function vendorTierFrom(record: Record<string, unknown>, decision: unknown): VendorTier | undefined {
  const d = decision && typeof decision === 'object' ? (decision as Record<string, unknown>) : null
  const ordinal = num(d?.tier, record.inherentTier, record.tier)
  if (ordinal === 0) return undefined
  return {
    tier: ordinal,
    label: str(d?.tierLabel, record.tierLabel, tierLabel(ordinal)),
    // Arithmetic rather than a second table, for the reason domain.Tier gives:
    // a table can be edited into disagreement with itself and 5 - ordinal
    // cannot.
    displayRank: num(d?.displayRank, 5 - ordinal),
    recorded: d !== null,
    ruleKey: str(d?.ruleKey) || undefined,
    why: str(d?.why) || undefined,
    decidedAt: str(d?.decidedAt) || undefined,
    decidedBy: str(d?.decidedBy) || undefined,
    riskModelVersionId: str(d?.riskModelVersionId) || undefined,
    source: str(d?.source) || undefined,
    overrideId: str(d?.overrideId) || undefined,
  }
}

function tierOverrideFrom(raw: unknown): TierOverride | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  return raw as TierOverride
}

function tierHistoryFrom(raw: unknown): VendorTier[] {
  if (!Array.isArray(raw)) return []
  return raw.map((d) => vendorTierFrom({}, d)).filter((t): t is VendorTier => t !== undefined)
}

function parseOptions(raw: unknown): QuestionOption[] | null {
  const v = typeof raw === 'string' ? safeParse(raw) : raw
  return Array.isArray(v) ? (v as QuestionOption[]) : null
}

// ------------------------------------------------------------------ people

// The tenant's own people, as the identity provider holds them.
//
// This is not a cache of a users table, because there is no users table. Every
// call below reads or writes the provider that issued the caller's token: two
// stores of the same fact drift, and the provider is where a leaver is actually
// removed. What changed is that the product can now ask the provider to do
// something rather than only reading the token it was handed.
//
// No tenant appears in any request here. The API takes it from the caller's own
// token, which is why an invitation carries an email address and a set of roles
// and nothing else: there is nowhere for this console to name a tenant, even by
// mistake, and nowhere for anybody editing it later to add one.
//
// No password appears here either, in a request, a response or an error. The
// provider mints the temporary credential and delivers it to the person; this
// product asks for an account and is told a subject back.

export type DirectoryStatus = 'invited' | 'confirmed' | 'disabled' | 'unknown'

// One account. There is deliberately no tenant on the row: every person the API
// will answer with is in the caller's own tenant, so the field would carry the
// same value on every row and invite a screen to start trusting it.
export type Person = {
  // What a token carries in sub, and what every evidence row names. The API
  // addresses a person by this and never by the email, because an address
  // changes and a subject does not.
  subject: string
  email: string
  roles: string[]
  enabled: boolean
  status: DirectoryStatus
  createdAt: string
  updatedAt: string
  // The persona the account works as, and whether an administrator chose it
  // (personaSet) or it is derived from roles. More than one persona held is
  // an account to split before production. Optional for an older API.
  persona?: string
  personaSet?: boolean
  personas?: string[]
  splitBeforeProduction?: boolean
  // The work phone kept for this person, in E.164. Absent when none is on file.
  phone?: string
  // TPRM's own accounts (AUTH_MODE=accounts): the name given, how the account
  // may sign in, whether its single sign-on identity is bound yet, and why it
  // was switched off. Absent on a deployment whose provider holds the people.
  name?: string
  passwordAllowed?: boolean
  passwordSet?: boolean
  ssoBound?: boolean
  disabledReason?: string
  // When the persona given stops granting anything, and whether it already
  // has (release 3.1: the external auditor always has one). Absent for none.
  personaExpiresAt?: string
  personaExpired?: boolean
  // Whether a second factor is in force for the account's TPRM password.
  secondFactor?: boolean
}

const DIRECTORY_STATUSES = ['invited', 'confirmed', 'disabled', 'unknown']

// The write routes answer with the person they changed, and this console reads
// it bare or under a user key. The API slice and this screen were written at
// the same time; a client that refused the answer over the name of an envelope
// would be a worse failure than one that reads both.
function personFrom(raw: unknown): Person {
  const outer = (raw ?? {}) as Record<string, unknown>
  const o = (outer.user ?? outer) as Record<string, unknown>
  const status = str(o.status).toLowerCase()
  return {
    subject: str(o.subject),
    email: str(o.email),
    roles: Array.isArray(o.roles) ? o.roles.map(String) : [],
    // A provider state this build has no name for is carried through as
    // unknown rather than flattened into confirmed. Somebody archived or
    // compromised painted green is a worse answer than somebody the screen
    // admits it cannot read.
    status: DIRECTORY_STATUSES.includes(status) ? (status as DirectoryStatus) : 'unknown',
    enabled: typeof o.enabled === 'boolean' ? o.enabled : status !== 'disabled',
    createdAt: str(o.createdAt),
    updatedAt: str(o.updatedAt),
    persona: typeof o.persona === 'string' && o.persona !== '' ? o.persona : undefined,
    personaSet: o.personaSet === true,
    personas: Array.isArray(o.personas) ? o.personas.map(String) : undefined,
    splitBeforeProduction: o.splitBeforeProduction === true,
    phone: typeof o.phone === 'string' && o.phone !== '' ? o.phone : undefined,
    name: typeof o.name === 'string' && o.name !== '' ? o.name : undefined,
    passwordAllowed: o.passwordAllowed === true,
    passwordSet: o.passwordSet === true,
    ssoBound: o.ssoBound === true,
    disabledReason: typeof o.disabledReason === 'string' && o.disabledReason !== '' ? o.disabledReason : undefined,
    personaExpiresAt: typeof o.personaExpiresAt === 'string' && o.personaExpiresAt !== '' ? o.personaExpiresAt : undefined,
    personaExpired: o.personaExpired === true,
    secondFactor: o.secondFactor === true,
  }
}

// Where a roster came from, alongside the roster. A screen that cannot tell an
// inherited list from its own will offer to change one it does not own.
export type Roster = {
  people: Person[]
  managedElsewhere: boolean
  why: string
}

function peopleFrom(raw: unknown): Person[] {
  const rows = Array.isArray(raw) ? raw : ((raw ?? {}) as Record<string, unknown>).users
  return Array.isArray(rows) ? rows.map(personFrom) : []
}

// ------------------------------------------------------------------- calls

export type VendorQuery = {
  tier?: string[]
  band?: string[]
  status?: string[]
  dataCategory?: string[]
  overdue?: boolean
  crossBorder?: boolean
  q?: string
  owner?: string
}

// The reverse of vendorQueryString. The overview links every card it can to
// the register filtered by the same thing it counted, and a card that lands on
// an unfiltered list is worse than one that does not link at all: the reader
// believes the list is the answer.
export function vendorQueryFromHash(hash: string): VendorQuery {
  const at = hash.indexOf('?')
  if (at < 0) return {}
  const p = new URLSearchParams(hash.slice(at + 1))
  const list = (k: string): string[] | undefined => {
    const raw = p.get(k)
    if (!raw) return undefined
    const parts = raw
      .split(',')
      .map((v) => v.trim().toUpperCase())
      .filter(Boolean)
    return parts.length > 0 ? parts : undefined
  }
  const f: VendorQuery = {}
  const tier = list('tier')
  if (tier) f.tier = tier
  const band = list('band')
  if (band) f.band = band
  const status = list('status')
  if (status) f.status = status
  const data = list('data') ?? list('dataCategory')
  if (data) f.dataCategory = data
  if (p.get('overdue') === 'true') f.overdue = true
  if (p.get('crossBorder') === 'true') f.crossBorder = true
  const q = p.get('q')
  if (q) f.q = q
  return f
}

export function vendorQueryString(f: VendorQuery): string {
  const p = new URLSearchParams()
  if (f.tier?.length) p.set('tier', f.tier.join(','))
  if (f.band?.length) p.set('band', f.band.join(','))
  if (f.status?.length) p.set('status', f.status.join(','))
  if (f.dataCategory?.length) p.set('dataCategory', f.dataCategory.join(','))
  if (f.overdue) p.set('overdue', 'true')
  if (f.crossBorder) p.set('crossBorder', 'true')
  if (f.q?.trim()) p.set('q', f.q.trim())
  if (f.owner?.trim()) p.set('owner', f.owner.trim())
  const s = p.toString()
  return s ? '?' + s : ''
}

// The header's links to the other FOCTTA products (release 3.1, persona spec
// 7.4). Each is null when it is not for this persona or not configured, so the
// header never shows a link that goes nowhere.
export type SuiteLink = { name: string; url: string }
export type SuiteLinksForMe = { consent: SuiteLink | null; discovery: SuiteLink | null }
export type DiscoveryMode = 'OFF' | 'THROUGH_CONSENT' | 'DIRECT'
export type SuiteLinkSettings = {
  consentShown: boolean
  consentUrl: string
  pairedConsentUrl: string
  discoveryMode: DiscoveryMode
  discoveryUrl: string
  updatedBy: string
  updatedAt: string | null
  consentLink: string
  discoveryLink: string
  shownTo: string[]
}

export const api = {
  me: () => request<Me>('/v1/me'),

  suiteLinks: () => request<SuiteLinksForMe>('/v1/suite-links'),
  suiteLinkSettings: () => request<SuiteLinkSettings>('/v1/settings/suite-links'),
  saveSuiteLinkSettings: (body: { consentShown: boolean; consentUrl: string; discoveryMode: DiscoveryMode; discoveryUrl: string }) =>
    request<SuiteLinkSettings>('/v1/settings/suite-links', { method: 'PUT', body: JSON.stringify(body) }),

  roles: () => request<RoleCatalogue>('/v1/roles'),

  // Personas: the catalogue, the home for one of the caller's personas, and the
  // tenant's mapping from its own directory's words to personas.
  personas: () => request<PersonaCatalogue>('/v1/personas'),
  // Subject to address for the people in this tenant, for display only.
  // people is the same read with the name beside the address where the
  // directory holds one; an older API answers without it.
  peopleNames: () => request<{ names: Record<string, string>; people?: Record<string, PersonContact> }>('/v1/people/names'),
  // The Home control panel. Needs you carries the first five rows of My work;
  // View all opens My work, which pages the whole list.
  homePanel: (period: HomePeriodKey) => request<HomePanel>('/v1/home/panel?period=' + period),
  // Everything waiting for the person signed in (release 3.2), one filter and
  // one page at a time; vendor narrows it to one vendor, for the vendor page.
  myWork: (o: { show?: MyWorkShow; limit?: number; after?: string; vendor?: string } = {}) => {
    const q = new URLSearchParams()
    if (o.show) q.set('show', o.show)
    if (o.limit) q.set('limit', String(o.limit))
    if (o.after) q.set('after', o.after)
    if (o.vendor) q.set('vendor', o.vendor)
    const qs = q.toString()
    return request<MyWork>('/v1/my-work' + (qs ? '?' + qs : ''))
  },
  // The counts alone, for the sidebar and the bell: no rows travel.
  myWorkCount: () => request<MyWorkCount>('/v1/my-work?count=only'),
  // now is the server's clock, so the screen marks expired mappings by it.
  personaMappings: () =>
    request<{ mappings: PersonaMapping[]; personas: Persona[]; now?: string }>('/v1/settings/identity/persona-mappings'),
  // expiresAt (YYYY-MM-DD) is when the mapping stops naming its persona:
  // required for the external auditor (30 days when left out, at most 180).
  addPersonaMapping: (claimValue: string, persona: string, note: string, expiresAt = '') =>
    request<unknown>('/v1/settings/identity/persona-mappings', {
      method: 'POST',
      body: JSON.stringify(expiresAt ? { claimValue, persona, note, expiresAt } : { claimValue, persona, note }),
    }),
  removePersonaMapping: (id: string) =>
    request<unknown>('/v1/settings/identity/persona-mappings/' + encodeURIComponent(id) + '/remove', { method: 'POST' }),
  previewPersonaMapping: (values: string[]) =>
    request<PersonaMappingPreview>('/v1/settings/identity/persona-mappings/preview', {
      method: 'POST',
      body: JSON.stringify({ values }),
    }),

  // The five things this product does to a directory, and no sixth. There is
  // no delete: an account is switched off and keeps everything it did, because
  // every row in the evidence chain names a subject that has to stay
  // resolvable years later.
  // The roster AND where it comes from. A tenant whose people are managed on
  // the platform is shown the platform's list and offered none of the four
  // actions beside it: pressing one would create an account here and none
  // where their joiners and leavers actually run.
  users: async (): Promise<Roster> => {
    const raw = (await request<unknown>('/v1/users')) as Record<string, unknown>
    return {
      people: peopleFrom(raw),
      managedElsewhere: raw?.managedElsewhere === true,
      why: typeof raw?.why === 'string' ? raw.why : '',
    }
  },

  // One persona, and the role follows from it on the server. The persona's role
  // travels beside it only for an API from before personas were chosen, which
  // reads roles and ignores the persona; a current API does the opposite.
  inviteUser: async (
    email: string,
    persona: string,
    role: string,
    // TPRM's own accounts only: the person's name, and whether they may also
    // sign in with a TPRM password (otherwise single sign-on only). expiresAt
    // (YYYY-MM-DD) is when the persona stops granting anything: required for
    // the external auditor, 30 days when left out, at most 180 (release 3.1).
    extra?: { name?: string; passwordSignIn?: boolean; expiresAt?: string },
  ): Promise<Person> =>
    personFrom(
      await request<unknown>('/v1/users', {
        method: 'POST',
        body: JSON.stringify({ email, persona, roles: [role], ...(extra ?? {}) }),
      }),
    ),

  // Whether a TPRM account may also sign in with a TPRM password. Off forgets
  // any password it had.
  setPasswordSignIn: async (subject: string, allowed: boolean): Promise<Person> =>
    personFrom(
      await request<unknown>('/v1/users/' + encodeURIComponent(subject) + '/password-sign-in', {
        method: 'PUT',
        body: JSON.stringify({ allowed }),
      }),
    ),

  // Saving a persona again replaces its expiry, which is how an engagement is
  // extended; an empty expiresAt is none (or 30 days for the external auditor).
  setUserPersona: async (subject: string, persona: string, expiresAt = ''): Promise<Person> =>
    personFrom(
      await request<unknown>('/v1/users/' + encodeURIComponent(subject) + '/persona', {
        method: 'PUT',
        body: JSON.stringify(expiresAt ? { persona, expiresAt } : { persona }),
      }),
    ),

  // A set and not an add. Taking a role away is the half of the job that
  // matters, and a route that only ever added them would leave every leaver
  // holding what they left with.
  setUserRoles: async (subject: string, roles: string[]): Promise<Person> =>
    personFrom(
      await request<unknown>('/v1/users/' + encodeURIComponent(subject) + '/roles', {
        method: 'PUT',
        body: JSON.stringify({ roles }),
      }),
    ),

  // A work phone, or an empty one to take it off. The server keeps it in
  // E.164 and answers with what it kept.
  setUserPhone: (subject: string, phone: string) =>
    request<{ subject: string; phone: string; changed: boolean; note: string }>(
      '/v1/users/' + encodeURIComponent(subject) + '/phone',
      { method: 'PUT', body: JSON.stringify({ phone }) },
    ),

  disableUser: async (subject: string): Promise<Person> =>
    personFrom(
      await request<unknown>('/v1/users/' + encodeURIComponent(subject) + '/disable', { method: 'POST' }),
    ),

  enableUser: async (subject: string): Promise<Person> =>
    personFrom(
      await request<unknown>('/v1/users/' + encodeURIComponent(subject) + '/enable', { method: 'POST' }),
    ),

  vendors: (f: VendorQuery) => request<VendorPage>('/v1/vendors' + vendorQueryString(f)),

  async vendor(id: string): Promise<Vendor> {
    const raw = await request<Record<string, unknown>>('/v1/vendors/' + encodeURIComponent(id))
    const standing = (raw.findings ?? {}) as Record<string, unknown>
    return {
      ...(raw as unknown as Vendor),
      tier: vendorTierFrom(raw, raw.tierDecision),
      standingFindings: typeof standing.standing === 'number' ? standing.standing : undefined,
    }
  },

  // Only the fields being changed, because the entitlement to make the change
  // is decided per field: sending the untouched risk profile back alongside a
  // corrected address is how an address correction becomes a 403.
  updateVendor: (id: string, changes: Record<string, unknown>) =>
    request<{ id: string; changed: string[] }>('/v1/vendors/' + encodeURIComponent(id), {
      method: 'PUT',
      body: JSON.stringify(changes),
    }),

  // Re-runs the tier rules over the profile as it stands. An edit already
  // reclassifies, so this is for the case where the rules moved and the vendor
  // did not: a promoted risk model version leaves every tier decided by the old
  // one until something asks again.
  async classifyVendor(id: string): Promise<ClassifyResult> {
    const raw = await request<Record<string, unknown>>(
      '/v1/vendors/' + encodeURIComponent(id) + '/classify',
      { method: 'POST' },
    )
    return {
      changed: Boolean(raw.changed),
      previousTier: typeof raw.previousTier === 'number' ? raw.previousTier : undefined,
      previousTierLabel: str(raw.previousTierLabel) || undefined,
      decision: vendorTierFrom({}, raw.decision),
      setByHand: raw.setByHand === true,
      rules: vendorTierFrom({}, raw.rules),
    }
  },

  // Why this vendor is on the tier it is on, and every time that answer moved.
  async vendorTier(id: string): Promise<VendorTierView> {
    const raw = await request<Record<string, unknown>>('/v1/vendors/' + encodeURIComponent(id) + '/tier')
    return {
      current: vendorTierFrom(raw, raw.current),
      history: tierHistoryFrom(raw.history),
      note: str(raw.note),
      override: tierOverrideFrom(raw.override),
      pending: tierOverrideFrom(raw.pending),
      rules: vendorTierFrom({}, raw.rules),
      overrides: Array.isArray(raw.overrides) ? (raw.overrides as TierOverride[]) : [],
    }
  },

  // A tier set by hand: asked for by one person, decided by another who holds
  // risk.approve. Ending one returns the vendor to what the rules say.
  requestTierOverride: (vendorId: string, body: { tier: number; reason: string; expiresOn?: string }) =>
    request<{ override: TierOverride }>('/v1/vendors/' + encodeURIComponent(vendorId) + '/tier-override', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  decideTierOverride: (vendorId: string, overrideId: string, verb: 'approve' | 'reject' | 'withdraw', note?: string) =>
    request<{ override: TierOverride }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/tier-override/' + encodeURIComponent(overrideId) + '/' + verb,
      { method: 'POST', body: JSON.stringify(note ? { note } : {}) },
    ),

  endTierOverride: (vendorId: string, overrideId: string, note?: string) =>
    request<{ override: TierOverride }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/tier-override/' + encodeURIComponent(overrideId),
      { method: 'DELETE', body: JSON.stringify(note ? { note } : {}) },
    ),

  tiers: () => request<{ tiers: TierRow[] }>('/v1/tiers').then((r) => r.tiers ?? []),

  // The catalogue behind the data tab. Read per screen rather than cached: it
  // is small, and a stale copy of the sentence that explains why Aadhaar is
  // different is worse than a second request.
  dataElementCatalogue: () => request<DataElementCatalogue>('/v1/data-elements'),

  dataProcessing: (vendorId: string) =>
    request<unknown>('/v1/vendors/' + encodeURIComponent(vendorId) + '/data-processing').then(dataProcessingReply),

  putDataProcessing: (vendorId: string, body: DataProcessingWrite) =>
    request<unknown>('/v1/vendors/' + encodeURIComponent(vendorId) + '/data-processing', {
      method: 'PUT',
      body: JSON.stringify(body),
    }).then(dataProcessingReply),

  // active and outsideIndia are the API's counts, taken rather than recomputed:
  // it decides what counts as outside India for the residency view and the tier
  // rule, and a stat strip disagreeing with the panel beside it is worse than
  // no stat strip.
  subprocessors: (vendorId: string) =>
    request<{
      subprocessors: Subprocessor[]
      count?: number
      active?: number
      outsideIndia?: number
      note?: string
    }>('/v1/vendors/' + encodeURIComponent(vendorId) + '/subprocessors'),

  // alsoBehindVendorIds comes back when this party already stands behind other
  // vendors in the tenant. It is the concentration answer, given at the moment
  // the party is named rather than left for somebody to go looking for.
  createSubprocessor: (vendorId: string, body: SubprocessorWrite) =>
    request<{ id: string; legalName?: string; alsoBehindVendorIds?: string[]; note?: string }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/subprocessors',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // The legal name is not one of the fields this route takes: the register is
  // keyed on it, so a rename is a withdrawal and a fresh record.
  updateSubprocessor: (vendorId: string, id: string, body: SubprocessorWrite) =>
    request<{ id: string; legalName?: string; updated?: boolean; note?: string }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/subprocessors/' + encodeURIComponent(id),
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  // Withdrawn, never deleted, for the reason nothing in this schema deletes.
  // The reason is optional to the API and lands on the audit chain.
  withdrawSubprocessor: (vendorId: string, id: string, reason: string) =>
    request<{ id: string; status?: string; note?: string }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/subprocessors/' + encodeURIComponent(id) + '/deactivate',
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  residency: (vendorId: string) =>
    request<ResidencyView>('/v1/vendors/' + encodeURIComponent(vendorId) + '/residency'),

  validateVendor: (body: Record<string, unknown>) =>
    request<{ valid: boolean; issues: Issue[] }>('/v1/vendors/validate', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  assessments: (vendorId?: string) =>
    request<{ assessments: Assessment[] }>(
      '/v1/assessments' + (vendorId ? '?vendorId=' + encodeURIComponent(vendorId) : ''),
    ),

  async assessment(id: string): Promise<AssessmentDetail> {
    const raw = await request<Record<string, unknown>>('/v1/assessments/' + encodeURIComponent(id))
    const questions = ((raw.questions ?? []) as Record<string, unknown>[]).map((q) => ({
      ...(q as unknown as Question),
      options: parseOptions(q.options),
    }))
    const score = normalizeScore(raw.score)
    // The per-type rows are children of the score, and the read may carry
    // them inside it or beside it. Either way they belong to the score here.
    if (score && (score.riskTypes?.length ?? 0) === 0 && Array.isArray(raw.riskTypes)) {
      score.riskTypes = typeScoresFrom(raw.riskTypes)
    }
    return {
      assessment: raw.assessment as Assessment,
      questions,
      responses: (raw.responses ?? []) as AnswerRow[],
      score,
      lastTime: (raw.lastTime ?? undefined) as LastTime | undefined,
      questionFlags: (raw.questionFlags ?? []) as QuestionFlag[],
      findings: (raw.findings ?? []) as Finding[],
    }
  },

  async score(id: string): Promise<Score> {
    const raw = await request<unknown>('/v1/assessments/' + encodeURIComponent(id) + '/score', { method: 'POST' })
    const s = normalizeScore(raw)
    if (!s) throw new ApiError(500, 'BAD_SCORE', 'The scoring endpoint returned nothing to display.')
    return s
  },

  compileIntent: (text: string) =>
    request<IntentResult>('/v1/intent/compile', { method: 'POST', body: JSON.stringify({ text }) }),

  dashboard: () => request<DashboardAnswer>('/v1/dashboard'),

  audit: (q: AuditQuery = {}) => request<AuditPage>('/v1/audit' + auditQueryString(q)),

  auditActions: () => request<{ actions: Tally[]; note: string }>('/v1/audit/actions'),

  coverage: () => request<CoverageReport>('/v1/coverage'),

  reports: () => request<ReportCatalogue>('/v1/reports'),

  report: (key: string, f: ReportQuery = {}) =>
    request<{ report: ManagementReport }>('/v1/reports/' + encodeURIComponent(key) + reportQueryString(f)),

  reportCsv: (key: string, f: ReportQuery = {}) =>
    requestFile('/v1/reports/' + encodeURIComponent(key) + reportQueryString(f, 'csv')),

  verifyChain: () => request<ChainVerdict>('/v1/evidence/verify'),

  imports: () => request<{ imports: ImportBatch[] }>('/v1/imports'),

  importProfiles: () => request<{ profiles: ImportProfile[] }>('/v1/import-profiles'),

  importRows: (id: string, status?: string) =>
    request<{ rows: ImportRow[] }>(
      '/v1/imports/' + encodeURIComponent(id) + '/rows' + (status ? '?status=' + encodeURIComponent(status) : ''),
    ),

  startImport: (file: File, sourceSystem: string, entity: string) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('sourceSystem', sourceSystem)
    fd.append('entity', entity)
    return request<StageResult>('/v1/imports', { method: 'POST', body: fd })
  },

  // The blank file to fill in, served by the API off the same import profile
  // the importer reads with. Returned as a Blob so the caller can hand it to
  // the browser as a download under the name the server chose.
  // sourceSystem picks the import profile the template is built from, so a
  // file filled in from it is read by the same profile it was drawn off.
  // Without one the API builds the spreadsheet template.
  importTemplate: async (format: 'xlsx' | 'csv', sourceSystem?: string): Promise<{ blob: Blob; fileName: string }> => {
    const source = sourceSystem ? '&sourceSystem=' + encodeURIComponent(sourceSystem) : ''
    const res = await fetch('/v1/imports/template?entity=VENDOR&format=' + format + source, {
      headers: { Authorization: 'Bearer ' + (await bearerToken()), Accept: '*/*' },
    })
    if (!res.ok) {
      let body: unknown = { message: 'The template could not be prepared (' + res.status + ').' }
      try {
        body = await res.json()
      } catch {
        // A non-JSON failure body says nothing more than the status did.
      }
      const err = apiErrorFrom(res, body, '/v1/imports/template')
      reportActionFailure('GET', '/v1/imports/template', err)
      throw err
    }
    const dispo = res.headers.get('Content-Disposition') ?? ''
    const m = /filename="([^"]+)"/.exec(dispo)
    return { blob: await res.blob(), fileName: m?.[1] ?? 'foctta-vendor-import-template.' + format }
  },
  // 200 with the result, or (universal import, a large batch, or one carried on
  // after it stopped part way) 202 with CommitAccepted; commitAccepted() tells
  // them apart. 409 COMMIT_RUNNING while another commit of it is running.
  commitImport: (id: string) =>
    request<CommitResult | CommitAccepted>('/v1/imports/' + encodeURIComponent(id) + '/commit', { method: 'POST' }),

  // The universal import (release 3.2). Reading a file creates an import; the
  // same file posted again with another sheet or header row creates a new one,
  // and the newest id is the one to carry on with.
  analyseImport: (file: File, opts: { sheet?: string; headerRow?: number } = {}) => {
    const fd = new FormData()
    fd.append('file', file)
    if (opts.sheet) fd.append('sheet', opts.sheet)
    if (opts.headerRow && opts.headerRow > 0) fd.append('headerRow', String(opts.headerRow))
    return request<ImportAnalysis>('/v1/imports/analyse', { method: 'POST', body: fd })
  },

  // What the upload of an import answered, kept by the server (release 3.3,
  // I1), so an import opened by its address in a new tab shows its columns.
  importAnalysisKept: (id: string) => request<ImportAnalysis>('/v1/imports/' + encodeURIComponent(id) + '/analysis'),

  importDraft: (id: string) => request<ImportServerDraft>('/v1/imports/' + encodeURIComponent(id) + '/draft'),

  putImportDraft: (id: string, body: ImportDraftWrite) =>
    request<ImportServerDraft>('/v1/imports/' + encodeURIComponent(id) + '/draft', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // "Remember this mapping as": saves the matching this import applied under
  // a name, so the next file with the same columns arrives matched.
  saveImportMapping: (id: string, name: string) =>
    request<ImportSavedMapping>('/v1/imports/' + encodeURIComponent(id) + '/save-mapping', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  importValues: (id: string, body: { columns: ImportColumnChoices; answers: Record<string, string> }) =>
    request<{ valueSets: ImportValueSet[] }>('/v1/imports/' + encodeURIComponent(id) + '/values', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  applyImportMapping: (id: string, body: ImportMappingBody) =>
    request<ImportMappingResult>('/v1/imports/' + encodeURIComponent(id) + '/mapping', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  importPreview: (id: string, q: { filter?: ImportPreviewFilter; offset?: number; limit?: number } = {}) => {
    const p = new URLSearchParams()
    p.set('filter', q.filter ?? 'all')
    p.set('offset', String(q.offset ?? 0))
    p.set('limit', String(q.limit ?? 20))
    return request<ImportPreview>('/v1/imports/' + encodeURIComponent(id) + '/preview?' + p.toString())
  },

  fixImportRow: (id: string, rowId: string, body: ImportRowChange) =>
    request<{ row: ImportPreviewRow; counts: ImportCounts }>(
      '/v1/imports/' + encodeURIComponent(id) + '/rows/' + encodeURIComponent(rowId),
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  fixImportColumn: (id: string, body: { column: string; from: string; to: string }) =>
    request<{ changed: number; counts: ImportCounts }>('/v1/imports/' + encodeURIComponent(id) + '/fix-all', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  findReplaceImport: (id: string, body: ImportFindReplace) =>
    request<ImportFindReplaceResult>('/v1/imports/' + encodeURIComponent(id) + '/find-replace', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Imports in progress (Import history's In progress, release 3.3 I5), and
  // discarding one: the owner or a holder of vendor.import_admin.
  importDrafts: () => request<{ drafts: ImportDraftSummary[]; keptForDays: number }>('/v1/imports/drafts'),

  discardImportDraft: (id: string) =>
    request<ImportDraftDiscarded>('/v1/imports/' + encodeURIComponent(id) + '/draft', { method: 'DELETE' }),

  // Saved mappings (Import history's Saved mappings, release 3.3 I5).
  importMappings: () => request<{ mappings: ImportMappingListed[] }>('/v1/import-mappings'),

  renameImportMapping: (id: string, name: string) =>
    request<ImportMappingListed>('/v1/import-mappings/' + encodeURIComponent(id), {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  duplicateImportMapping: (id: string) =>
    request<ImportMappingListed>('/v1/import-mappings/' + encodeURIComponent(id) + '/duplicate', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  deleteImportMapping: (id: string) =>
    request<{ deleted: boolean; id: string }>('/v1/import-mappings/' + encodeURIComponent(id), { method: 'DELETE' }),

  exportImportMapping: (id: string) => requestFile('/v1/import-mappings/' + encodeURIComponent(id) + '/export'),

  importReport: (id: string) => request<ImportReport>('/v1/imports/' + encodeURIComponent(id) + '/report'),

  importReportCsv: (id: string) => requestFile('/v1/imports/' + encodeURIComponent(id) + '/report.csv'),

  // Nothing is stored by a match: it says which vendor each file belongs to.
  matchImportDocuments: (id: string, files: File[], mappingSheet?: File | null) => {
    const fd = new FormData()
    for (const f of files) fd.append('files', f)
    if (mappingSheet) fd.append('mappingSheet', mappingSheet)
    return request<ImportDocMatch>('/v1/imports/' + encodeURIComponent(id) + '/documents/match', {
      method: 'POST',
      body: fd,
    })
  },

  // The same files again, with the vendor and type chosen for each. A file
  // with no assignment is skipped.
  attachImportDocuments: (id: string, files: File[], assignments: ImportDocAssignment[]) => {
    const fd = new FormData()
    for (const f of files) fd.append('files', f)
    fd.append('assignments', JSON.stringify(assignments))
    return request<ImportDocAttachResult>('/v1/imports/' + encodeURIComponent(id) + '/documents', {
      method: 'POST',
      body: fd,
    })
  },

  undoImport: (id: string, reason: string) =>
    request<ImportUndoResult>('/v1/imports/' + encodeURIComponent(id) + '/undo', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  restoreImport: (id: string) =>
    request<ImportRestoreResult>('/v1/imports/' + encodeURIComponent(id) + '/restore', { method: 'POST' }),

  createVendor: (body: Record<string, unknown>) =>
    request<VendorCreateResult>('/v1/vendors', { method: 'POST', body: JSON.stringify(body) }),
  // The tier a vendor with these answers would get, before it exists: the
  // tenant's own rules, run by the API, nothing written (release 3.3, M6).
  tierPreview: (body: TierPreviewInput) =>
    request<TierPreview>('/v1/vendors/tier-preview', { method: 'POST', body: JSON.stringify(body) }),
  // A questionnaire for one vendor, raised from its own record. Issues at
  // once unless the deployment asks for a second person, in which case the
  // answer is a drafted campaign and status DRAFT.
  resendInvitation: (assessmentId: string) =>
    request<{ assessmentId: string; reference: string; to: string; attempt: number; mail: MailState | string; mailNote?: string; note?: string; reach?: Reach }>(
      '/v1/assessments/' + encodeURIComponent(assessmentId) + '/resend-invitation',
      { method: 'POST' },
    ),
  inviteVendor: (vendorId: string, body: InviteRequest = {}) =>
    request<InviteResult>('/v1/vendors/' + encodeURIComponent(vendorId) + '/invite', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // The request step (release 3.3): a TPRM lead approves a vendor request so
  // due diligence may begin (reason optional), or rejects it with a reason,
  // which ends at Rejected.
  approveVendorRequest: (vendorId: string, reason = '') =>
    request<{ id: string; requestApproved: boolean; next?: string }>('/v1/vendors/' + encodeURIComponent(vendorId) + '/approve-request', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  rejectVendorRequest: (vendorId: string, reason: string) =>
    request<{ id: string; status: string }>('/v1/vendors/' + encodeURIComponent(vendorId) + '/reject-request', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  vendorRequestPolicy: () => request<VendorRequestPolicy>('/v1/settings/vendor-requests'),
  setVendorRequestPolicy: (secondPersonApproves: boolean) =>
    request<VendorRequestPolicy>('/v1/settings/vendor-requests', {
      method: 'PUT',
      body: JSON.stringify({ secondPersonApproves }),
    }),

  reassessVendor: (vendorId: string, body: ReassessRequest) =>
    request<InviteResult>('/v1/vendors/' + encodeURIComponent(vendorId) + '/reassess', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  reassessmentRoster: () => request<ReassessmentRoster>('/v1/reassessment/vendors'),

  campaigns: () => request<{ campaigns: Campaign[]; mail?: MailState | string }>('/v1/campaigns'),

  campaign: (id: string) => request<CampaignDetail>('/v1/campaigns/' + encodeURIComponent(id)),

  createCampaign: (body: CampaignCreate) =>
    request<{ id: string; status: string; recipients: number; skipped: { vendorId?: string; vendorID?: string; reason: string }[] }>('/v1/campaigns', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  approveCampaign: (id: string) =>
    request<ApproveResult>('/v1/campaigns/' + encodeURIComponent(id) + '/approve', { method: 'POST' }),

  cancelCampaign: (id: string, reason: string) =>
    request<{ cancelled: boolean }>('/v1/campaigns/' + encodeURIComponent(id) + '/cancel', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // The register, or one vendor's rows of it: the vendor page reads its own to
  // see whether a risk acceptance or a contract waiver is already waiting.
  exceptions: (vendorId?: string) =>
    request<{ exceptions: RiskException[] }>(
      '/v1/exceptions' + (vendorId ? '?vendorId=' + encodeURIComponent(vendorId) : ''),
    ),

  // The reply names the expiry it recorded and, where the exception is against
  // a finding, what approving it will do to that finding. Both are shown as the
  // API said them. For a risk acceptance or a contract waiver the note says who
  // approves it.
  createException: (body: ExceptionCreate) =>
    request<{ id: string; status?: string; expiresOn?: string; findingId?: string; note?: string; kind?: string }>(
      '/v1/exceptions',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // What is left of section 14's chain on this acceptance, and who has yet to
  // sign. Read before the button is drawn, because which capability signs next
  // depends on the band and tier and the console must not guess it.
  exceptionChain: (id: string) =>
    request<ExceptionChain>('/v1/exceptions/' + encodeURIComponent(id) + '/chain'),

  // Approving is signing. The route kept its name because "I agree" is still
  // what it means; what changed is that agreeing signs one stage, and only the
  // last stage grants the acceptance. stageIndex is sent so a console that read
  // the chain a minute ago does not sign whatever moved into the space while it
  // was being read.
  approveException: (id: string, stageIndex: number, note = '') =>
    request<ExceptionSignResult>('/v1/exceptions/' + encodeURIComponent(id) + '/approve', {
      method: 'POST',
      body: JSON.stringify({ stageIndex, note }),
    }),

  // The third outcome section 14 asks for. Not a rejection: a rejection says no
  // to carrying the risk, this says the risk could be carried once the named
  // work is done, and the note is what is being asked for.
  remediateException: (id: string, note: string) =>
    request<ExceptionDecision>('/v1/exceptions/' + encodeURIComponent(id) + '/remediate', {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  // note, not reason. The route has always decoded note, so a rejection's
  // words went nowhere and the chain recorded a refusal nobody explained. The
  // API now accepts both names, and this sends the one it documents.
  rejectException: (id: string, reason: string) =>
    request<ExceptionDecision>('/v1/exceptions/' + encodeURIComponent(id) + '/reject', {
      method: 'POST',
      body: JSON.stringify({ note: reason }),
    }),

  // The evidence register: every document the tenant holds, arranged by expiry
  // rather than by vendor. vendor.read, not reports.read, because every row
  // names a supplier and a contract title.
  documentRegister: (
    f: { kind?: string[]; status?: string[]; vendorId?: string; limit?: number; offset?: number; legalReview?: string } = {},
  ) => {
    const q = new URLSearchParams()
    if (f.kind?.length) q.set('kind', f.kind.join(','))
    // awaiting: the agreements nobody has recorded a legal review of.
    if (f.legalReview) q.set('legalReview', f.legalReview)
    if (f.status?.length) q.set('status', f.status.join(','))
    if (f.vendorId) q.set('vendorId', f.vendorId)
    if (f.limit) q.set('limit', String(f.limit))
    if (f.offset) q.set('offset', String(f.offset))
    const s = q.toString()
    return request<DocumentRegister>('/v1/documents' + (s ? '?' + s : ''))
  },

  // The kinds and statuses the schema actually enforces, so the filter cannot
  // offer a value the register would refuse.
  documentVocabulary: () => request<DocumentVocabulary>('/v1/documents/kinds'),

  // One filed document's bytes, as a download. The API serves it as an
  // attachment under the document's own title and refuses, as JSON, one whose
  // bytes were never kept or no longer match their digest.
  documentFile: (id: string) => requestFile('/v1/documents/' + encodeURIComponent(id) + '/content'),

  // The portfolio as an Excel workbook, for the same filters the screen sends
  // to the list, so the file holds the rows on screen.
  portfolioExport: (f: VendorQuery & { dueBefore?: string }) => {
    const q = new URLSearchParams(vendorQueryString(f).replace(/^\?/, ''))
    if (f.dueBefore) q.set('dueBefore', f.dueBefore)
    q.set('format', 'xlsx')
    return requestFile('/v1/portfolio/export?' + q.toString())
  },

  // A management report as an Excel workbook, beside reportCsv. Same rows,
  // same filters, with what the report left out after the table.
  reportXlsx: (key: string, f: ReportQuery = {}) =>
    requestFile('/v1/reports/' + encodeURIComponent(key) + reportQueryString(f, 'xlsx')),

  // What suppliers have sent that nobody has looked at, across the tenant.
  awaitingDocuments: () =>
    request<{ documents: VendorDocument[]; total: number }>('/v1/documents/awaiting'),

  // Accepting is what makes a supplier's document count. Declining needs a
  // reason, because a refusal with nothing said reads to them exactly like
  // being ignored, and the API refuses one without.
  decideDocument: (id: string, accept: boolean, reason = '') =>
    request<{ id: string; accepted: boolean; note?: string }>(
      '/v1/documents/' + encodeURIComponent(id) + (accept ? '/accept' : '/decline'),
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  // What is waiting for this reader, and how much of it is unread. One call:
  // a bell that costs a second request is a bell that gets removed.
  notices: () => request<NoticeInbox>('/v1/notices'),

  markNoticeRead: (id: string) =>
    request<{ id: string; read: boolean; unread: number }>(
      '/v1/notices/' + encodeURIComponent(id) + '/read',
      { method: 'POST' },
    ),

  // The filters are the API's, not this file's. overdue in particular has to
  // mean the same thing here, in the remediation report and in whatever ages a
  // finding overnight, so it is a flag the API reads rather than a date worked
  // out from the browser clock.
  findings: (f: FindingQuery = {}) =>
    request<Record<string, unknown>>('/v1/findings' + findingQueryString(f)).then(findingPageFrom),

  vendorFindings: (vendorId: string, f: FindingQuery = {}) =>
    request<Record<string, unknown>>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/findings' + findingQueryString(f),
    ).then(findingPageFrom),

  // What the latest score of this assessment raised or refreshed. The fold
  // moves a finding's assessment pointer forward on every rescore, so a finding
  // a later round refreshed belongs to that later round and not to this one.
  assessmentFindings: (assessmentId: string, f: FindingQuery = {}) =>
    request<Record<string, unknown>>(
      '/v1/assessments/' + encodeURIComponent(assessmentId) + '/findings' + findingQueryString(f),
    ).then(findingPageFrom),

  raiseFinding: (vendorId: string, body: FindingCreate) =>
    request<unknown>('/v1/vendors/' + encodeURIComponent(vendorId) + '/findings', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then(findingReplyFrom),

  // Owner, due date, severity and status, singly or together.
  patchFinding: (id: string, changes: FindingChange) =>
    request<unknown>('/v1/findings/' + encodeURIComponent(id), {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }).then(findingReplyFrom),

  // The ordinary close has its own route so the refusal names the note as a
  // field rather than arriving as a sentence about a transition.
  resolveFinding: (id: string, body: { note: string; documentId?: string }) =>
    request<unknown>('/v1/findings/' + encodeURIComponent(id) + '/resolve', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then(findingReplyFrom),

  clarifications: (assessmentId: string) =>
    request<Record<string, unknown>>(
      '/v1/assessments/' + encodeURIComponent(assessmentId) + '/clarifications',
    ).then(clarificationListFrom),

  raiseClarification: (assessmentId: string, body: { questionKey: string; asked: string }) =>
    request<Record<string, unknown>>('/v1/assessments/' + encodeURIComponent(assessmentId) + '/clarifications', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Three routes rather than one carrying a status, because the API names each
  // act and a rejection and a withdrawal owe different sentences. An
  // acceptance may send nothing.
  //
  // Both writes answer with the row they wrote and the outstanding list, and
  // neither answers with the settled rounds. The screen reloads the whole list
  // afterwards rather than splicing the reply into what it already holds: a
  // second round on the same answer changes two rows at once, and a list
  // patched in the browser is how the two drift.
  decideClarification: (id: string, act: 'accept' | 'reject' | 'withdraw', note: string) =>
    request<Record<string, unknown>>('/v1/clarifications/' + encodeURIComponent(id) + '/' + act, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  fieldDefinitions: (entity = 'VENDOR', includeInactive = false) =>
    request<{ definitions: FieldDefinition[] }>(
      '/v1/custom-fields?entity=' + encodeURIComponent(entity) + (includeInactive ? '&includeInactive=true' : ''),
    ),

  createFieldDefinition: (body: Partial<FieldDefinition>) =>
    request<{ id: string }>('/v1/custom-fields', { method: 'POST', body: JSON.stringify(body) }),

  updateFieldDefinition: (id: string, body: Partial<FieldDefinition>) =>
    request<{ updated: boolean }>('/v1/custom-fields/' + encodeURIComponent(id), {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  vendorFields: (vendorId: string) =>
    request<VendorFields>('/v1/vendors/' + encodeURIComponent(vendorId) + '/custom-fields'),

  setVendorFields: (vendorId: string, values: Record<string, FieldValue>) =>
    request<{ saved: number }>('/v1/vendors/' + encodeURIComponent(vendorId) + '/custom-fields', {
      method: 'PUT',
      body: JSON.stringify({ values }),
    }),

  eraseVendorFields: (vendorId: string, reason: string) =>
    request<{ fieldsErased: number }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/custom-fields/erase',
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  unmappedColumns: (importId: string) =>
    request<{ unmappedColumns: string[]; note: string }>(
      '/v1/imports/' + encodeURIComponent(importId) + '/unmapped-columns',
    ),

  promoteColumns: (importId: string, columns: string[], entity = 'VENDOR') =>
    request<PromoteResult>('/v1/imports/' + encodeURIComponent(importId) + '/promote-columns', {
      method: 'POST',
      body: JSON.stringify({ entity, columns }),
    }),
  // ---- the risk matrix, the view on it, and where risks go
  // The registers that send us risks, and what they sent. The other half of
  // riskSinks: one is where our risks go, this is where theirs come from.
  // Everything this product talks to, read from what the deployment is set to
  // and what has actually gone through it.
  // What this supplier publishes about itself, seen from outside.
  observations: (vendorId: string) =>
    request<ObservationsView>('/v1/vendors/' + encodeURIComponent(vendorId) + '/observations'),

  // What was sent to this vendor's contacts, on every channel, and what became
  // of it: sent, delivered to the phone, read, or refused with a reason.
  vendorMessages: (vendorId: string) =>
    request<{ messages: VendorMessage[]; inbound: InboundMessage[] }>('/v1/vendors/' + encodeURIComponent(vendorId) + '/messages'),

  // How each contact may be reached, and what the reminder ladders did.
  vendorChannels: (vendorId: string) =>
    request<{ consents: ChannelConsent[]; ladders: MessageLadder[]; note: string }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/channels',
    ),
  setContactChannel: (vendorId: string, contactId: string, channel: string, body: Record<string, unknown>) =>
    request<{ consent: ChannelConsent }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/contacts/' + encodeURIComponent(contactId) +
        '/channels/' + encodeURIComponent(channel.toLowerCase()),
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  messagingPolicy: () => request<{ policy: MessagingPolicy }>('/v1/messaging/policy'),
  saveMessagingPolicy: (policy: MessagingPolicy) =>
    request<{ policy: MessagingPolicy; note: string }>('/v1/messaging/policy', {
      method: 'PUT',
      body: JSON.stringify(policy),
    }),

  setVendorDomain: (vendorId: string, domain: string, confirming = false) =>
    request<{ domain: string; domainSource: string; note: string }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/domain',
      { method: 'PUT', body: JSON.stringify({ domain, confirming }) },
    ),

  // The shared mailboxes a tenant is told at. One address per purpose, read by
  // whoever is on the rota, rather than a copy of their staff list.
  // What every question asks, by key, so a weight is set against a sentence
  // rather than a code.
  questionPrompts: () => request<{ prompts: Record<string, string>; total: number }>('/v1/question-prompts'),

  mailboxes: () => request<{ procurementEmail: string }>('/v1/mailboxes'),

  setMailboxes: (procurementEmail: string) =>
    request<{ procurementEmail: string; note: string }>('/v1/mailboxes', {
      method: 'PUT',
      body: JSON.stringify({ procurementEmail }),
    }),

  connections: () => request<ConnectionsView>('/v1/connections'),

  // Where each domain reads from, and the pairing that makes inheriting
  // possible at all.
  configState: () => request<ConfigState>('/v1/settings/config'),
  licenceStatus: () => request<LicenceStatus>('/v1/settings/licence'),
  setConfigSource: (domain: string, source: ConfigSource, note?: string) =>
    request<{ domain: string; source: ConfigSource; why: string; degraded: boolean }>(
      '/v1/settings/config/' + encodeURIComponent(domain),
      { method: 'PUT', body: JSON.stringify({ source, note }) },
    ),
  savePlatformLink: (body: { baseUrl: string; remoteTenantId: string; displayName?: string; tokenRef: string }) =>
    request<{ status: string; note: string }>('/v1/settings/platform-link', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  testPlatformLink: () =>
    request<ProbeResult>('/v1/settings/platform-link/test', { method: 'POST' }),
  unpairPlatform: () =>
    request<{ note: string }>('/v1/settings/platform-link', { method: 'DELETE' }),
  // One test message through the carrier Consent holds, for a tenant that
  // inherits its mail or messaging from a pairing Consent made.
  relayTest: (channel: 'EMAIL' | 'SMS' | 'WHATSAPP', to: string) =>
    request<{ sent: boolean; note: string; detail?: string; ref?: string }>('/v1/settings/relay/test', {
      method: 'POST',
      body: JSON.stringify({ channel, to }),
    }),

  connectors: () => request<ConnectorsView>('/v1/connectors'),
  // A real WhatsApp or SMS through one messaging card, to the operator's own
  // phone. The card says it can send only once the carrier confirms delivery.
  sendTestMessage: (id: string, to: string, kind?: string) =>
    request<{ sent: boolean; status: string; note: string; detail?: string; carrier?: string; ref?: string }>(
      '/v1/connectors/' + encodeURIComponent(id) + '/send-test-message',
      { method: 'POST', body: JSON.stringify({ to, kind }) },
    ),
  messagingKinds: () => request<{ kinds: MessageKind[]; note: string }>('/v1/messaging/kinds'),
  sendTestEmail: (id: string, to: string) =>
    request<{ sent: boolean; note: string; detail?: string; carrier?: string; ref?: string }>(
      '/v1/connectors/' + encodeURIComponent(id) + '/send-test',
      { method: 'POST', body: JSON.stringify({ to }) },
    ),
  addConnector: (templateKey: string, config: Record<string, unknown>, displayName?: string) =>
    request<{ id: string; status: string; note: string }>('/v1/connectors', {
      method: 'POST',
      body: JSON.stringify({ templateKey, config, displayName }),
    }),
  saveConnector: (id: string, config: Record<string, unknown>, displayName?: string) =>
    request<{ status: string; note: string }>('/v1/connectors/' + encodeURIComponent(id), {
      method: 'PUT',
      body: JSON.stringify({ config, displayName }),
    }),
  removeConnector: (id: string) =>
    request<{ note: string }>('/v1/connectors/' + encodeURIComponent(id), { method: 'DELETE' }),
  testConnector: (id: string) =>
    request<ProbeResult>('/v1/connectors/' + encodeURIComponent(id) + '/test', { method: 'POST' }),
  issueInboundKey: (id: string) =>
    request<{ apiKey: string; header: string; note: string }>(
      '/v1/connectors/' + encodeURIComponent(id) + '/inbound-key',
      { method: 'POST' },
    ),

  // Procurement: the purchase requests buying systems have filed, the answer
  // each supplier has, the keys those systems use, and what the worker did
  // for them. See apps/tprm-api/internal/api/procurement.go.
  procurementRequests: (status?: ClearanceStatus) =>
    request<{ requests: ProcurementRequest[]; counts: Partial<Record<ClearanceStatus, number>> }>(
      '/v1/procurement/requests' + (status ? '?status=' + encodeURIComponent(status) : ''),
    ),
  vendorClearance: (vendorId: string) =>
    request<Clearance & { supplierLinks: SupplierLink[]; writebacks: Writeback[] }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/clearance',
    ),
  procurementCredentials: () => request<{ credentials: ProcurementCredential[] }>('/v1/procurement/credentials'),
  createProcurementCredential: (body: { name: string; kind: 'API_KEY' | 'WEBHOOK_HMAC'; scopes: string[]; sourceSystem?: string }) =>
    request<IssuedProcurementCredential>('/v1/procurement/credentials', { method: 'POST', body: JSON.stringify(body) }),
  revokeProcurementCredential: (id: string) =>
    request<{ id: string; note: string }>('/v1/procurement/credentials/' + encodeURIComponent(id) + '/revoke', {
      method: 'POST',
    }),
  procurementApprovals: () => request<{ approvals: ApprovalTask[] }>('/v1/procurement/approvals'),
  statutoryWatch: () =>
    request<{ configured: boolean; note: string; windowDays: number; checks: StatutoryCheck[] }>(
      '/v1/procurement/statutory-watch',
    ),

  riskSources: () =>
    request<{ riskSources: RiskSource[]; webhookPath: string; note: string }>('/v1/risk-sources'),

  createRiskSource: (body: {
    kind: string
    name: string
    originLabel: string
    secret: string
    likelihoodMap?: Record<string, number>
    impactMap?: Record<string, number>
  }) =>
    request<{ id: string; webhookUrl: string; note: string }>('/v1/risk-sources', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  enableRiskSource: (id: string, enabled: boolean) =>
    request<{ enabled: boolean; note: string }>('/v1/risk-sources/' + encodeURIComponent(id) + '/enable', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),

  // Correcting a mapping places everything that source has already sent,
  // which is why the reply says how many moved.
  updateRiskSourceScales: (id: string, likelihoodMap: Record<string, number>, impactMap: Record<string, number>) =>
    request<{ replaced: number; note: string }>('/v1/risk-sources/' + encodeURIComponent(id) + '/scales', {
      method: 'PUT',
      body: JSON.stringify({ likelihoodMap, impactMap }),
    }),

  externalRisks: (withdrawn = false) =>
    request<{ externalRisks: ExternalRisk[]; total: number }>(
      '/v1/risk-sources/external' + (withdrawn ? '?withdrawn=true' : ''),
    ),

  riskMatrix: () => request<{ riskMatrix: RiskMatrixConfig; note: string }>('/v1/risk-matrix'),
  putRiskMatrix: (body: RiskMatrixInput) =>
    request<{ riskMatrix: RiskMatrixConfig; note: string }>('/v1/risk-matrix', { method: 'PUT', body: JSON.stringify(body) }),
  lockRiskMatrix: (signOffNote: string) =>
    request<{ riskMatrix: RiskMatrixConfig; note: string }>('/v1/risk-matrix/lock', { method: 'POST', body: JSON.stringify({ signOffNote }) }),
  unlockRiskMatrix: (reason: string) =>
    request<{ riskMatrix: RiskMatrixConfig; note: string }>('/v1/risk-matrix/unlock', { method: 'POST', body: JSON.stringify({ reason }) }),
  riskMatrixView: () => request<RiskMatrixView>('/v1/risk/matrix'),
  riskSinks: () => request<{ riskSinks: RiskSink[]; note: string }>('/v1/risk-sinks'),
  createRiskSink: (body: { kind: string; name: string; url: string; secret: string }) =>
    request<{ id: string; queued: number; note: string }>('/v1/risk-sinks', { method: 'POST', body: JSON.stringify(body) }),
  enableRiskSink: (id: string, enabled: boolean) =>
    request<{ id: string; enabled: boolean }>('/v1/risk-sinks/' + encodeURIComponent(id) + '/enable', { method: 'POST', body: JSON.stringify({ enabled }) }),
  testRiskSink: (id: string) =>
    request<{ id: string; httpStatus: number; ok: boolean; note: string }>('/v1/risk-sinks/' + encodeURIComponent(id) + '/test', { method: 'POST', body: '{}' }),
  resyncRiskSink: (id: string) =>
    request<{ queued: number }>('/v1/risk-sinks/' + encodeURIComponent(id) + '/resync', { method: 'POST', body: '{}' }),
  riskRegisters: () =>
    request<{ registers: RiskRegister[]; ownership: RegisterOwnership; note: string }>('/v1/risk-registers'),
  createRiskRegister: (body: RegisterInput) =>
    request<{ id: string; queued: number; note: string }>('/v1/risk-registers', { method: 'POST', body: JSON.stringify(body) }),
  updateRiskRegister: (id: string, body: RegisterInput) =>
    request<{ id: string; note: string }>('/v1/risk-registers/' + encodeURIComponent(id), { method: 'PUT', body: JSON.stringify(body) }),
  enableRiskRegister: (id: string, enabled: boolean) =>
    request<{ id: string; enabled: boolean }>('/v1/risk-registers/' + encodeURIComponent(id) + '/enable', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),
  testRiskRegister: (id: string) =>
    request<RegisterProbe>('/v1/risk-registers/' + encodeURIComponent(id) + '/test', { method: 'POST', body: '{}' }),
  resyncRiskRegister: (id: string) =>
    request<{ queued: number }>('/v1/risk-registers/' + encodeURIComponent(id) + '/resync', { method: 'POST', body: '{}' }),
  setRegisterTreatment: (id: string, reflect: boolean, reason: string) =>
    request<{ id: string; reflectTreatment: boolean; note: string }>(
      '/v1/risk-registers/' + encodeURIComponent(id) + '/treatment',
      { method: 'PUT', body: JSON.stringify({ reflect, reason }) },
    ),
  registerReceipts: (id: string) =>
    request<{ receipts: RegisterReceipt[]; returnPath: string }>('/v1/risk-registers/' + encodeURIComponent(id) + '/receipts'),
  // The register as an OSCAL plan of action and milestones, for an auditor.
  riskRegisterPOAM: () => requestFile('/v1/risk/export/oscal-poam'),
  questionnaires: () => request<{ questionnaires: Questionnaire[] }>('/v1/questionnaires'),
  // The studio's own list, archived ones included and marked. Every picker
  // reads questionnaires() above, which leaves them out.
  allQuestionnaires: () => request<{ questionnaires: Questionnaire[] }>('/v1/questionnaires?includeArchived=true'),
  archiveQuestionnaire: (id: string, reason: string) =>
    request<QuestionnaireArchiveResult>('/v1/questionnaires/' + encodeURIComponent(id) + '/archive', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  restoreQuestionnaire: (id: string) =>
    request<QuestionnaireArchiveResult>('/v1/questionnaires/' + encodeURIComponent(id) + '/restore', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  importQuestionnaireDoc: (body: unknown) =>
    request<ImportQuestionnaireResult>('/v1/questionnaires/import', { method: 'POST', body: JSON.stringify(body) }),


  questionnaire: (id: string) =>
    request<QuestionnaireDetail>('/v1/questionnaires/' + encodeURIComponent(id)),

  createQuestionnaire: (body: { code: string; name: string; description?: string; purpose?: string }) =>
    request<{ id: string }>('/v1/questionnaires', { method: 'POST', body: JSON.stringify(body) }),

  questionnaireVersion: (id: string) =>
    request<VersionDetail>('/v1/questionnaire-versions/' + encodeURIComponent(id)),

  createQuestionnaireVersion: (
    id: string,
    body: { cloneFromVersionId?: string; sourcePack?: string; notes?: string },
  ) =>
    request<{ id: string; versionNo: number }>(
      '/v1/questionnaires/' + encodeURIComponent(id) + '/versions',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  saveQuestionnaireVersion: (id: string, sections: SectionInput[], notes?: string) =>
    request<SaveVersionResult>('/v1/questionnaire-versions/' + encodeURIComponent(id), {
      method: 'PUT',
      body: JSON.stringify({ sections, notes }),
    }),

  publishQuestionnaireVersion: (id: string, body?: { riskModelCode?: string; notes?: string }) =>
    request<PublishResult>('/v1/questionnaire-versions/' + encodeURIComponent(id) + '/publish', {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  questionnaireDiff: (fromId: string, toId: string) =>
    request<VersionDiff>(
      '/v1/questionnaire-versions/' + encodeURIComponent(fromId) + '/diff/' + encodeURIComponent(toId),
    ),

  riskTypes: () => request<{ riskTypes: RiskTypeCatalogueEntry[]; note: string }>('/v1/risk-types'),

  async vendorRiskTypes(vendorId: string): Promise<VendorRiskTypes> {
    const raw = await request<Record<string, unknown>>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/risk-types',
    )
    return vendorRiskTypesFrom(raw)
  },

  riskModels: () => request<{ riskModels: RiskModel[] }>('/v1/risk-models'),

  riskModel: (id: string) => request<RiskModelDetail>('/v1/risk-models/' + encodeURIComponent(id)),

  createRiskModel: (body: { code: string; name: string; description?: string; isDefault?: boolean }) =>
    request<{ id: string; code: string; note: string }>('/v1/risk-models', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  riskModelVersion: (id: string) =>
    request<ModelVersion>('/v1/risk-model-versions/' + encodeURIComponent(id)),

  createRiskModelVersion: (
    id: string,
    body: { fromVersionId?: string; notes?: string; model?: ScoringModel },
  ) =>
    request<{ id: string; versionNo: number }>('/v1/risk-models/' + encodeURIComponent(id) + '/versions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  saveRiskModelVersion: (id: string, model: ScoringModel, notes?: string) =>
    request<{ id: string; status: string; contentSha256: string; outstanding: string[]; note: string }>(
      '/v1/risk-model-versions/' + encodeURIComponent(id),
      { method: 'PUT', body: JSON.stringify({ model, notes }) },
    ),

  validateRiskModelVersion: (id: string, questionnaireVersionId?: string) =>
    request<ModelValidation>('/v1/risk-model-versions/' + encodeURIComponent(id) + '/validate', {
      method: 'POST',
      body: JSON.stringify({ questionnaireVersionId }),
    }),

  explainRiskModelVersion: (id: string, body: { vendorId: string; questionnaireVersionId?: string }) =>
    request<ModelExplain>('/v1/risk-model-versions/' + encodeURIComponent(id) + '/explain', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  promoteRiskModelVersion: (id: string, body: { recompute: boolean; questionnaireVersionId?: string }) =>
    request<ModelPromoteResult>('/v1/risk-model-versions/' + encodeURIComponent(id) + '/promote', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // The gate, evaluated rather than recomputed. It reads the tier decision, the
  // stored score, the findings register and the document shelf, so the answer
  // does not depend on when it was asked for.
  vendorActivation: (vendorId: string) => request<ActivationView>(APPROVAL_PATHS.activation(vendorId)),

  // stageIndex is optional to the API, which signs whatever stage is in front
  // of the vendor. It is sent anyway: a console that read the chain a minute
  // ago should not sign whatever moved into the space while it was being read.
  signApprovalStage: (vendorId: string, body: { stageIndex: number; note?: string }) =>
    request<ApprovalSignResult>(APPROVAL_PATHS.approvals(vendorId), {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // The ten steps. The same evaluation the activation refusal makes, so this
  // screen and that refusal cannot disagree about where a vendor has got to.
  vendorOnboarding: (vendorId: string) => request<OnboardingProgress>(ONBOARDING_PATHS.progress(vendorId)),

  assertOnboardingStep: (vendorId: string, key: string, note: string) =>
    request<OnboardingStepReply>(ONBOARDING_PATHS.step(vendorId, key), {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  withdrawOnboardingStep: (vendorId: string, key: string, reason: string) =>
    request<OnboardingStepReply>(ONBOARDING_PATHS.step(vendorId, key) + '/withdraw', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  onboardingProfileVersions: () => request<OnboardingVersionList>(ONBOARDING_PATHS.versions),

  onboardingProfileVersion: (id: string) => request<OnboardingVersionRead>(ONBOARDING_PATHS.version(id)),

  // With neither a document nor a source it starts from what is in force,
  // which on a tenant that has never configured one is the shipped default.
  draftOnboardingProfile: (body: { profile?: OnboardingProfileDoc; fromVersionId?: string; notes?: string }) =>
    request<OnboardingDraftResult>(ONBOARDING_PATHS.versions, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  saveOnboardingProfile: (id: string, body: { profile: OnboardingProfileDoc; notes?: string }) =>
    request<OnboardingSaveResult>(ONBOARDING_PATHS.version(id), {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  promoteOnboardingProfile: (id: string) =>
    request<OnboardingPromoteResult>(ONBOARDING_PATHS.version(id) + '/promote', { method: 'POST' }),

  // Section 17's checklist, evaluated. The same call the OFFBOARDED transition
  // makes, so what this screen shows is what that refusal will say.
  vendorOffboarding: (vendorId: string) => request<OffboardingView>(OFFBOARDING_PATHS.checklist(vendorId)),

  // PUT and addressed by the item key, because recording the same thing twice
  // is the same record: correcting a note must not leave two rows for one item.
  recordOffboardingItem: (vendorId: string, key: string, body: { done: boolean; note?: string }) =>
    request<OffboardingRecordResult>(OFFBOARDING_PATHS.item(vendorId, key), {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // The condition vocabulary and the band list travel with the versions, so an
  // editor is never one release away from offering a condition the gate cannot
  // evaluate.
  approvalMatrixVersions: () => request<ApprovalMatrixList>(APPROVAL_PATHS.versions),

  approvalMatrixVersion: (id: string) =>
    request<ApprovalMatrixVersionView>(APPROVAL_PATHS.version(id)),

  // With neither a document nor a source the API clones the active version, and
  // with no active version it starts from the shipped default. An empty draft is
  // not offered: it could never be promoted, and twenty problems to fix by hand
  // is not a blank page.
  createApprovalMatrixVersion: (body: { fromVersionId?: string; notes?: string; matrix?: MatrixDocument }) =>
    request<MatrixWriteResult>(APPROVAL_PATHS.versions, { method: 'POST', body: JSON.stringify(body) }),

  // The whole document, never a patch. A partial edit of a document that says
  // who must sign is how a stage goes missing.
  saveApprovalMatrixVersion: (id: string, matrix: MatrixDocument, notes?: string) =>
    request<MatrixWriteResult>(APPROVAL_PATHS.version(id), {
      method: 'PUT',
      body: JSON.stringify({ matrix, notes }),
    }),

  promoteApprovalMatrixVersion: (id: string) =>
    request<MatrixPromoteResult>(APPROVAL_PATHS.version(id) + '/promote', { method: 'POST' }),

  overrideBand: (
    assessmentId: string,
    body: { band: string; reason: string; approvedBy: string; expiresOn: string },
  ) =>
    request<Record<string, unknown>>('/v1/scores/' + encodeURIComponent(assessmentId) + '/override', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  tierProfiles: () =>
    request<{ tierProfiles: TierProfile[]; note: string; issuing?: TierIssuing }>('/v1/tier-profiles'),

  saveTierProfile: (
    tier: number,
    body: {
      questionnaireId?: string
      questionnaireCode?: string
      riskModelId?: string
      riskModelCode?: string
      cadenceMonths: number | null
      requiresApproval?: boolean
    },
  ) =>
    request<Record<string, unknown>>('/v1/tier-profiles/' + tier, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  reassessmentPolicy: (vendorId: string) =>
    request<PolicyView>('/v1/vendors/' + encodeURIComponent(vendorId) + '/reassessment-policy'),

  saveReassessmentPolicy: (
    vendorId: string,
    body: {
      cadenceMonths: number | null
      defaultMode?: string
      suppressionDays?: number | null
      eventTriggers?: unknown
      isPaused?: boolean
      pausedReason?: string
      // A date a person chose: a date sets it, an empty string clears it, and
      // leaving it out keeps whatever is there.
      nextReassessmentOn?: string
    },
  ) =>
    request<PolicyView>('/v1/vendors/' + encodeURIComponent(vendorId) + '/reassessment-policy', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  triggers: (status?: string, vendorId?: string) => {
    const qs = new URLSearchParams()
    if (status) qs.set('status', status)
    if (vendorId) qs.set('vendorId', vendorId)
    const tail = qs.toString()
    return request<TriggerList>('/v1/triggers' + (tail ? '?' + tail : ''))
  },

  raiseTrigger: (body: {
    vendorId: string
    triggerType: string
    dueOn: string
    detail?: unknown
    affectedSectionKeys?: string[]
  }) => request<{ id: string }>('/v1/triggers', { method: 'POST', body: JSON.stringify(body) }),

  dismissTrigger: (id: string, reason: string) =>
    request<Record<string, unknown>>('/v1/triggers/' + encodeURIComponent(id) + '/dismiss', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),


  contacts: (vendorId: string) =>
    request<{ contacts: Contact[]; count: number; reachable: number; note: string }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/contacts',
    ),

  createContact: (vendorId: string, body: ContactInput) =>
    request<{ id: string; respondentRef: string; note: string }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/contacts',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  updateContact: (id: string, body: ContactInput) =>
    request<{ id: string; updated: boolean }>('/v1/contacts/' + encodeURIComponent(id), {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  setContactActive: (id: string, active: boolean) =>
    request<{ id: string; active: boolean; note: string }>(
      '/v1/contacts/' + encodeURIComponent(id) + '/deactivate',
      { method: 'POST', body: JSON.stringify({ active }) },
    ),

  eraseContact: (id: string, reason: string) =>
    request<{ id: string; respondentRef: string; erased: boolean; note: string }>(
      '/v1/contacts/' + encodeURIComponent(id) + '/erase',
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  vendorDocuments: (vendorId: string) =>
    request<{ documents: VendorDocument[] }>('/v1/vendors/' + encodeURIComponent(vendorId) + '/documents'),

  // Multipart, so it does not go through the JSON request helper. The browser
  // sets the boundary itself; setting Content-Type by hand omits it and the
  // server cannot parse the body.
  // effectiveFrom and expiresOn are optional to the API and load bearing to the
  // product. The reassessment scheduler requires expires_on IS NOT NULL before
  // it will raise CERT_EXPIRY or CONTRACT_RENEWAL, and this form did not send
  // either field, so every document uploaded through the console was invisible
  // to both. The API has read them since it was written.
  uploadVendorDocument: (
    vendorId: string,
    file: File,
    docType: string,
    title: string,
    dates?: { effectiveFrom?: string; expiresOn?: string; autoRenew?: boolean },
  ) => {
    const form = new FormData()
    form.append('file', file)
    form.append('docType', docType)
    form.append('title', title)
    if (dates?.effectiveFrom) form.append('effectiveFrom', dates.effectiveFrom)
    if (dates?.expiresOn) form.append('expiresOn', dates.expiresOn)
    if (dates?.autoRenew) form.append('autoRenew', 'true')
    return request<VendorDocument & { note: string; warnings?: DateWarning[] }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/documents',
      { method: 'POST', body: form },
    )
  },

  // Read the file again: after the OCR add-on is switched on, or after a
  // failure that has since been put right.
  readDocumentAgain: (id: string) =>
    request<{ id: string; readState: string; note: string }>(
      '/v1/documents/' + encodeURIComponent(id) + '/read',
      { method: 'POST' },
    ),

  document: (id: string) => request<DocumentDetail>('/v1/documents/' + encodeURIComponent(id)),

  vendorDocumentFindings: (vendorId: string) =>
    request<DocumentFindingSet>('/v1/vendors/' + encodeURIComponent(vendorId) + '/document-findings'),

  decideDocumentFinding: (id: string, decision: string, note?: string) =>
    request<{ finding: DocumentFinding; note: string }>('/v1/document-findings/' + encodeURIComponent(id) + '/decide', {
      method: 'POST',
      body: JSON.stringify({ decision, note: note ?? '' }),
    }),

  vendorContractTerms: (vendorId: string) =>
    request<ContractTermSet>('/v1/vendors/' + encodeURIComponent(vendorId) + '/contract-terms'),

  // runId names the reading the person looked at: a document read again
  // since is refused with 409 READ_AGAIN. subjectCountBand is needed only
  // when an acceptance would start the vendor's first data record; without it
  // that is refused with 422 and an issue on subjectCountBand.
  decideContractTerms: (
    documentId: string,
    runId: string,
    decisions: { key: string; decision: 'ACCEPT' | 'REJECT'; value?: string; note?: string }[],
    subjectCountBand?: string,
  ) =>
    request<{
      documentId: string
      proposals: ContractTerm[]
      note: string
      // The tier the accepted facts re-ran, when anything was accepted.
      tier?: { tier: number; tierLabel: string; ruleKey: string; why?: string }
    }>(
      '/v1/documents/' + encodeURIComponent(documentId) + '/terms/decide',
      {
        method: 'POST',
        body: JSON.stringify(subjectCountBand ? { runId, decisions, subjectCountBand } : { runId, decisions }),
      },
    ),

  // A contract gap becomes a finding on the vendor (finding.manage), or is
  // dismissed with a reason (evidence.review), which holds until a document
  // is read again.
  raiseContractGap: (vendorId: string, code: string, severity?: string) =>
    request<{ vendorId: string; gap: string; findingId: string; note: string }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/contract-gaps/' + encodeURIComponent(code) + '/raise',
      { method: 'POST', body: JSON.stringify({ severity: severity ?? '' }) },
    ),

  dismissContractGap: (vendorId: string, code: string, note: string) =>
    request<{ vendorId: string; gap: string; note: string }>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/contract-gaps/' + encodeURIComponent(code) + '/dismiss',
      { method: 'POST', body: JSON.stringify({ note }) },
    ),

  // Release 3.3, C14: whether a filed MSA or SOW renews itself.
  setDocumentAutoRenew: (id: string, autoRenews: boolean) =>
    request<{ id: string; autoRenews: boolean }>('/v1/documents/' + encodeURIComponent(id) + '/auto-renew', {
      method: 'POST',
      body: JSON.stringify({ autoRenews }),
    }),

  // Release 3.3, M4: stored dates that break a rule or carry a warning, paged.
  datesToCheck: (limit: number, offset: number) =>
    request<{ items: DateToCheck[]; total: number; limit: number; offset: number; asOf: string }>(
      '/v1/reports/dates-to-check?limit=' + limit + '&offset=' + offset,
    ),

  extractDocument: (id: string, questionnaireCode: string) =>
    request<ProposalSet>('/v1/documents/' + encodeURIComponent(id) + '/extract', {
      method: 'POST',
      body: JSON.stringify({ questionnaireCode }),
    }),

  proposals: (id: string) => request<ProposalSet>('/v1/documents/' + encodeURIComponent(id) + '/proposals'),

  confirmProposals: (
    id: string,
    decisions: { questionKey: string; decision: string; value?: string; note?: string }[],
  ) =>
    request<Record<string, unknown>>('/v1/documents/' + encodeURIComponent(id) + '/proposals/confirm', {
      method: 'POST',
      body: JSON.stringify({ decisions }),
    }),

  provisionalAssessment: (
    vendorId: string,
    body: { questionnaireCode?: string; riskModelCode?: string; confirmationDueDays?: number; reason?: string },
  ) =>
    request<Record<string, unknown>>(
      '/v1/vendors/' + encodeURIComponent(vendorId) + '/provisional-assessment',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  evidenceVerify: () =>
    request<{
      tenantId: string
      events: number
      ok: boolean
      breakAt?: number
      detail?: string
      headHash?: string
    }>('/v1/evidence/verify'),

  // The pack, and whether handing it over was recorded. See exportRecorded.
  evidencePack: async (params: { vendorId?: string; pack?: string; from?: string; to?: string }) => {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v)
    let recorded = true
    const pack = await request<EvidencePack>('/v1/evidence/export?' + qs.toString(), {}, (res) => {
      recorded = exportRecorded(res.headers)
    })
    return { pack, recorded }
  },

  // ----------------------------------------------- incidents and exposure

  // The register across every vendor, and one vendor's history. Both take the
  // same filter; the per-vendor route reads the whole register rather than the
  // live list, because "has this happened here before" is what somebody asks
  // the moment a supplier calls a second time.
  incidents: async (q: IncidentQuery = {}): Promise<IncidentPage> =>
    incidentPageFrom(await request<Record<string, unknown>>('/v1/incidents' + incidentQuery(q))),

  vendorIncidents: async (vendorId: string, q: IncidentQuery = {}): Promise<IncidentPage> =>
    incidentPageFrom(
      await request<Record<string, unknown>>(
        '/v1/vendors/' + encodeURIComponent(vendorId) + '/incidents' + incidentQuery(q),
      ),
    ),

  // The vendor page's incident box: whether Consent is linked, and what it
  // made of each incident.
  vendorIncidentBreach: (vendorId: string): Promise<IncidentBreachPage> =>
    request<IncidentBreachPage>('/v1/vendors/' + encodeURIComponent(vendorId) + '/incident-breach'),

  recordIncident: async (vendorId: string, draft: IncidentDraft): Promise<IncidentReply> =>
    incidentReplyFrom(
      await request<Record<string, unknown>>('/v1/vendors/' + encodeURIComponent(vendorId) + '/incidents', {
        method: 'POST',
        body: JSON.stringify(draft),
      }),
    ),

  // "Record that we told them": the body a duty is owed to was told. Telling it
  // twice keeps the first record and answers alreadyRecorded.
  recordDutyTold: (incidentId: string, code: string, input: DutyToldInput) =>
    request<{ duty: string; told: DutyNotice; alreadyRecorded: boolean }>(
      '/v1/incidents/' + encodeURIComponent(incidentId) + '/duties/' + encodeURIComponent(code) + '/told',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  changeIncident: async (id: string, change: IncidentChange): Promise<IncidentReply> =>
    incidentReplyFrom(
      await request<Record<string, unknown>>('/v1/incidents/' + encodeURIComponent(id), {
        method: 'PATCH',
        body: JSON.stringify(change),
      }),
    ),

  incidentExposure: async (id: string): Promise<Exposure> =>
    exposureFrom(
      await request<Record<string, unknown>>('/v1/incidents/' + encodeURIComponent(id) + '/exposure'),
    ),

  // The same traversal against a discovery time the caller supplies, for a
  // vendor whose incident nobody has opened yet. Nothing is recorded by
  // asking, which is the point: making somebody file a record before the
  // product will say what is at stake gets the record filed badly, or late.
  vendorExposure: async (vendorId: string, discoveredAt: string): Promise<Exposure> =>
    exposureFrom(
      await request<Record<string, unknown>>(
        '/v1/vendors/' +
          encodeURIComponent(vendorId) +
          '/exposure?discoveredAt=' +
          encodeURIComponent(discoveredAt),
      ),
    ),
}

// --------------------------------------------------------- what you may do

// The caller's capabilities, worked out once from the catalogue and the roles
// on their token, and read from here by every screen that shows or hides
// anything.
//
// held is null until that has been worked out, and null is not the same as
// empty. A caller whose roles the catalogue does not list, and a build talking
// to an API old enough not to serve /v1/roles, keep the console they had this
// morning: every control visible, and the API refusing in the API's words when
// one is pressed. Showing a person a control they cannot use costs them a
// click; hiding the whole console from somebody the server would have let in
// costs them the day.

let catalogue: RoleCatalogue | null = null
let signedInRoles: string[] = []
let held: Set<string> | null = null
// The persona catalogue, read beside the role table so a list of who signs can
// name the job a person knows ("Risk officer") rather than the role row behind
// it ("Risk / Compliance"). Null when the read failed, which only costs the
// nicer name: signerNames falls back to the role table's own words.
let personaBook: PersonaCatalogue | null = null

// Case is folded and space trimmed for the same reason the API folds it: a
// role arrives as an identity provider group and providers are not consistent
// about either.
function sameRole(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

export async function loadCapabilities(roles: string[]): Promise<void> {
  signedInRoles = roles
  // Both reads at once, and neither waits on the other to fail: a persona
  // catalogue that cannot be read must not cost anybody their capabilities.
  const [roleRead, personaRead] = await Promise.allSettled([api.roles(), api.personas()])
  personaBook = personaRead.status === 'fulfilled' ? personaRead.value : null
  if (roleRead.status === 'rejected') {
    catalogue = null
    held = null
    return
  }
  catalogue = roleRead.value
  const mine = catalogue.roles.filter((r) => roles.some((c) => sameRole(c, r.code)))
  if (mine.length === 0) {
    held = null
    return
  }
  held = new Set<string>(mine.flatMap((r) => r.capabilities))
}

export function can(c: Capability): boolean {
  return held === null || held.has(c)
}

// A read a screen can do without, made only when the reader's roles hold what
// it needs (release 3.1, persona spec P1: a screen a role is given loads
// without a refusal). The executive, the business requester and the
// governance steward read the register and not the programme's configuration,
// so a label read under config.read is left out for them and the screen shows
// what it has (a code rather than a risk type's name) instead of a 403.
export function readIf<T>(need: Capability, read: () => Promise<T>, otherwise: T): Promise<T> {
  return can(need) ? read() : Promise.resolve(otherwise)
}

// The jobs whose menu holds a screen, by name, for the page that tells a
// reader a screen is not part of theirs (persona spec P9: name the role that
// does own it). Empty when the catalogue could not be read or nobody holds it.
export function personasWithScreen(screen: string): string[] {
  return (personaBook?.personas ?? []).filter((p) => (p.screens ?? []).includes(screen)).map((p) => p.name)
}

// The catalogue's name for each code, for the chip in the header bar. A code
// the catalogue does not list is shown as itself rather than dropped: a person
// signed in with something this build has never heard of should be able to see
// what it was.
export function roleNames(codes: string[]): string[] {
  return codes.map((c) => catalogue?.roles.find((r) => sameRole(r.code, c))?.name ?? c)
}

export function capabilityAct(c: Capability): string {
  return catalogue?.capabilities.find((d) => d.code === c)?.act ?? c
}

export function rolesWith(c: Capability): string[] {
  return (catalogue?.roles ?? []).filter((r) => r.capabilities.includes(c)).map((r) => r.code)
}

// Every capability the API declares, for the one screen that has to offer them
// as a choice rather than test one of them. An approval stage names a
// capability, and somebody writing the matrix should be picking from the
// product's own list rather than typing a code they remember.
export function capabilityCatalogue(): CapabilityEntry[] {
  return catalogue?.capabilities ?? []
}

// The whole role table, for the one screen that hands roles out rather than
// asking a question about them. Every row comes from the API, the why text
// included: a console with its own copy of what a role means is a console that
// tells somebody they are granting one thing while the server grants another.
export function roleCatalogue(): RoleEntry[] {
  return catalogue?.roles ?? []
}

// The codes on the caller's own token, which is a different question from what
// those codes let them do. They are recited in a refusal and nowhere else: what
// a person may hand out is decided against capabilities rather than codes, and
// the API answers it per row on GET /v1/roles, so nothing in here works it out.
export function signedInRoleCodes(): string[] {
  return signedInRoles
}

// This deployment has no directory it can administer, which is an answer and
// not a failure: no provider is configured, or the build is running on made-up
// development tokens with nothing behind them. The API says so with 501
// NO_DIRECTORY and the port's own sentence, and the screen prints that instead
// of an empty table, which would read as a tenant with nobody in it.
export function noDirectory(err: unknown): boolean {
  return err instanceof ApiError && err.status === 501
}

// The holder of c that can do the least besides c. Used where the console
// suggests a role to somebody: the answer to "what do I need for this" is the
// narrowest thing that would work, not the first row of the table, which is
// the administrator.
export function narrowestRoleWith(c: Capability): string | null {
  const holders = (catalogue?.roles ?? []).filter((r) => r.capabilities.includes(c))
  if (holders.length === 0) return null
  return holders.reduce((a, b) => (b.capabilities.length < a.capabilities.length ? b : a)).code
}

// The holders are cut off at five wherever they are recited. vendor.read is
// held by seventeen rows, and a paragraph that recites all seventeen is one
// nobody finishes reading; the table is ordered with the roles a tenant is
// actually given first and the codes kept for existing estates behind them, so
// the five that survive the cut are the five worth asking for.
const HOLDERS_SHOWN = 5

// Who holds a capability, in names rather than codes, for the screens that say
// who can sign or do something rather than why the reader cannot. The approval
// matrix asks this of every stage it draws.
export function holdersOf(c: Capability): string {
  return signersText(rolesWith(c))
}

// ---------------------------------------------------------------- who signs

// The role table keeps a row for each code an existing estate was given before
// the roles were renamed, and names it after the role it became with this
// suffix. The row is real and a person may still carry it, so the People
// screen shows it; a list of who signs a stage should not.
const EXISTING_CODE = /\s*\(existing code\)\s*$/i

// The persona a role code belongs to, by the catalogue's own derivation, or
// by the one persona that names the role when the derivation does not list it.
// Two personas can share a role, and guessing between them would put a job on
// the screen the tenant never gave anybody, so an ambiguous role keeps its name.
function personaNameFor(code: string): string | undefined {
  const book = personaBook
  if (!book) return undefined
  const derived = Object.entries(book.derivedFrom ?? {}).find(([role]) => sameRole(role, code))?.[1]
  if (derived) return book.personas.find((p) => p.code === derived)?.name
  const named = book.personas.filter((p) => p.role !== undefined && sameRole(p.role, code))
  return named.length === 1 ? named[0]?.name : undefined
}

// Who may sign, as the names a reader knows, for every list of roles a stage or
// a capability is held by: the exit chain, the activation and risk acceptance
// chains, the approval matrix and its exit editor.
//
// The rule, in order, for each code:
//   1. A legacy code is dropped when a code whose name it repeats without the
//      "(existing code)" suffix is in the same list. Both let the same people
//      sign, so saying both says one thing twice.
//   2. The persona name is used where the persona catalogue gives one, looked
//      up for the code and, for a legacy code, for the current role it repeats.
//   3. Otherwise the role table's name without the suffix, then the name the
//      API sent beside the code (fallbackNames, index for index), then the code.
//   4. A name already in the list is not added again.
//
// Only what is read changes. A stage still stores and sends the codes it held.
export function signerNames(codes: string[], fallbackNames: string[] = []): string[] {
  const roles = catalogue?.roles ?? []
  const names = codes.map((code, i) => roles.find((r) => sameRole(r.code, code))?.name ?? fallbackNames[i] ?? code)
  const out: string[] = []
  codes.forEach((code, i) => {
    const name = names[i] ?? code
    const bare = name.replace(EXISTING_CODE, '')
    const legacy = bare !== name
    if (legacy && names.some((n) => sameRole(n, bare))) return
    let shown = personaNameFor(code)
    if (shown === undefined && legacy) {
      const twin = roles.find((r) => sameRole(r.name, bare))
      if (twin) shown = personaNameFor(twin.code)
    }
    const word = shown ?? bare
    if (!out.some((n) => sameRole(n, word))) out.push(word)
  })
  return out
}

// The same names as one phrase: "Business owner or TPRM lead", "CISO, DPO or
// Risk officer". It is the one place a list of signers is joined, and it keeps
// the console's cut at five names, so a capability held across the whole table
// reads "... or 2 others" rather than a line nobody finishes.
export function signersText(codes: string[], fallbackNames?: string[]): string {
  const names = signerNames(codes, fallbackNames)
  if (names.length <= 1) return names[0] ?? ''
  const shown = names.slice(0, HOLDERS_SHOWN)
  const rest = names.length - shown.length
  if (rest > 0) return shown.join(', ') + ' or ' + rest + (rest === 1 ? ' other' : ' others')
  return shown.slice(0, -1).join(', ') + ' or ' + shown[shown.length - 1]
}

// What a disabled control carries, in the sentence the API's own 403 uses.
// Somebody who meets the same wall twice, greyed out here and refused there,
// should not have to work out that it was one wall. Codes rather than names,
// because a code is what gets added to an identity provider group.
export function refusalReason(c: Capability): string {
  const holders = rolesWith(c)
  const carried = signedInRoles.length > 0 ? signedInRoles.join(', ') : 'no roles at all'
  const rest = holders.length - HOLDERS_SHOWN
  const named = holders.slice(0, HOLDERS_SHOWN).join(', ')
  const who =
    holders.length === 0
      ? ''
      : ' The roles that hold it are ' +
        named +
        (rest > 0 ? ' and ' + rest + (rest === 1 ? ' other' : ' others') : '') +
        '.'
  return 'This needs the ability to ' + capabilityAct(c) + ' (' + c + ').' + who + ' Your sign-in carries ' + carried + '.'
}

// ---------------------------------------------------------------- display

// The tier table, loaded once and shared. Several screens want the same four
// rows and none of them should race for them, so the promise is what is cached
// rather than the answer. A failed load is forgotten so the next screen retries
// rather than inheriting a blank table for the life of the session.
let tierTable: TierRow[] | null = null
let tierLoad: Promise<TierRow[]> | null = null

export function loadTiers(): Promise<TierRow[]> {
  if (!tierLoad) {
    tierLoad = api
      .tiers()
      .then((rows) => {
        tierTable = rows
        return rows
      })
      .catch((err) => {
        tierLoad = null
        throw err
      })
  }
  return tierLoad
}

export function tierTableLoaded(): TierRow[] | null {
  return tierTable
}

// What is printed before the table arrives, and where a tenant has no row for
// an ordinal. The same four defaults domain.TierLabel falls back to, and here
// for the same reason: the stored ordinal runs the other way, so a screen that
// prints a bare 4 has told the reader the opposite of the truth.
const SHIPPED_TIER_LABELS: Record<number, string> = {
  4: 'Tier 1 Critical',
  3: 'Tier 2 High',
  2: 'Tier 3 Moderate',
  1: 'Tier 4 Low',
}

// The label for a stored ordinal. The tenant's own label wins: a tier can be
// relabelled on its tier_profiles row and this console must not print a name
// the tenant has stopped using.
export function tierLabel(ordinal: number | null | undefined): string {
  if (ordinal === null || ordinal === undefined) return ''
  const row = tierTable?.find((t) => t.tier === ordinal)
  return row?.label || SHIPPED_TIER_LABELS[ordinal] || 'Tier ordinal ' + ordinal
}

// The short name of the rule that decided a tier, for a screen about a vendor.
// A rule is stored with a reason written for whoever maintains the model, and
// the product officer's review of 17 September asked for that reasoning to
// leave the vendor screens. The name is the reason's first clause, cut at the
// first colon or full stop, and at the first comma where that is still long.
// The stored reason is never changed, and the rule key stands in where a rule
// carries no reason at all.
export function tierRuleName(why: string | undefined, ruleKey: string | undefined): string {
  const text = (why ?? '').trim()
  if (text === '') return ruleKey ?? ''
  let name = (text.split(/[:.](?:\s|$)/)[0] ?? '').trim()
  if (name.length > 70) {
    const comma = name.indexOf(', ')
    const space = name.lastIndexOf(' ', 67)
    name = comma >= 20 ? name.slice(0, comma) : space > 0 ? name.slice(0, space) + '...' : name.slice(0, 67) + '...'
  }
  return name
}

export function pctValue(v: number | undefined | null): number | null {
  if (v === undefined || v === null || !Number.isFinite(v)) return null
  // The engine emits 0..1 for ratios and 0..100 for percentages depending on
  // the field, so anything at or below 1 is read as a ratio.
  return v <= 1 ? v * 100 : v
}

// A stored code, as words.
//
// # WHY AN ACRONYM LIST AND NOT JUST TITLE CASE
//
// Four screens each had their own copy of "lower-case it and capitalise the
// first letter", which turns LLP into "Llp", MSME into "Msme" and PAN into
// "Pan" on a record an Indian finance team is reading. Those are not words
// that can be title-cased: they are initialisms, and a register that prints
// "Llp" next to a company's legal name looks like it was built by somebody who
// has never seen one.
//
// So the codes that are initialisms are named, and everything else becomes
// words. The list is short on purpose: only what this product actually stores
// and shows. Anything not on it is treated as a word, which is the safe way
// round, because a word wrongly left upper-case shouts and a word correctly
// lower-cased reads.
const INITIALISMS = new Set([
  'LLP', 'LLC', 'PAN', 'GST', 'GSTIN', 'CIN', 'MSME', 'HUF', 'AOP', 'BOI',
  'PSU', 'NGO', 'NBFC', 'RBI', 'SEBI', 'IRDAI', 'DPDPA', 'GDPR', 'ISO',
  'SOC', 'PCI', 'DSS', 'MFA', 'API', 'SLA', 'DPA', 'NDA', 'MSA', 'SOW',
  'IT', 'HR', 'KYC', 'AML', 'BCP', 'DR', 'IN', 'UK', 'US', 'EU', 'UAE',
])

export function codeWords(code: string | null | undefined): string {
  if (!code) return ''
  return code
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part, i) => {
      const up = part.toUpperCase()
      if (INITIALISMS.has(up)) return up
      const lower = part.toLowerCase()
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower
    })
    .join(' ')
}

// What a source system is called on screen.
//
// The keys are the ones the mapping profiles and the import layouts are stored
// under, and most of them carry the name of another company's product. That
// key is fine where it lives, in a migration and a column, and it is not fine
// on a screen a customer of ours is looking at: it names the tool they are
// leaving (migration 0051's rule). So a key is never turned into words: the
// few neutral ones are named here and every other key, today's or one added
// later, reads "Another tool". Prefer the batch's own sourceLabel where there
// is one (importSource below).
const SYSTEM_NAME: Record<string, string> = {
  REDACTO: 'Your current tool',
  GENERIC_CSV: 'A spreadsheet',
  FILE: 'A spreadsheet',
}

export function systemName(code: string): string {
  if (!code) return ''
  return SYSTEM_NAME[code.toUpperCase()] ?? 'Another tool'
}

// Where an import came from, as a person reads it: the API's neutral words
// for the batch, or the source system said through systemName.
export function importSource(b: { sourceSystem: string; sourceLabel?: string }): string {
  return b.sourceLabel?.trim() || systemName(b.sourceSystem)
}

export function shortDate(iso: string | undefined | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10)
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

// Counted in lib/days.ts. Exported from here still, because this is where
// every screen that counts days reads it from.
export { daysUntil }

// ------------------------------------------- incidents and exposure display

function incidentQuery(q: IncidentQuery): string {
  const p = new URLSearchParams()
  if (q.status?.length) p.set('status', q.status.join(','))
  if (q.overdueOnly) p.set('overdue', 'true')
  if (q.withClocks) p.set('clocks', 'true')
  if (q.limit) p.set('limit', String(q.limit))
  const qs = p.toString()
  return qs ? '?' + qs : ''
}

function incidentPageFrom(raw: Record<string, unknown>): IncidentPage {
  const rows = Array.isArray(raw.incidents) ? (raw.incidents as Incident[]) : []
  return {
    incidents: rows,
    matched: num(raw.matched, rows.length),
    scanned: num(raw.scanned, rows.length),
    rowsRead: num(raw.rowsRead),
    queries: num(raw.queries),
    note: str(raw.note),
  }
}

function incidentReplyFrom(raw: Record<string, unknown>): IncidentReply {
  return { incident: (raw.incident ?? raw) as Incident, note: str(raw.note) }
}

// Both exposure routes wrap the answer and add a sentence of their own about
// what was counted from when. The wrapper is unfolded here and nowhere else,
// per the note at the top of this file, so every screen reads one object
// whichever route answered it.
//
// Beyond that this folds nothing: the traversal already answers in the shape
// packages/core/repo declares. It guarantees the collections exist and runs the
// findings through the register's own normaliser, so an exposure findings list
// and the remediation screen cannot describe one finding differently.
function exposureFrom(raw: Record<string, unknown>): Exposure {
  const body = (raw.exposure ?? raw) as Record<string, unknown>
  const e = body as unknown as Exposure
  const findings = Array.isArray(body.openFindings) ? body.openFindings : []
  return {
    ...e,
    subprocessors: Array.isArray(e.subprocessors) ? e.subprocessors : [],
    concentration: Array.isArray(e.concentration) ? e.concentration : [],
    clocks: Array.isArray(e.clocks) ? e.clocks : [],
    caveats: Array.isArray(e.caveats) ? e.caveats : [],
    openFindings: findings.map((f) => normalizeFinding(f)),
  }
}

// A tenant with no active statutory clock version. The API refuses rather than
// answering with an empty list, because an incident answer that silently said
// nobody has to be told would be the most expensive kind of wrong. The console
// has to recognise it so the screen says what is missing instead of printing a
// refusal beside four sections of real facts.
export function noClockCatalogue(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === 'NO_CLOCK_CATALOGUE'
}

// A deadline is printed as a date and a time with the zone named. The API
// works in UTC and the person reading this is in a room somewhere; a deadline
// that does not say which clock it is against is one somebody argues about at
// the worst possible moment.
export function stampWithZone(iso: string | undefined | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  })
}

// The window as the catalogue holds it, which is a Go duration: "6h0m0s". It
// is stored that way so whoever owns the regulatory content can hold it beside
// the circular and check it. Nothing prints it raw next to a statute.
export function windowWords(window: string | undefined | null): string {
  if (!window) return ''
  const h = /(\d+)h/.exec(window)
  const m = /(\d+)m/.exec(window)
  const hours = Number(h?.[1] ?? 0)
  const mins = Number(m?.[1] ?? 0)
  const parts: string[] = []
  if (hours > 0) parts.push(countOf(hours, 'hour'))
  if (mins > 0) parts.push(countOf(mins, 'minute'))
  return parts.length > 0 ? parts.join(' ') : window
}

function countOf(n: number, word: string): string {
  return n + ' ' + word + (n === 1 ? '' : 's')
}

// A span of time in words, to the precision somebody acts on. Seconds are
// dropped: nobody times a statutory notification to the second, and a figure
// that changes while it is being read is a figure nobody trusts.
export function durationWords(ms: number): string {
  const abs = Math.abs(ms)
  const days = Math.floor(abs / 86400000)
  const hours = Math.floor((abs % 86400000) / 3600000)
  const mins = Math.floor((abs % 3600000) / 60000)
  if (days > 0) return countOf(days, 'day') + ' ' + countOf(hours, 'hour')
  if (hours > 0) return countOf(hours, 'hour') + ' ' + countOf(mins, 'minute')
  if (mins > 0) return countOf(mins, 'minute')
  return 'under a minute'
}

// How long is left, or how long ago it went. A minus sign in front of a
// duration is a thing people misread at the moment they can least afford to,
// so the direction is said in words rather than signed.
export function countdownWords(ms: number): string {
  return ms < 0 ? durationWords(ms) + ' past the deadline' : durationWords(ms) + ' left'
}

// The instant a calendar date begins in the reader's own zone, as a timestamp
// the API will accept.
//
// A date control's value is a calendar date with nothing attached: 2026-09-07
// and no clock. Passed through unchanged it is parsed at the other end as UTC
// midnight, so in India a window said to run from the 7th actually began at
// half past five that morning, and the day it was said to stop before kept its
// own first five and a half hours. Rows either side of those boundaries were
// dropped or kept with nothing on the screen to say so, beside timestamps
// printed on the reader's clock by stampWithZone. A filter that quietly drops
// evidence is worse than one that refuses.
//
// plusDays is how a window ends: the start of the day after the one named, so
// the named day is outside it whole rather than by five and a half hours.
export function localDayStart(date: string | undefined | null, plusDays = 0): string | undefined {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec((date ?? '').trim())
  if (!parts) return undefined
  const d = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]) + plusDays)
  if (Number.isNaN(d.getTime())) return undefined
  // Seconds and no milliseconds, which is what RFC 3339 asks for and what the
  // API's layout reads back.
  return d.toISOString().slice(0, 19) + 'Z'
}

// What a datetime-local control holds, from a moment. The control works in the
// reader's own zone and has no seconds, which is the right precision for a
// discovery time somebody is recalling from a phone call.
export function localInputStamp(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    d.getFullYear() +
    '-' +
    pad(d.getMonth() + 1) +
    '-' +
    pad(d.getDate()) +
    'T' +
    pad(d.getHours()) +
    ':' +
    pad(d.getMinutes())
  )
}

// The programme report as GET /v1/reports/programme serves it
// (apps/tprm-api/internal/api/report_programme.go). Every part that names
// vendors is optional: the API leaves out a part the reader may not see rather
// than sending it empty, and an empty list that IS sent means nothing is there.
export type ProgrammeReportTone = 'crit' | 'warn' | 'info' | 'ok'
export type ProgrammeReportFigure = {
  key: string
  label: string
  now: number
  // The value at the end of the previous window, rebuilt from timestamps.
  previous: number | null
  direction: 'up_good' | 'up_bad' | 'neutral'
  href: string
}
export type ProgrammeReportHeadline = { tone: ProgrammeReportTone; text: string; href: string }
export type ProgrammeReportSeverities = { critical: number; high: number; medium: number; low: number }
export type ProgrammeReportVendor = {
  vendorId: string
  name: string
  code: string
  tier: number
  tierLabel: string
  band: string
  score: number | null
  openFindings: ProgrammeReportSeverities
  lastAssessedAt?: string
  owner?: string
  href: string
}
export type ProgrammeReportAgeing = ProgrammeReportSeverities & { bucket: '0-30' | '31-60' | '61-90' | '90+' }
export type ProgrammeReportOldFinding = { title: string; vendor: string; severity: string; openedAt: string; days: number; href: string }
export type ProgrammeReportOverdue = { vendor: string; reference: string; dueOn: string; daysLate: number; href: string }
export type ProgrammeReportReassessment = { vendor: string; dueOn: string; overdue: boolean; href: string }
export type ProgrammeReportStage = { stage: string; label: string; count: number }
export type ProgrammeReportBlockedExit = {
  vendorId: string
  vendor: string
  stage: string
  blocker: string
  missingEvidence: boolean
  href: string
}
export type ProgrammeReport = {
  tenant: { name: string; legalName: string }
  period: { key: HomePeriodKey; days: number; from: string; to: string }
  previous: { from: string; to: string }
  generatedAt: string
  reportId: string
  preparedBy: { persona?: string; name?: string }
  figures: ProgrammeReportFigure[]
  headlines: ProgrammeReportHeadline[]
  topVendors?: ProgrammeReportVendor[]
  findings: {
    bySeverity: ProgrammeReportSeverities
    ageing: ProgrammeReportAgeing[]
    oldest?: ProgrammeReportOldFinding[]
  }
  assessments: {
    liveVendors: number
    scoredCurrent: number
    notScored: number
    coveragePercent: number | null
    overdueTotal: number
    overdue?: ProgrammeReportOverdue[]
    reassessmentsDueTotal: number
    reassessmentsDue?: ProgrammeReportReassessment[]
  }
  offboarding: {
    byStage: ProgrammeReportStage[]
    blockedTotal: number
    blockedOnEvidence: number
    blocked?: ProgrammeReportBlockedExit[]
    evidence: { required: number; recorded: number }
  }
  regulatory?: {
    incidentClocks?: HomeClock[]
    acceptances?: { expired: number; expiring: number; items: HomeClock[] }
    // Approved due diligence exceptions whose corrective action is overdue or
    // due within a week, and the exceptions waiting on this reader.
    closures?: { overdue: number; dueSoon: number; items: HomeClock[] }
    waitingForYou?: { id: string; title: string; vendorName: string; severityName?: string; statusName: string; act: string; nextStep: string; href: string }[]
  }
  purchasing?: { system: string; cleared: number; pending: number; blocked: number; href: string }
  method: { score: number; word: string; components: HomeHealthComponent[]; inputs: Record<string, number> }
}

export function programmeReport(period: HomePeriodKey): Promise<ProgrammeReport> {
  return request<ProgrammeReport>('/v1/reports/programme?period=' + period)
}
